
import { auth, db, googleProvider, isFirebaseActive } from './firebase';
import { toPublicName } from '../utils/publicName';
import { signInWithPopup, signInWithRedirect, signOut, linkWithPopup, linkWithRedirect, getRedirectResult, signInWithCredential, GoogleAuthProvider, OAuthProvider, updateProfile } from "firebase/auth";
import { isNativeIOS } from '../utils/capacitorBridge';
import { doc, setDoc, updateDoc, onSnapshot, arrayUnion, arrayRemove, increment, getDoc, collection, getDocs, addDoc, query, orderBy, limit as firestoreLimit, deleteDoc, serverTimestamp, Timestamp, where, deleteField, writeBatch, getCountFromServer, runTransaction, FieldPath } from "firebase/firestore";
import { UserProfile, UserGoals, TopicStat, GeminiTestQuestion, FavoriteQuestion, SubjectType, AnalysisLog, TestHistoryItem, GlobalChatMessage, FlashSession, Agent81Session, TravelerRecord, UserMemory, RouteRecord, ChatMessage } from '../types';
import { HISTORY_CHRONOLOGY } from '../data/history/chronology';
import { normalizeTopicName, resolveQuestionTopic, EXAM_BLUEPRINTS, TARGET_QUESTION_COUNTS } from '../data/topicDistributions';
import { uploadBase64Image } from './storageUploadService';
import * as notificationService from './notificationService';
import { TOPICS_BY_SUBJECT } from '../data/topics';

// Varsayılan Profil Şablonu
const defaultProfile: UserProfile = {
    uid: 'guest',
    name: 'Öğrenci',
    stats: {},
    wrongQuestions: {}, 
    dislikedQuestions: [],
    analysisHistory: {}, 
    testHistory: [], 
    chatHistory: [],
    flashHistory: [], // YENİ
    migrationVersion: 3, // YENİ
    learningStyle: {
        visual: 10,
        auditory: 10,
        analytical: 10
    },
    totalTestsTaken: 0,
    highScores: {},
    conqueredRegions: {},
    solvedAgentMissions: [],
    kapiExamResults: {},
    recentOpponents: []
};

/**
 * Yalnızca sunucunun (Cloud Functions / admin) yazabildiği abonelik, Kapı Puanı ve düello yasağı alanları.
 * firestore.rules istemcinin bu alanları değiştirmesini reddeder; istemci yazımlarından çıkarılmalı.
 * Ayrıntı: .agent/docs/magaza_abonelik_mimarisi.md
 */
export const SERVER_ONLY_PROFILE_FIELDS = [
    'isPremium', 'premiumUntil', 'premiumStartedAt', 'activePackageId',
    'storePremiumUntil', 'shopierPremiumUntil', 'manualPremiumUntil', 'storeSubscription',
    'kapiPoints', 'kapiSignupBonusGranted', 'kapiLastDailyGrant', 'duelBanUntil',
] as const;

import { slugify, generateQuestionId, TOPIC_TO_NODE_MAP, getSubjectFromTopic, resolveQuestionSubject, mapTopicToNodeId, HISTORY_BLUEPRINT_TO_NODE, mapHistoryTopicToNodeId, mapTurkishTopicToNodeId, getTargetNodeId, cleanSubTopic as filterSubTopic, effectiveQuestionTopic, isTuikTopic } from '../utils/topicRouting';
export { generateQuestionId, getSubjectFromTopic, resolveQuestionSubject, mapTurkishTopicToNodeId, getTargetNodeId };


// Firestore 'undefined' değerleri kabul etmez. Bu fonksiyon objeyi temizler.
const sanitizeData = (data: any): any => {
    return JSON.parse(JSON.stringify(data));
};

// Bellek İçi Önbellek
let currentUserProfile: UserProfile = { ...defaultProfile };
let currentChainData: { wateredHistory: number[]; lastWatered: string } = { wateredHistory: [], lastWatered: '' };
let currentFavorites: FavoriteQuestion[] = [];
let isProfileLoadedFromFirestore = false;
let isChainLoadedFromFirestore = false;
let profileSizeAlerted = false;

// --- DEĞİŞEN ALAN YAZIMI (delta) ---
// Ana dokümanın son görülen hali (önbellek ya da sunucu): alan → kararlı JSON. saveUserProfile yalnızca bundan
// farklı olan alanları yazar. Böylece (1) önbellekten açılan uygulama da kayıt yapabilir (internetsiz açılış),
// (2) eski bir profil kopyası kaydedilse bile dokunulmayan alanlar buluttaki yeni değerin üzerine yazılmaz.
let profileBaseline: Map<string, string> | null = null;
// user_data/stats için aynısı: istatistik anahtarı → JSON
let statsBaseline: Map<string, string> | null = null;
const baselineOf = (obj: Record<string, any> | undefined | null) => {
    const m = new Map<string, string>();
    for (const [k, v] of Object.entries(obj || {})) m.set(k, stableStringify(sanitizeData(v ?? null)));
    return m;
};

// --- HATALI SORULAR: users/{uid}/wrong_items/{düğüm__soruKimliği} (her soru ayrı doküman; alanlar + nodeId + _seq) ---
// Eskiden wrong_questions/{düğüm}.questions dizisiydi: düğüm başına sınırsız büyüyüp 1 MB'a dayanabilirdi ve her
// değişiklikte bütün düğüm yeniden yazılıyordu. Bellekteki şekil aynı kaldı: currentUserProfile.wrongQuestions[düğüm] = [...]
const WRONG_ITEMS = 'wrong_items';
let isWrongsLoadedFromFirestore = false;
// Dinleyicinin son gördüğü hal: docId → { nodeId, json, seq }. Yazarken yalnızca bundan farklı olan gider.
const serverWrongDocs = new Map<string, { nodeId: string; json: string; seq: number }>();

/** Anahtar sırasından bağımsız JSON (Firestore alanları sıralı döndürür, bellekteki nesne farklı sırada olabilir) */
const stableStringify = (v: any): string => JSON.stringify(v, (_k, val) =>
    val && typeof val === 'object' && !Array.isArray(val)
        ? Object.keys(val).sort().reduce((o: any, k) => { o[k] = val[k]; return o; }, {})
        : val);
const fnv1a = (s: string) => {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
    return (h >>> 0).toString(36);
};
const wrongDocId = (nodeId: string, q: any) =>
    `${nodeId}__${q?.id ? String(q.id) : `h${fnv1a(String(q?.question || ''))}${String(q?.question || '').length}`}`
        .replace(/\//g, '_').slice(0, 700);
const wrongItemForCloud = (q: any) => sanitizeData({
    ...q,
    image: typeof q?.image === 'string' && q.image.startsWith('data:') ? null : (q?.image ?? null),
});

/**
 * Verilen düğümlerdeki yeni/değişen soruları yazar (buluttaki son halden farklı olanlar). ASLA silmez:
 * bazı akışlar profilin eski bir kopyasını kaydediyor (analyzeTestResults görsel yüklerken bekliyor); o kopyada
 * olmayan sorular "silinmiş" sayılsaydı arada eklenen hatalar kaybolurdu. Silme yalnızca removeWrongDocs ile.
 */
const syncWrongNodes = async (uid: string, wrongs: Record<string, any[]> | undefined, nodeIds: Iterable<string>) => {
    if (!isFirebaseActive || !isWrongsLoadedFromFirestore) return;
    const nodes = new Set(nodeIds);
    const ops: Array<(b: ReturnType<typeof writeBatch>) => void> = [];
    let nextSeq = Date.now() * 1000;
    for (const nodeId of nodes) {
        const list = wrongs?.[nodeId] || [];
        // Listenin başına eklenen (unshift) yeni soru başta kalsın: düğümdeki en küçük sıradan küçük sıra al
        let minSeq = Infinity;
        for (const cur of serverWrongDocs.values()) if (cur.nodeId === nodeId && cur.seq < minSeq) minSeq = cur.seq;
        const seen = new Set<string>();
        list.forEach((q, index) => {
            const id = wrongDocId(nodeId, q);
            if (seen.has(id)) return;
            seen.add(id);
            const data = wrongItemForCloud(q);
            const cur = serverWrongDocs.get(id);
            if (cur && cur.json === stableStringify(data)) return;
            const seq = cur?.seq ?? (index === 0 && list.length > 1 && Number.isFinite(minSeq) ? --minSeq : nextSeq++);
            ops.push(b => b.set(doc(db, "users", uid, WRONG_ITEMS, id), { ...data, nodeId, _seq: seq }));
        });
    }
    for (let i = 0; i < ops.length; i += 400) {
        const batch = writeBatch(db);
        ops.slice(i, i + 400).forEach(op => op(batch));
        await batch.commit();
    }
};

/** Belirli soruları (düğüm + soru) buluttan siler. Çağıran tam olarak neyi çıkardığını bilir. */
const removeWrongDocs = async (uid: string, removed: { nodeId: string; q: any }[]) => {
    if (!isFirebaseActive || removed.length === 0) return;
    const ids = [...new Set(removed.map(r => wrongDocId(r.nodeId, r.q)))];
    for (let i = 0; i < ids.length; i += 400) {
        const batch = writeBatch(db);
        ids.slice(i, i + 400).forEach(id => batch.delete(doc(db, "users", uid, WRONG_ITEMS, id)));
        await batch.commit();
    }
};

/** Bir düğüm listesinden koşula uyanları çıkarır, çıkanları `removed`'a ekler */
const pullWrongs = (list: any[] | undefined, nodeId: string, shouldRemove: (q: any) => boolean, removed: { nodeId: string; q: any }[]) =>
    (list || []).filter(q => {
        if (shouldRemove(q)) { removed.push({ nodeId, q }); return false; }
        return true;
    });

// --- KONU İSTATİSTİKLERİ: users/{uid}/user_data/stats (alan adları = istatistik anahtarları) ---
// Eskiden ana profil dokümanında `stats` haritasıydı; yapay zekânın verdiği alt başlıklarla büyüyordu.
// Bellekteki şekil aynı: currentUserProfile.stats. Sunucu da buradan okur (functions › withUserStats).
let isStatsLoadedFromFirestore = false;
let statsDocExists = false;
const statsDocRef = (uid: string) => doc(db, "users", uid, "user_data", "stats");
/**
 * İstatistiklerde yalnızca son görülen halden farklı anahtarları yazar (her anahtar bütün olarak). Anahtar SİLMEZ:
 * eski bir kopyada olmayan anahtar, arada başka akışın eklediği yeni istatistik olabilir.
 * Doküman henüz yoksa (sunucu onaylı) bütün haritayla oluşturur.
 */
const writeStatsDoc = async (uid: string, stats: any) => {
    if (!isFirebaseActive || !statsBaseline) return;
    const ref = statsDocRef(uid);
    const clean = sanitizeData(stats || {});
    if (!statsDocExists) {
        if (Object.keys(clean).length === 0) return;
        await setDoc(ref, clean, { merge: true });
        return;
    }
    const args: any[] = [];
    for (const [key, value] of Object.entries(clean)) {
        if (statsBaseline.get(key) !== stableStringify(value)) args.push(new FieldPath(key), value);
    }
    if (args.length === 0) return;
    const [first, firstVal, ...rest] = args;
    await updateDoc(ref, first, firstVal, ...rest);
};

/**
 * Ana dokümandaki eski `stats` → user_data/stats, sonra alan silinir. İstatistik dokümanı zaten varsa üzerine yazmaz;
 * yalnızca eski alanda daha yeni (lastStudied) olan anahtarlar aktarılır (eski uygulama sürümü yazmaya devam edebilir).
 */
let statsMigrationRunning = false;
const migrateLegacyStats = async (uid: string, legacy: Record<string, any>) => {
    if (statsMigrationRunning) return;
    statsMigrationRunning = true;
    try {
        const ref = statsDocRef(uid);
        const snap = await getDoc(ref);
        const current: Record<string, any> = snap.exists() ? (snap.data() || {}) : {};
        let changed = !snap.exists() && Object.keys(legacy || {}).length > 0;
        const merged = { ...current };
        for (const [key, stat] of Object.entries(legacy || {})) {
            if (!merged[key] || ((stat as any)?.lastStudied || 0) > (merged[key]?.lastStudied || 0)) {
                merged[key] = stat;
                changed = true;
            }
        }
        if (changed) await setDoc(ref, sanitizeData(merged));
        await updateDoc(doc(db, "users", uid), { stats: deleteField() });
        console.log(`%c[MİGRASYON] 📊 Konu istatistikleri user_data/stats'a taşındı.`, 'color:#a855f7;font-weight:bold');
    } catch (e) {
        console.error('[MİGRASYON] İstatistikler taşınamadı:', e);
    } finally {
        statsMigrationRunning = false;
    }
};

// --- FAVORİLER: users/{uid}/favorites/{soru metninden kimlik} (eskiden user_data/favorites.items tek dizi) ---
const FAVORITES = 'favorites';
const favDocId = (questionText: string) => `f${fnv1a(String(questionText || ''))}${String(questionText || '').length}`;

/** Eski user_data/favorites.items dizisini favori başına dokümana taşır, sonra eski dokümanı siler. */
const migrateLegacyFavorites = async (uid: string, have: Set<string>) => {
    try {
        const legacyRef = doc(db, "users", uid, "user_data", "favorites");
        const snap = await getDoc(legacyRef);
        if (!snap.exists()) return;
        const items: any[] = Array.isArray(snap.data()?.items) ? snap.data()!.items : [];
        const writes = new Map<string, any>();
        for (const it of items) {
            const id = favDocId(it?.question);
            if (it?.question && !have.has(id) && !writes.has(id)) writes.set(id, sanitizeData(it));
        }
        const entries = [...writes.entries()];
        for (let i = 0; i < entries.length; i += 400) {
            const batch = writeBatch(db);
            entries.slice(i, i + 400).forEach(([id, data]) => batch.set(doc(db, "users", uid, FAVORITES, id), data));
            await batch.commit();
        }
        await deleteDoc(legacyRef);
        console.log(`%c[MİGRASYON] ⭐ ${entries.length} favori ayrı dokümanlara taşındı.`, 'color:#a855f7;font-weight:bold');
    } catch (e) {
        console.error('[MİGRASYON] Favoriler taşınamadı:', e);
    }
};

/** Eski wrong_questions/{düğüm} dokümanlarını soru başına dokümana taşır ve siler (eski uygulama sürümü yazmaya devam edebilir). */
let wrongMigrationRunning = false;
const migrateLegacyWrongQuestions = async (uid: string) => {
    if (wrongMigrationRunning) return;
    wrongMigrationRunning = true;
    try {
        const snap = await getDocs(collection(db, "users", uid, "wrong_questions"));
        if (snap.empty) return;
        const writes = new Map<string, any>();
        snap.forEach(d => {
            const qs = d.data().questions;
            if (!Array.isArray(qs)) return;
            qs.forEach((q: any, i: number) => {
                const id = wrongDocId(d.id, q);
                if (!serverWrongDocs.has(id) && !writes.has(id)) writes.set(id, { ...wrongItemForCloud(q), nodeId: d.id, _seq: i });
            });
        });
        const entries = [...writes.entries()];
        for (let i = 0; i < entries.length; i += 400) {
            const batch = writeBatch(db);
            entries.slice(i, i + 400).forEach(([id, data]) => batch.set(doc(db, "users", uid, WRONG_ITEMS, id), data));
            await batch.commit();
        }
        const del = writeBatch(db);
        snap.forEach(d => del.delete(d.ref));
        await del.commit();
        console.log(`%c[MİGRASYON] ❌ ${entries.length} hatalı soru soru-başına dokümana taşındı, ${snap.size} eski düğüm silindi.`, 'color:#a855f7;font-weight:bold');
    } catch (e) {
        console.error('[MİGRASYON] Hatalı sorular taşınamadı:', e);
    } finally {
        wrongMigrationRunning = false;
    }
};

// --- FOTOĞRAFLI SORU ANALİZLERİ: users/{uid}/analysis_logs/{logId} (her analiz ayrı doküman) ---
// Eskiden ana profil dokümanında `analysisHistory.{nodeId}` dizisiydi; 1 MB sınırına dayanınca yeni analizler
// yazılamıyordu (2026-09-30: 192 analizin 135'i kayboldu). TEK KAYNAK bu alt koleksiyondur: yerel önbellekteki
// analizler buluta asla geri yazılmaz (silinen analiz dirilmesin, başka hesabın verisi karışmasın).
const ANALYSIS_LOGS = 'analysis_logs';

const analysisLogDoc = (nodeId: string, log: AnalysisLog) => {
    const img = log.imageUrl || '';
    return sanitizeData({
        ...log,
        nodeId,
        imageUrl: img.startsWith('data:') ? (img.length < 150_000 ? img : null) : (img || null),
    });
};

/** Analizleri alt koleksiyona yazar (tam doküman; yoksa oluşturur). Hata fırlatır: çağıran kaydın başarısını bilmeli. */
const writeAnalysisLogs = async (uid: string, entries: { nodeId: string; log: AnalysisLog }[]) => {
    for (let i = 0; i < entries.length; i += 400) {
        const batch = writeBatch(db);
        for (const { nodeId, log } of entries.slice(i, i + 400)) {
            batch.set(doc(db, "users", uid, ANALYSIS_LOGS, String(log.id)), analysisLogDoc(nodeId, log));
        }
        await batch.commit();
    }
};

const deleteAnalysisLogDocs = async (uid: string, logIds: string[]) => {
    for (let i = 0; i < logIds.length; i += 400) {
        const batch = writeBatch(db);
        for (const id of logIds.slice(i, i + 400)) batch.delete(doc(db, "users", uid, ANALYSIS_LOGS, String(id)));
        await batch.commit();
    }
};

/** Bellekteki belirli düğümlerin bütün analizlerini alt koleksiyona yazar (işaret/taşıma sonrası). */
const persistAnalysisNodes = (uid: string, nodeIds: Iterable<string>) => {
    const entries: { nodeId: string; log: AnalysisLog }[] = [];
    for (const nodeId of nodeIds) {
        for (const log of currentUserProfile.analysisHistory?.[nodeId] || []) entries.push({ nodeId, log });
    }
    return writeAnalysisLogs(uid, entries);
};

/**
 * Ana dokümanda kalmış eski `analysisHistory` alanını alt koleksiyona taşır ve alanı siler (buluttan buluta).
 * Alt koleksiyonda zaten olan kayıtların üzerine yazmaz (orada daha yeni işaretler olabilir).
 * Eski sürüm uygulamalar (ör. yüklü iOS build'i) ana dokümana yazmaya devam ederse sonraki açılışta yine taşınır.
 */
/**
 * Ana dokümandaki eski `testHistory` (son 5 sınavın tam kopyası, tek sınav ~100 KB) → `tests` alt koleksiyonu.
 * Asıl kayıt zaten `tests`'te; burada yalnızca orada olmayanlar yazılır, sonra alan silinir.
 */
let testMigrationRunning = false;
const migrateLegacyTestHistory = async (uid: string, legacy: TestHistoryItem[]) => {
    if (testMigrationRunning) return;
    testMigrationRunning = true;
    try {
        const existing = await getDocs(collection(db, "users", uid, "tests"));
        const have = new Set(existing.docs.map(d => d.id));
        const missing = legacy.filter(t => t?.id && !have.has(t.id));
        for (let i = 0; i < missing.length; i += 400) {
            const batch = writeBatch(db);
            for (const t of missing.slice(i, i + 400)) batch.set(doc(db, "users", uid, "tests", t.id), sanitizeData(t));
            await batch.commit();
        }
        await updateDoc(doc(db, "users", uid), { testHistory: deleteField() });
        console.log(`%c[MİGRASYON] 📝 Ana dokümandaki test geçmişi kaldırıldı (${missing.length} eksik test taşındı).`, 'color:#a855f7;font-weight:bold');
    } catch (e) {
        console.error('[MİGRASYON] Test geçmişi taşınamadı:', e);
    } finally {
        testMigrationRunning = false;
    }
};

let analysisMigrationRunning = false;
const migrateLegacyAnalysisHistory = async (uid: string, legacy: Record<string, AnalysisLog[]>) => {
    if (analysisMigrationRunning) return;
    analysisMigrationRunning = true;
    try {
        const existing = await getDocs(collection(db, "users", uid, ANALYSIS_LOGS));
        const have = new Set(existing.docs.map(d => d.id));
        const entries: { nodeId: string; log: AnalysisLog }[] = [];
        for (const [nodeId, logs] of Object.entries(legacy)) {
            if (!Array.isArray(logs)) continue;
            for (const log of logs) {
                if (log?.id && !have.has(String(log.id))) {
                    have.add(String(log.id));
                    entries.push({ nodeId, log });
                }
            }
        }
        await writeAnalysisLogs(uid, entries);
        await updateDoc(doc(db, "users", uid), { analysisHistory: deleteField() });
        console.log(`%c[MİGRASYON] 📸 ${entries.length} fotoğraf analizi alt koleksiyona taşındı, ana dokümandan silindi.`, 'color:#a855f7;font-weight:bold');
    } catch (e) {
        console.error('[MİGRASYON] Fotoğraf analizleri taşınamadı:', e);
    } finally {
        analysisMigrationRunning = false;
    }
};

// --- FAVORİ YÖNETİMİ (GLOBAL BEĞENİ SİSTEMİ) ---

export const getFavorites = (): FavoriteQuestion[] => {
    // Önbellekteki soruların subject alanlarını, eksik veya hatalıysa dinamik olarak düzeltiyoruz
    return currentFavorites.map(f => {
        const resolvedSub = f.subject || getSubjectFromTopic(f.topic) || 'geography';
        if (f.subject !== resolvedSub) {
            return { ...f, subject: resolvedSub };
        }
        return f;
    });
};

export const isFavorite = (questionText: string): boolean => {
    return currentFavorites.some(f => f.question === questionText);
};

export const isDisliked = (questionText: string): boolean => {
    return currentUserProfile.dislikedQuestions?.includes(questionText) || false;
};

/**
 * Ortak beğeni havuzu (public_questions.likeCount: Yıldızlar Sıralaması, Kapı Bank topluluğu). 2026-10-29: sayaç yalnız
 * sunucuda (functions/src/questionLikes.ts › questionLike) değişir, kişi başı soru başına tek beğeni; kurallar istemcinin
 * sayacı yazmasını reddeder. Favori ve 👍 aynı beğeni kaydını kullanır (iki kez sayılmaz).
 */
async function sendQuestionLike(questionId: string, like: boolean, question?: Partial<GeminiTestQuestion>): Promise<void> {
    const user = auth.currentUser;
    if (!isFirebaseActive || !user || !questionId) return;
    try {
        const token = await user.getIdToken();
        const base = import.meta.env.VITE_BACKEND_URL || 'https://europe-west1-tarih-atlas-kpss.cloudfunctions.net';
        const q = question ? sanitizeData({ question: question.question, options: question.options, correctAnswerIndex: question.correctAnswerIndex, subject: question.subject, topic: question.topic, explanation: question.explanation, source: (question as any).source, year: question.year, image: question.image }) : undefined;
        const res = await fetch(`${base}/questionLike`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ questionId, like, question: q }),
        });
        if (!res.ok) console.warn('[questionLike]', res.status);
    } catch (e) {
        console.warn('[questionLike] beğeni gönderilemedi:', e);
    }
}

// BEĞENME İŞLEMİ: ID YOKSA OLUŞTURUR VE VERİTABANINA KAYDEDER
export const addFavorite = async (question: GeminiTestQuestion, sourceMode: string, subject?: SubjectType) => {
    // 1. Önce "Beğenmedim" listesindeyse oradan çıkar
    if (isDisliked(question.question)) {
        await dislikeQuestion(question.question, true);
    }

    // 2. Zaten favoriyse işlem yapma (Duplicate önleme)
    if (isFavorite(question.question)) return;

    // 3. ID KONTROLÜ VE ÜRETİMİ
    // Eğer sorunun ID'si yoksa -> Konu bilgisiyle beraber ID üret
    const qId = question.id || generateQuestionId(question.question, question.topic);
    
    // 🛡️ İLK KAYIT ZIRHI: Sorunun gerçek dersini tespit et
    const finalSubject = resolveQuestionSubject(question, subject || 'geography');

    // Soruyu tam objeye çevir (ID eklenmiş haliyle)
    const questionToSave: GeminiTestQuestion = {
        ...question,
        id: qId,
        questionType: question.questionType || 'manual-bank',
        // subject alanını garanti et
        subject: finalSubject,
        // Olası undefined alanları doldur
        explanation: question.explanation || "Açıklama mevcut değil.",
        year: question.year || "",
        topic: question.topic || "Genel"
    };

    const newFav: FavoriteQuestion = { 
        ...questionToSave, 
        id: qId, 
        dateAdded: Date.now(), 
        sourceMode: sourceMode || 'unknown'
    };

    // LOCAL CACHE UPDATE (Anında UI güncellemesi için EN ÖNEMLİ KISIM)
    // React state'i beklemeden hafızadaki diziyi güncelliyoruz.
    currentFavorites.push(newFav);

    if (isFirebaseActive) {
        // A) Ortak beğeni havuzu (sunucu sayar; soru havuzda yoksa ilk beğenide yazılır). UI'ı bekletmez.
        sendQuestionLike(qId, true, questionToSave);

        // B) KULLANICI FAVORİLERİNE EKLE (her favori ayrı doküman)
        if (auth.currentUser) {
            try {
                await setDoc(doc(db, "users", auth.currentUser.uid, FAVORITES, favDocId(newFav.question)), sanitizeData(newFav));
            } catch (e) {
                console.error("Kullanıcı favorisi eklenemedi:", e);
            }
        }
    } else {
        // Offline Mod
        const uid = currentUserProfile.uid;
        localStorage.setItem(`user_favorites_${uid}`, JSON.stringify(currentFavorites));
    }
};

export const removeFavorite = async (questionText: string) => {
    const favItem = currentFavorites.find(f => f.question === questionText);
    if (!favItem) return;

    const qId = favItem.id;

    // LOCAL CACHE UPDATE
    currentFavorites = currentFavorites.filter(f => f.question !== questionText);

    if (isFirebaseActive) {
        // A) Ortak beğeni havuzundan beğeniyi geri al (sunucu)
        if (qId) sendQuestionLike(qId, false);

        // B) KULLANICI FAVORİLERİNDEN SİL
        if (auth.currentUser) {
            try {
                await deleteDoc(doc(db, "users", auth.currentUser.uid, FAVORITES, favDocId(questionText)));
            } catch (e) { console.error("Kullanıcı favorisi silinemedi:", e); }
        }
    } else {
        // Offline Mod
        const uid = currentUserProfile.uid;
        localStorage.setItem(`user_favorites_${uid}`, JSON.stringify(currentFavorites));
    }
};

export const dislikeQuestion = async (questionText: string, remove: boolean) => {
    if (!currentUserProfile.dislikedQuestions) currentUserProfile.dislikedQuestions = [];

    if (remove) {
        currentUserProfile.dislikedQuestions = currentUserProfile.dislikedQuestions.filter(q => q !== questionText);
    } else {
        if (!currentUserProfile.dislikedQuestions.includes(questionText)) {
            currentUserProfile.dislikedQuestions.push(questionText);
            if (isFavorite(questionText)) {
                await removeFavorite(questionText);
            }
        }
    }
    
    // ⚠️ saveUserProfile yerine sadece dislikedQuestions alanını güncelle
    // Böylece chainData, wrongQuestions, aiUsage gibi alanlar DOKUNULMAZ
    if (isFirebaseActive && auth.currentUser && profileBaseline) {
        const userRef = doc(db, "users", auth.currentUser.uid);
        updateDoc(userRef, { dislikedQuestions: currentUserProfile.dislikedQuestions })
            .catch(e => console.error("[dislikeQuestion] Firestore güncelleme hatası:", e));
    }
    const uid = auth.currentUser?.uid || currentUserProfile.uid;
    if (uid) saveToLocal(`user_profile_${uid}`, currentUserProfile);
    notifyProfileSubscribers();
};

export const isQuestionLiked = (questionIdOrText: string): boolean => {
    const uid = currentUserProfile.uid || 'guest';
    const localLikes: string[] = loadFromLocal(`user_likes_${uid}`) || [];
    return localLikes.includes(questionIdOrText);
};

export const toggleLikeQuestion = async (question: any, subject?: SubjectType): Promise<{ liked: boolean; count: number }> => {
    const qId = question.id || generateQuestionId(question.question, question.topic);
    const qKey = qId || question.question;
    const uid = currentUserProfile.uid || 'guest';
    
    let localLikes: string[] = loadFromLocal(`user_likes_${uid}`) || [];
    const alreadyLiked = localLikes.includes(qKey);
    
    let newLiked = false;
    if (alreadyLiked) {
        localLikes = localLikes.filter((k: string) => k !== qKey && k !== question.question);
        newLiked = false;
    } else {
        localLikes.push(qKey);
        newLiked = true;
    }
    
    saveToLocal(`user_likes_${uid}`, localLikes);
    
    // Ortak beğeni sayacı sunucuda (kişi başı tek beğeni)
    if (qId) await sendQuestionLike(qId, newLiked, { ...question, subject: subject || question.subject });
    
    notifyProfileSubscribers();
    return { liked: newLiked, count: Math.max(0, (question.likeCount || 0) + (newLiked ? 1 : 0)) };
};

/**
 * 🎯 Tekli Soru Cevaplama Kaydı (Çıkmış Sorular & Hızlı Testler İçin)
 * Hem istatistikleri (stats, subTopics, totalQuestionsSeen, wrongAnswers) 
 * hem de yanlış yapıldıysa Zihin Ağını (wrongQuestions[nodeId]) anında günceller.
 * 
 * ⚠️ KRİTİK: Bu fonksiyon currentUserProfile'ı asla tümüyle ezmez!
 * Sadece stats, wrongQuestions ve learningStyle alanlarını IN-PLACE günceller.
 * chainData, aiUsage, isPremium gibi alanlara DOKUNMAZ.
 */
/** Çalışma planı: karneye giden cevaplar ders ders sayılır (services/studyPlanService.ts dinler, plan görevlerini işaretler) */
const emitAnswered = (items: { subject: SubjectType; correct: boolean }[]) => {
    try { if (items.length && typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('kpss-questions-answered', { detail: items })); } catch { /* yok */ }
};

export const recordSingleQuestionAnswer = async (
    q: GeminiTestQuestion | any,
    selectedOpt: number,
    subject: SubjectType
): Promise<{ isCorrect: boolean; targetNodeId: string }> => {
    emitAnswered([{ subject: resolveQuestionSubject(q, subject), correct: Number(selectedOpt) === Number(q.correctAnswerIndex) }]);
    // ⚠️ currentUserProfile'ı deep copy ile EZMEK YERİNE, doğrudan mutate ediyoruz
    if (!currentUserProfile.stats) currentUserProfile.stats = {};
    if (!currentUserProfile.wrongQuestions) currentUserProfile.wrongQuestions = {};
    if (!currentUserProfile.learningStyle) {
        currentUserProfile.learningStyle = { visual: 10, auditory: 10, analytical: 10 };
    }

    const isCorrect = Number(selectedOpt) === Number(q.correctAnswerIndex);
    const rawTopic = effectiveQuestionTopic(q); // TÜİK → metinden ünite
    // 🛡️ İLK KAYIT ZIRHI: Soru objesinden, konusundan veya metninden gerçek dersi %100 kesinlikle tespit et:
    const detectedSubject: SubjectType = resolveQuestionSubject(q, subject);
    const mainTopic = normalizeTopicName(rawTopic, detectedSubject);
    const subTopic = filterSubTopic(q.subTopic || ((rawTopic !== mainTopic && rawTopic !== 'Genel') ? rawTopic : null), detectedSubject, mainTopic);
    
    const statsKey = `${detectedSubject}_${mainTopic.replace(/\s+/g, '_')}`;

    if (!currentUserProfile.stats[statsKey]) {
        currentUserProfile.stats[statsKey] = {
            topicName: mainTopic,
            lesson: detectedSubject,
            totalQuestionsSeen: 0,
            wrongAnswers: 0,
            consecutiveRetryCount: 0,
            lastStudied: Date.now(),
            subTopics: {}
        };
    }

    const stat = currentUserProfile.stats[statsKey];
    stat.totalQuestionsSeen++;
    stat.lastStudied = Date.now();

    // Alt konu istatistikleri
    if (subTopic) {
        if (!stat.subTopics) stat.subTopics = {};
        const subKey = subTopic.replace(/\s+/g, '_');
        if (!stat.subTopics[subKey]) {
            stat.subTopics[subKey] = { name: subTopic, seen: 0, wrong: 0 };
        }
        stat.subTopics[subKey].seen++;
        if (!isCorrect) stat.subTopics[subKey].wrong++;
    }

    const targetNodeId = getTargetNodeId(mainTopic, detectedSubject);

    if (!isCorrect) {
        stat.wrongAnswers++;

        // Hata sorusunu hazırla
        const qId = q.id || generateQuestionId(q.question, q.topic);
        const questionWithId: GeminiTestQuestion = {
            ...q,
            id: qId,
            isResolved: false,
            addedDate: Date.now(),
            subject: detectedSubject,
            topic: mainTopic,
            subTopic: subTopic || undefined,
            questionType: q.questionType || 'past_exams',
            userAnswerIndex: typeof selectedOpt === 'number' && Number.isInteger(selectedOpt) ? selectedOpt : undefined
        } as GeminiTestQuestion;

        if (targetNodeId) {
            // 🧹 DİĞER TÜM YANLIŞ DÜĞÜMLERDEN TEMİZLE (Çapraz sızıntıyı anında imha et):
            const crossRemoved: { nodeId: string; q: any }[] = [];
            if (currentUserProfile.wrongQuestions) {
                Object.keys(currentUserProfile.wrongQuestions).forEach(existingNodeId => {
                    if (existingNodeId !== targetNodeId) {
                        currentUserProfile.wrongQuestions![existingNodeId] = pullWrongs(currentUserProfile.wrongQuestions![existingNodeId], existingNodeId,
                            existing => !(existing.question !== q.question && (!qId || existing.id !== qId)), crossRemoved);
                    }
                });
            }

            if (!currentUserProfile.wrongQuestions[targetNodeId]) {
                currentUserProfile.wrongQuestions[targetNodeId] = [];
            }
            const exists = currentUserProfile.wrongQuestions[targetNodeId].some(existing => existing.question === q.question || (existing.id && existing.id === qId));
            if (!exists) {
                currentUserProfile.wrongQuestions[targetNodeId].push(questionWithId);
                console.log(`%c[recordSingleQuestionAnswer] 🔴 Soru Hatası Doğru Zihin Haritasına Eklendi → Node ID: "${targetNodeId}" (${detectedSubject}) | Soru ID: "${qId}"`, 'color:#ef4444; font-weight:bold;');
            }

            // ⚠️ DOĞRUDAN alt koleksiyona yaz (yalnızca değişen sorular)
            if (isFirebaseActive && auth.currentUser) {
                const uid = auth.currentUser.uid;
                syncWrongNodes(uid, currentUserProfile.wrongQuestions, [targetNodeId]).catch(err => {
                    console.error(`[recordSingleQuestionAnswer] ❌ Alt koleksiyon yazma hatası (${targetNodeId}):`, err);
                });
                removeWrongDocs(uid, crossRemoved).catch(console.warn);
            }
        }
    } else {
        // Eğer doğru çözüldüyse ve daha önce yanlışlar listesindeyse resolved işaretle
        if (targetNodeId && currentUserProfile.wrongQuestions[targetNodeId]) {
            currentUserProfile.wrongQuestions[targetNodeId] = currentUserProfile.wrongQuestions[targetNodeId].map(existing => {
                if (existing.question === q.question || (q.id && existing.id === q.id)) {
                    return { ...existing, isResolved: true, solvedDate: Date.now() };
                }
                return existing;
            });

            // Çözülen soruları da alt koleksiyona yaz
            if (isFirebaseActive && auth.currentUser) {
                syncWrongNodes(auth.currentUser.uid, currentUserProfile.wrongQuestions, [targetNodeId]).catch(console.warn);
            }
        }
    }

    // Öğrenme stili güncellemesi
    if (mainTopic.toLowerCase().includes("harita") || mainTopic.toLowerCase().includes("konum")) {
        currentUserProfile.learningStyle.visual += isCorrect ? 1 : 0.2;
    } else if (mainTopic.toLowerCase().includes("ekonomi") || mainTopic.toLowerCase().includes("analiz") || subject === 'math') {
        currentUserProfile.learningStyle.analytical += isCorrect ? 1 : 0.2;
    } else {
        currentUserProfile.learningStyle.auditory += isCorrect ? 1 : 0.2;
    }

    // ⚠️ Sadece stats (user_data/stats) ve learningStyle (ana doküman) kaydedilir
    // chainData, wrongQuestions, aiUsage, isPremium, freeExamsUsed vb. DOKUNULMAZ
    // Beklenmez (internetsizken kuyrukta kalır); önbellekten açılmış profil de yazabilir (yalnızca değişenler gider)
    if (isFirebaseActive && auth.currentUser && profileBaseline) {
        const uid = auth.currentUser.uid;
        Promise.all([
            writeStatsDoc(uid, currentUserProfile.stats),
            updateDoc(doc(db, "users", uid), { learningStyle: sanitizeData(currentUserProfile.learningStyle) }),
        ]).catch(e => console.error("[recordSingleQuestionAnswer] ❌ Stats kayıt hatası:", e));
    }
    
    // Yerel önbelleği güncelle
    const uid = auth.currentUser?.uid || currentUserProfile.uid;
    if (uid) saveToLocal(`user_profile_${uid}`, currentUserProfile);
    
    notifyProfileSubscribers();

    return { isCorrect, targetNodeId };
};

// --- FLASH SESSION YÖNETİMİ (YENİ) ---

export const getFlashSessions = (topic?: string): FlashSession[] => {
    const history = currentUserProfile.flashHistory || [];
    if (!topic) return history;
    // "Tüm Müfredat" veya "Karışık" durumunu ele al
    if (topic === "Tüm Müfredat (Karışık)") {
        return history.filter(h => h.topic === topic);
    }
    return history.filter(h => h.topic === topic);
};

export const saveFlashSession = async (topic: string, questions: GeminiTestQuestion[], subject: SubjectType = 'geography') => {
    const profile = JSON.parse(JSON.stringify(currentUserProfile)) as UserProfile;
    if (!profile.flashHistory) profile.flashHistory = [];

    // Kaçıncı flash olduğunu hesapla
    const existingCount = profile.flashHistory.filter(h => h.topic === topic).length;
    const title = `${topic} Flash #${existingCount + 1}`;

    const newSession: FlashSession = {
        id: `flash_${Date.now()}`,
        subject: subject,
        topic: topic || "Genel Karma",
        date: Date.now(),
        questions: questions,
        title: title,
        questionCount: questions.length,
        createdBy: auth.currentUser?.email || 'unknown',
    };

    // Yeni flash'ı başa ekle
    profile.flashHistory.unshift(newSession);
    
    // Çok şişmemesi için limit koyabiliriz (örneğin son 50 flash)
    if (profile.flashHistory.length > 50) {
        profile.flashHistory = profile.flashHistory.slice(0, 50);
    }

    // Ana dokümana değil kendi dokümanına (50 oturum × tam sorular ana dokümanı 1 MB'a taşırdı)
    currentUserProfile.flashHistory = profile.flashHistory;
    if (isFirebaseActive && auth.currentUser) {
        await setDoc(doc(db, "users", auth.currentUser.uid, "flash_sessions", newSession.id), sanitizeData(newSession));
    }
    return newSession;
};

// --- YEREL VERİTABANI YARDIMCILARI ---
const compactProfileForStorage = (profileObj: any) => {
    if (!profileObj || typeof profileObj !== 'object') return profileObj;
    const clone = { ...profileObj };
    
    // Yerel önbellekte testHistory'yi en son 3 test ile sınırla ve base64 görselleri at
    if (Array.isArray(clone.testHistory)) {
        clone.testHistory = clone.testHistory.slice(0, 3).map((t: any) => ({
            id: t.id,
            date: t.date,
            subject: t.subject,
            score: t.score,
            totalQuestions: t.totalQuestions,
            testType: t.testType,
            testTitle: t.testTitle,
            questions: (t.questions || []).map((q: any) => ({
                id: q.id,
                question: q.question,
                options: q.options,
                correctAnswerIndex: q.correctAnswerIndex,
                image: q.image?.startsWith('data:') ? undefined : q.image
            }))
        }));
    }

    // Yerel önbellekteki wrongQuestions'ta base64 varsa temizle
    if (clone.wrongQuestions && typeof clone.wrongQuestions === 'object') {
        const compactWrongs: Record<string, any[]> = {};
        for (const [k, v] of Object.entries(clone.wrongQuestions)) {
            if (Array.isArray(v)) {
                compactWrongs[k] = v.map((q: any) => ({
                    id: q.id,
                    question: q.question,
                    options: q.options,
                    correctAnswerIndex: q.correctAnswerIndex,
                    topic: q.topic,
                    isRepaired: q.isRepaired,
                    isResolved: q.isResolved,
                    isStubborn: q.isStubborn,
                    image: q.image?.startsWith('data:') ? undefined : q.image
                }));
            }
        }
        clone.wrongQuestions = compactWrongs;
    }

    // analysisHistory'deki base64'leri yerel kopyadan kaldır (bulut Storage URL'leri kalır, hiçbir analiz kaydı silinmez)
    if (clone.analysisHistory && typeof clone.analysisHistory === 'object') {
        const compactAnalysis: Record<string, any[]> = {};
        for (const [k, v] of Object.entries(clone.analysisHistory)) {
            if (Array.isArray(v)) {
                compactAnalysis[k] = v.map((log: any) => ({
                    id: log.id,
                    analysisText: log.analysisText || '',
                    date: log.date,
                    timestamp: log.timestamp,
                    topic: log.topic,
                    subTopic: log.subTopic,
                    fileName: log.fileName,
                    imageHash: log.imageHash,
                    isRepaired: log.isRepaired,
                    isResolved: log.isResolved,
                    isStubborn: log.isStubborn,
                    imageUrl: log.imageUrl?.startsWith('data:') ? undefined : log.imageUrl
                }));
            }
        }
        clone.analysisHistory = compactAnalysis;
    }

    return clone;
};

const loadFromLocal = (key: string) => {
    try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
    } catch (e) { return null; }
};

const saveToLocal = (key: string, data: any) => {
    try {
        const dataToSave = (key.startsWith('user_profile_') || key === 'kpss_current_user_profile')
            ? compactProfileForStorage(data)
            : data;
        localStorage.setItem(key, JSON.stringify(dataToSave));
    } catch (e) { 
        try {
            // Kota dolduysa gereksiz önbellekleri (RAG, Harita Detay, Chat) temizle
            for (let i = localStorage.length - 1; i >= 0; i--) {
                const k = localStorage.key(i);
                if (k && (k.startsWith('topic_cache_') || k.startsWith('loc_cache_') || k.startsWith('chat_history_') || k.startsWith('rag_') || k.startsWith('offline_'))) {
                    localStorage.removeItem(k);
                }
            }
            // Minimal profil versiyonunu kaydet
            const minimalData = compactProfileForStorage(data);
            if (minimalData && minimalData.testHistory) {
                minimalData.testHistory = minimalData.testHistory.slice(0, 1);
            }
            localStorage.setItem(key, JSON.stringify(minimalData));
        } catch (retryErr) {
            // Sessiz hata yönetimi - kullanıcı arayüzünü kilitlemez
        }
    }
};

// --- KİMLİK DOĞRULAMA ---

const promiseWithTimeout = <T>(promise: Promise<T>, ms: number, timeoutError = new Error('Zaman aşımı')): Promise<T> => {
    return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
            reject(timeoutError);
        }, ms);
        promise.then(
            (res) => {
                clearTimeout(timeoutId);
                resolve(res);
            },
            (err) => {
                clearTimeout(timeoutId);
                reject(err);
            }
        );
    });
};

// --- APPLE SIGN-IN GÜVENLİK YARDIMCILARI ---
const generateNonce = (length = 32) => {
    const charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._';
    let result = '';
    const values = new Uint8Array(length);
    window.crypto.getRandomValues(values);
    for (let i = 0; i < length; i++) {
        result += charset[values[i] % charset.length];
    }
    return result;
};

const sha256 = async (rawNonce: string) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(rawNonce);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Giriş bekleme ekranı (2026-10-16): hesap onaylandıktan sonra Firebase'e bağlanma birkaç saniye sürüyor, ekranda
 * hâlâ "hesabını seç / Face ID'yi onayla" yazıyordu. Native akış onaydan sonra 'kpss-auth-verifying' yayınlar
 * (giriş penceresi "Giriş yapılıyor" der); yönlendirmeli akış (Android/mobil web Google) dönüşte sonuç okunana kadar
 * App tam ekran bekleme gösterir (AUTH_REDIRECT_KEY, sessionStorage).
 */
export const AUTH_REDIRECT_KEY = 'kpss_auth_redirect_at';
const notifyAuthVerifying = () => { try { window.dispatchEvent(new CustomEvent('kpss-auth-verifying')); } catch { /* yoksay */ } };
const markAuthRedirect = () => { try { sessionStorage.setItem(AUTH_REDIRECT_KEY, String(Date.now())); } catch { /* yoksay */ } };

export const loginWithGoogle = async () => {
    console.log('🔍 [LOGIN] loginWithGoogle çağırıldı. isFirebaseActive:', isFirebaseActive);
    if (!isFirebaseActive) {
        alert("Bulut bağlantısı yok. Lütfen internet bağlantınızı kontrol edin.");
        throw new Error("Çevrimdışı modda giriş yapılamaz.");
    }

    try {
        let resultUser: any = null;

        // TrendModa pattern: Mobil Web → signInWithRedirect
        const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        if (isNativeIOS()) {
            // iOS uygulaması: native Google Sign-In (@capgo/capacitor-social-login) → idToken → Firebase credential.
            // signInWithRedirect Capacitor WKWebView'de çalışmaz: Google gömülü web görünümünde girişi engeller
            // (disallowed_useragent) ve native auth popup/redirect çözücüsüz kurulu (services/firebase.ts).
            // Kimlikler gizli değil: iOS istemcisi GoogleService-Info.plist'teki CLIENT_ID; sunucu istemcisi Firebase
            // Google sağlayıcısının web istemcisi (idToken'ın audience'ı bu olur, Firebase kabul eder).
            const { SocialLogin } = await import('@capgo/capacitor-social-login');
            await SocialLogin.initialize({
                google: {
                    iOSClientId: '722424420524-ibkb1r59n9en28u7lckj8rs215jc0ct4.apps.googleusercontent.com',
                    iOSServerClientId: '722424420524-2ptmjmjmqjdi29jer9rh4bak50ji6rn2.apps.googleusercontent.com',
                    mode: 'online',
                },
            });
            let login: any;
            try {
                login = await SocialLogin.login({ provider: 'google', options: { scopes: ['email', 'profile'] } });
            } catch (e: any) {
                // Kullanıcı Google penceresini kapattı / iptal etti
                if (/cancel|iptal|canceled|cancelled|-5/i.test(String(e?.message || e?.code || e))) {
                    console.warn('🔍 [LOGIN] Google girişi kullanıcı tarafından iptal edildi.');
                    return null;
                }
                throw e;
            }
            const idToken = login?.result?.idToken;
            if (!idToken) throw new Error("Google kimlik belirteci alınamadı.");
            notifyAuthVerifying();
            const credential = GoogleAuthProvider.credential(idToken);
            const result = await signInWithCredential(auth, credential);
            resultUser = result.user;
        } else if (isMobileDevice) {
            console.log('🔍 [LOGIN] Mobil ortam algılandı → signInWithRedirect başlatılıyor...');
            markAuthRedirect();
            await signInWithRedirect(auth, googleProvider);
            // Redirect olacağı için Promise asla resolve olmaz, sayfadan çıkılır
            return new Promise<any>(() => {});
        } else {
            // Masaüstü tarayıcı → signInWithPopup
            console.log('🔍 [LOGIN] Masaüstü ortam → signInWithPopup başlatılıyor...');
            const timeoutPromise = new Promise<never>((_, reject) => {
                setTimeout(() => {
                    reject(new Error("Giriş penceresi zaman aşımına uğradı veya yanıt vermedi. Lütfen tekrar deneyin."));
                }, 25000);
            });

            const result = await Promise.race([
                signInWithPopup(auth, googleProvider),
                timeoutPromise
            ]);
            resultUser = result.user;
        }

        console.log('✅ [LOGIN] Google giriş başarılı! UID:', resultUser.uid, 'Ad:', resultUser.displayName);
        
        try {
            localStorage.setItem('kpss_is_logged_in', 'true');
            localStorage.setItem('kpss_cached_user', JSON.stringify({
                uid: resultUser.uid,
                displayName: resultUser.displayName || 'Öğrenci',
                email: resultUser.email || '',
                photoURL: resultUser.photoURL || null
            }));
        } catch (e) {
            console.warn('[LOGIN] localStorage cache hatası:', e);
        }

        // Firestore'da kullanıcı dokümanı yoksa oluştur (arka planda hata vermeden çalışır)
        const firestoreUid = resultUser.uid;
        const firestoreName = resultUser.displayName || 'Öğrenci';
        const firestoreEmail = resultUser.email || '';
        (async () => {
            try {
                const userRef = doc(db, "users", firestoreUid);
                const userDoc = await getDoc(userRef);
                if (!userDoc.exists()) {
                    console.log('🔍 [LOGIN] Yeni kullanıcı Firestore\'a kaydediliyor (fire-and-forget)...');
                    await setDoc(userRef, {
                        ...defaultProfile,
                        uid: firestoreUid,
                        name: firestoreName,
                        email: firestoreEmail,
                        acceptedTerms: true,
                        acceptedTermsAt: new Date().toISOString()
                    });
                }
            } catch (docErr) {
                console.warn('[LOGIN] Firestore kullanıcı dokümanı hatası (oturum geçerli):', docErr);
            }
        })();

        return resultUser;
    } catch (error: any) {
        // Kullanıcı popup'ı kapattıysa sessizce geç
        if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
            console.log('🔍 [LOGIN] Kullanıcı popup\'ı kapattı.');
            return null;
        }
        // TrendModa pattern: popup engellendiyse redirect'e geç
        if (error.code === 'auth/popup-blocked') {
            console.log('🔍 [LOGIN] Popup engellendi → signInWithRedirect\'e geçiliyor...');
            markAuthRedirect();
            await signInWithRedirect(auth, googleProvider);
            return new Promise<any>(() => {});
        }
        console.error("❌ [LOGIN] Google Giriş Hatası:", error.code, error.message);
        throw error;
    }
};

export const loginWithApple = async () => {
    console.log('🍎 [APPLE-LOGIN] ══════════════════════════════════════');
    console.log('🍎 [APPLE-LOGIN] ADIM 0: loginWithApple başlatıldı');
    console.log('🍎 [APPLE-LOGIN] isFirebaseActive:', isFirebaseActive);
    console.log('🍎 [APPLE-LOGIN] isNativeIOS:', isNativeIOS());
    console.log('🍎 [APPLE-LOGIN] Platform:', navigator.userAgent.substring(0, 80));
    console.log('🍎 [APPLE-LOGIN] ══════════════════════════════════════');
    
    if (!isFirebaseActive) {
        alert("Bulut bağlantısı yok. Lütfen internet bağlantınızı kontrol edin.");
        throw new Error("Çevrimdışı modda giriş yapılamaz.");
    }

    try {
        let authUser: any = null;
        let appleGivenName: string | null = null;
        let appleFamilyName: string | null = null;
        let appleEmail: string | null = null;

        if (isNativeIOS()) {
            // ═══════════════════════════════════════════
            // ADIM 1: Capacitor Apple Sign-In Plugin'i Bul
            // ═══════════════════════════════════════════
            console.log('🍎 [APPLE-LOGIN] ADIM 1: Capacitor plugin aranıyor...');
            let AppleSignInPlugin: any = null;
            try {
                const mod = await import('@capacitor-community/apple-sign-in');
                AppleSignInPlugin = mod.SignInWithApple;
                console.log('🍎 [APPLE-LOGIN] ADIM 1 ✅ Plugin dynamic import ile bulundu');
            } catch (e) {
                console.warn('🍎 [APPLE-LOGIN] ADIM 1 ⚠️ Dynamic import başarısız, window.Capacitor fallback deneniyor:', e);
            }
            if (!AppleSignInPlugin) {
                AppleSignInPlugin = (window as any).Capacitor?.Plugins?.SignInWithApple;
                if (AppleSignInPlugin) {
                    console.log('🍎 [APPLE-LOGIN] ADIM 1 ✅ Plugin window.Capacitor.Plugins ile bulundu');
                }
            }
            if (!AppleSignInPlugin) {
                console.error('🍎 [APPLE-LOGIN] ADIM 1 ❌ Plugin BULUNAMADI!');
                throw new Error("Apple Sign In eklentisi bulunamadı. Capacitor native build gereklidir.");
            }

            // ═══════════════════════════════════════════
            // ADIM 2: Nonce Üret (Apple + Firebase güvenlik gereksinimleri)
            // Apple'a SHA256 HASH'lenmiş nonce gönderilir
            // Firebase'e RAW (orijinal) nonce gönderilir
            // Bu iki değer eşleşmezse auth/missing-or-invalid-nonce hatası alınır!
            // ═══════════════════════════════════════════
            console.log('🍎 [APPLE-LOGIN] ADIM 2: Nonce üretiliyor (güvenlik protokolü)...');
            const rawNonce = generateNonce(32);
            console.log('🍎 [APPLE-LOGIN] ADIM 2a: Raw nonce üretildi (ilk 8 karakter):', rawNonce.substring(0, 8) + '...');
            
            const hashedNonce = await sha256(rawNonce);
            console.log('🍎 [APPLE-LOGIN] ADIM 2b: SHA256 hash tamamlandı (ilk 16 karakter):', hashedNonce.substring(0, 16) + '...');

            // ═══════════════════════════════════════════
            // ADIM 3: Apple'ın Yetkilendirme Ekranını Aç (Face ID / Şifre)
            // ═══════════════════════════════════════════
            console.log('🍎 [APPLE-LOGIN] ADIM 3: Apple authorize çağrılıyor (Face ID / Şifre ekranı açılacak)...');
            const authorizeOptions = {
                clientId: 'com.kapihoca.kpss',
                redirectURI: 'https://tarih-atlas-kpss.firebaseapp.com/__/auth/handler',
                scopes: 'email name',
                nonce: hashedNonce,  // ← SHA256 HASH'LENMİŞ nonce Apple'a gider!
            };
            console.log('🍎 [APPLE-LOGIN] ADIM 3: authorize seçenekleri:', JSON.stringify({
                clientId: authorizeOptions.clientId,
                redirectURI: authorizeOptions.redirectURI,
                scopes: authorizeOptions.scopes,
                nonceLength: authorizeOptions.nonce.length,
            }));

            const res = await AppleSignInPlugin.authorize(authorizeOptions);
            
            console.log('🍎 [APPLE-LOGIN] ADIM 3 ✅ Apple authorize yanıtı geldi!');
            console.log('🍎 [APPLE-LOGIN]   → identityToken var mı:', !!res.response?.identityToken);
            console.log('🍎 [APPLE-LOGIN]   → identityToken uzunluk:', res.response?.identityToken?.length || 0);
            console.log('🍎 [APPLE-LOGIN]   → givenName:', res.response?.givenName || '(yok)');
            console.log('🍎 [APPLE-LOGIN]   → familyName:', res.response?.familyName || '(yok)');
            console.log('🍎 [APPLE-LOGIN]   → email:', res.response?.email || '(yok)');
            console.log('🍎 [APPLE-LOGIN]   → response nonce:', (res.response as any)?.nonce || '(yok)');

            if (!res.response || !res.response.identityToken) {
                console.error('🍎 [APPLE-LOGIN] ADIM 3 ❌ identityToken ALINAMADI!');
                throw new Error("Apple kimlik doğrulama belirteci alınamadı.");
            }

            appleGivenName = res.response.givenName;
            appleFamilyName = res.response.familyName;
            appleEmail = res.response.email;

            // ═══════════════════════════════════════════
            // ADIM 4: Firebase Credential Oluştur
            // rawNonce = SHA256 öncesi orijinal değer → Firebase bu ile doğrular
            // ═══════════════════════════════════════════
            console.log('🍎 [APPLE-LOGIN] ADIM 4: Firebase OAuthCredential oluşturuluyor...');
            console.log('🍎 [APPLE-LOGIN]   → rawNonce (ilk 8):', rawNonce.substring(0, 8) + '...');
            
            const provider = new OAuthProvider('apple.com');
            const credential = provider.credential({
                idToken: res.response.identityToken,
                rawNonce: rawNonce  // ← RAW (hash'lenmemiş) nonce Firebase'e gider!
            });
            console.log('🍎 [APPLE-LOGIN] ADIM 4 ✅ Credential oluşturuldu');

            // ═══════════════════════════════════════════
            // ADIM 5: Firebase signInWithCredential
            // ═══════════════════════════════════════════
            notifyAuthVerifying();
            console.log('🍎 [APPLE-LOGIN] ADIM 5: signInWithCredential çağrılıyor (Firebase Auth)...');
            // Takılırsa sessizce donmasın, hata olarak ekrana düşsün
            const result: any = await Promise.race([
                signInWithCredential(auth, credential),
                new Promise((_, reject) => setTimeout(() => reject(new Error('Firebase signInWithCredential 20 sn içinde yanıt vermedi (timeout)')), 20000)),
            ]);
            authUser = result.user;
            console.log('🍎 [APPLE-LOGIN] ADIM 5 ✅ signInWithCredential BAŞARILI!');
            console.log('🍎 [APPLE-LOGIN]   → UID:', authUser.uid);
            console.log('🍎 [APPLE-LOGIN]   → Email:', authUser.email || '(gizli)');
            console.log('🍎 [APPLE-LOGIN]   → DisplayName:', authUser.displayName || '(yok)');
        } else {
            // ═══════════════════════════════════════════
            // WEB AKIŞI (PWA, TWA, Safari vb.)
            // ═══════════════════════════════════════════
            console.log('🍎 [APPLE-LOGIN] WEB AKIŞI başlatılıyor...');
            const provider = new OAuthProvider('apple.com');
            provider.addScope('email');
            provider.addScope('name');
            
            const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            
            if (isMobileDevice) {
                console.log('🍎 [APPLE-LOGIN] WEB AKIŞI: Mobil cihaz algılandı, signInWithRedirect kullanılıyor...');
                // signInWithRedirect sayfayı yenileyeceği için hemen sonuç dönmez. 
                // Sonuç App.tsx'teki getRedirectResult() ile yakalanır.
                markAuthRedirect();
                await signInWithRedirect(auth, provider);
                return null;
            } else {
                console.log('🍎 [APPLE-LOGIN] WEB AKIŞI: Masaüstü algılandı, signInWithPopup kullanılıyor...');
                const timeoutPromise = new Promise<never>((_, reject) => {
                    setTimeout(() => {
                        reject(new Error("Apple giriş penceresi zaman aşımına uğradı (30sn)."));
                    }, 30000);
                });
                const result = await Promise.race([
                    signInWithPopup(auth, provider),
                    timeoutPromise
                ]);
                authUser = result.user;
                console.log('🍎 [APPLE-LOGIN] WEB AKIŞI ✅ signInWithPopup başarılı! UID:', authUser.uid);
            }
        }

        if (!authUser) {
            console.log('🍎 [APPLE-LOGIN] ⚠️ authUser null — giriş iptal edilmiş olabilir');
            return null;
        }

        // ═══════════════════════════════════════════
        // ADIM 6: Kullanıcı Adı Belirleme
        // Apple sadece İLK girişte givenName/familyName verir
        // ═══════════════════════════════════════════
        console.log('🍎 [APPLE-LOGIN] ADIM 6: Kullanıcı adı belirleniyor...');
        let resolvedName = authUser.displayName;
        if ((!resolvedName || resolvedName === 'Apple Kullanıcısı') && (appleGivenName || appleFamilyName)) {
            resolvedName = `${appleGivenName || ''} ${appleFamilyName || ''}`.trim();
            console.log('🍎 [APPLE-LOGIN] ADIM 6: Apple\'dan gelen isim kullanılıyor:', resolvedName);
            try {
                await updateProfile(authUser, { displayName: resolvedName });
                console.log('🍎 [APPLE-LOGIN] ADIM 6 ✅ Firebase profil ismi güncellendi');
            } catch (e) {
                console.warn('🍎 [APPLE-LOGIN] ADIM 6 ⚠️ Profil ismi güncellenemedi:', e);
            }
        }
        if (!resolvedName) {
            resolvedName = 'Apple Kullanıcısı';
        }

        console.log('🍎 [APPLE-LOGIN] ══════════════════════════════════════');
        console.log('🍎 [APPLE-LOGIN] ✅ GİRİŞ BAŞARILI!');
        console.log('🍎 [APPLE-LOGIN]   → UID:', authUser.uid);
        console.log('🍎 [APPLE-LOGIN]   → Ad:', resolvedName);
        console.log('🍎 [APPLE-LOGIN]   → Email:', authUser.email || appleEmail || '(gizli)');
        console.log('🍎 [APPLE-LOGIN] ══════════════════════════════════════');

        // ═══════════════════════════════════════════
        // ADIM 7: localStorage'a Kaydet (race condition koruması)
        // ═══════════════════════════════════════════
        try {
            localStorage.setItem('kpss_is_logged_in', 'true');
            localStorage.setItem('kpss_cached_user', JSON.stringify({
                uid: authUser.uid,
                displayName: resolvedName,
                email: authUser.email || appleEmail || '',
                photoURL: authUser.photoURL || null
            }));
            console.log('🍎 [APPLE-LOGIN] ADIM 7 ✅ localStorage cache kaydedildi');
        } catch (e) {
            console.warn('🍎 [APPLE-LOGIN] ADIM 7 ⚠️ localStorage cache hatası:', e);
        }

        // ═══════════════════════════════════════════
        // ADIM 8: Firestore Doküman (FIRE AND FORGET — girişi bloke etmez!)
        // ═══════════════════════════════════════════
        const firestoreUserId = authUser.uid;
        const firestoreResolvedName = resolvedName;
        const firestoreEmail = authUser.email || appleEmail || '';
        (async () => {
            try {
                console.log('🍎 [APPLE-LOGIN] ADIM 8: Firestore doküman kontrolü (arka plan)...');
                const userRef = doc(db, "users", firestoreUserId);
                const userDoc = await getDoc(userRef);
                if (!userDoc.exists()) {
                    console.log('🍎 [APPLE-LOGIN] ADIM 8: Yeni kullanıcı — Firestore dokümanı oluşturuluyor...');
                    await setDoc(userRef, {
                        ...defaultProfile,
                        uid: firestoreUserId,
                        name: firestoreResolvedName,
                        email: firestoreEmail,
                        acceptedTerms: true,
                        acceptedTermsAt: new Date().toISOString()
                    });
                    console.log('🍎 [APPLE-LOGIN] ADIM 8 ✅ Firestore dokümanı oluşturuldu');
                } else {
                    console.log('🍎 [APPLE-LOGIN] ADIM 8 ✅ Mevcut Firestore dokümanı zaten var');
                }
            } catch (docErr) {
                console.warn('🍎 [APPLE-LOGIN] ADIM 8 ⚠️ Firestore hatası (oturum yine de geçerli):', docErr);
            }
        })();

        // authUser'ı hemen dön — Firestore arka planda tamamlansın
        return authUser;
    } catch (error: any) {
        if (
            error.code === 'auth/popup-closed-by-user' ||
            error.code === 'auth/cancelled-popup-request' ||
            error.message?.includes('canceled') ||
            error.message?.includes('cancelled') ||
            error.error === '1001' // ASAuthorizationErrorCanceled
        ) {
            console.log('🔍 [LOGIN] Kullanıcı Apple girişini iptal etti.');
            return null;
        }
        console.error("❌ [LOGIN] Apple Giriş Hatası:", error.code || error.error, error.message);
        throw error;
    }
};



// --- REDIRECT SONUÇ İŞLEYİCİ (Fallback) ---
export const handleRedirectResult = async () => {
    console.log('🔍 [REDIRECT] handleRedirectResult çağırıldı. isFirebaseActive:', isFirebaseActive);
    if (!isFirebaseActive) return null;
    try {
        const result = await getRedirectResult(auth);
        if (result) {
            console.log("✅ [REDIRECT] Redirect giriş başarılı:", result.user.displayName);
            const userRef = doc(db, "users", result.user.uid);
            const userDoc = await getDoc(userRef);
            if (!userDoc.exists()) {
                await setDoc(userRef, {
                    ...defaultProfile,
                    uid: result.user.uid,
                    name: result.user.displayName || 'Öğrenci',
                    email: result.user.email || '',
                    acceptedTerms: true,
                    acceptedTermsAt: new Date().toISOString()
                });
            }
            return result.user;
        }
        return null;
    } catch (error: any) {
        if (error.code === 'auth/credential-already-in-use') {
            const credential = GoogleAuthProvider.credentialFromError(error);
            if (credential) {
                const result = await signInWithCredential(auth, credential);
                return result.user;
            }
        }
        console.error("Redirect Sonuç Hatası:", error);
        return null;
    }
};

export const logoutUser = async () => {
    try {
        localStorage.removeItem('kpss_is_logged_in');
        localStorage.removeItem('kpss_cached_user');
        if (isFirebaseActive) {
            try {
                // signOut'tan ÖNCE bitmeli: sonra yazma yetkisiz kalıyor, cihaz token'ı eski hesapta kalıp
                // aynı cihazdaki sonraki hesapla çift bildirim gidiyordu. Çevrimdışıda çıkışı en fazla 3 sn bekletir.
                await Promise.race([
                    notificationService.removePushSubscriptionOnLogout(),
                    new Promise(resolve => setTimeout(resolve, 3000))
                ]);
            } catch (err) {
                console.warn("Could not remove push subscription on logout:", err);
            }
            await signOut(auth);
        } else {
            localStorage.removeItem('kpss_local_user');
            sessionStorage.removeItem('just_logged_in');
            window.location.reload();
        }
        currentUserProfile = { ...defaultProfile };
        currentFavorites = [];
    } catch (error) {
        console.error("Çıkış Hatası:", error);
    }
};

export const deleteUserAccount = async () => {
    try {
        if (!auth.currentUser) {
            throw new Error("Kullanıcı kimliği doğrulanmadı.");
        }

        const user = auth.currentUser;
        const uid = user.uid;

        // 1. Call Backend to Delete Cloud Data
        if (isFirebaseActive) {
            const token = await user.getIdToken();
            const response = await fetch(`https://europe-west1-tarih-atlas-kpss.cloudfunctions.net/deleteUserData`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ uid })
            });

            if (!response.ok) {
                const errBody = await response.json().catch(() => ({}));
                throw new Error(errBody.error || "Sunucudaki kullanıcı verileri silinemedi.");
            }
        }

        // 2. Delete Firebase Auth User
        await user.delete();

        // 3. Clear Local storage & variables
        currentUserProfile = { ...defaultProfile };
        currentFavorites = [];
        localStorage.clear();
        sessionStorage.clear();
    } catch (error) {
        console.error("Hesap Silme Hatası:", error);
        throw error;
    }
};

// ═══ PROFİL DEĞİŞİKLİK DİNLEYİCİ (React state sync) ═══
const profileSubscribers = new Set<(profile: UserProfile) => void>();
export const onProfileUpdate = (callback: (profile: UserProfile) => void): (() => void) => {
    profileSubscribers.add(callback);
    return () => { profileSubscribers.delete(callback); };
};
const notifyProfileSubscribers = () => {
    profileSubscribers.forEach(cb => cb({ ...currentUserProfile }));
};

// --- VERİ SENKRONİZASYONU ---

export const migrateLegacyUserData = async (uid: string, profile: UserProfile) => {
    if (!isFirebaseActive) return;
    
    let needsUpdate = false;
    const updatePayload: Record<string, any> = {};

    // (Sınav geçmişi artık migrateLegacyTestHistory ile taşınır; ana dokümana hiç yazılmaz.)

    // Sohbet Geçmişlerini Göç Ettir
    if (profile.chatSessions && Object.keys(profile.chatSessions).length > 0) {
        console.log(`%c[MİGRASYON] 🚛 Sohbet oturumları subcollection'a taşınıyor...`, 'color:#d97706;font-weight:bold');
        for (const [key, messages] of Object.entries(profile.chatSessions)) {
            if (!messages || messages.length === 0) continue;
            try {
                const sessionRef = doc(db, "users", uid, "chat_sessions", key);
                // Bulutta zaten oturum varsa eski kopya onu ezmesin
                const existing = await getDoc(sessionRef);
                if (!existing.exists() || !(existing.data()?.messages?.length)) {
                    await setDoc(sessionRef, { messages: sanitizeData(messages.slice(-CHAT_SESSION_MAX)) }, { merge: true });
                }
            } catch (e) {
                console.error(`[MİGRASYON] Sohbet kaydetme hatası (${key}):`, e);
            }
        }
        // Ana profil dokümanından chatSessions temizle
        updatePayload.chatSessions = {};
        needsUpdate = true;
    }
    
    // Legacy chatHistory
    if (profile.chatHistory && profile.chatHistory.length > 0) {
        try {
            const sessionRef = doc(db, "users", uid, "chat_sessions", "cezeri_global");
            const existing = await getDoc(sessionRef);
            if (!existing.exists() || !(existing.data()?.messages?.length)) {
                await setDoc(sessionRef, { messages: sanitizeData(profile.chatHistory.slice(-CHAT_SESSION_MAX)) }, { merge: true });
            }
        } catch (e) {
            console.error(`[MİGRASYON] Legacy chatHistory hatası:`, e);
        }
        updatePayload.chatHistory = [];
        needsUpdate = true;
    }

    if (needsUpdate) {
        try {
            const userRef = doc(db, "users", uid);
            await updateDoc(userRef, updatePayload);
            console.log(`%c[MİGRASYON] 🎉 Başarıyla tamamlandı, ana doküman temizlendi!`, 'color:#22c55e;font-weight:bold');
        } catch (e) {
            console.error('[MİGRASYON] Ana doküman temizleme hatası:', e);
        }
    }
};


export const mergeProfiles = (local: UserProfile, remote: UserProfile): { merged: UserProfile; changed: boolean } => {
    let changed = false;
    const merged = JSON.parse(JSON.stringify(remote)) as UserProfile;

    if (!merged.stats) merged.stats = {};
    if (!merged.wrongQuestions) merged.wrongQuestions = {};
    if (!merged.conqueredRegions) merged.conqueredRegions = {};
    if (!merged.highScores) merged.highScores = {};
    if (!merged.solvedAgentMissions) merged.solvedAgentMissions = [];

    // 1. stats
    if (local.stats) {
        for (const [key, localStat] of Object.entries(local.stats)) {
            const remoteStat = merged.stats[key];
            if (!remoteStat) {
                merged.stats[key] = localStat;
                changed = true;
            } else {
                const localSeen = localStat.totalQuestionsSeen || 0;
                const localWrong = localStat.wrongAnswers || 0;
                const remoteSeen = remoteStat.totalQuestionsSeen || 0;
                const remoteWrong = remoteStat.wrongAnswers || 0;

                if (localSeen > 0) {
                    remoteStat.totalQuestionsSeen = remoteSeen + localSeen;
                    remoteStat.wrongAnswers = remoteWrong + localWrong;
                    remoteStat.lastStudied = Math.max(remoteStat.lastStudied || 0, localStat.lastStudied || 0);
                    changed = true;
                }
                
                if (localStat.subTopics) {
                    if (!remoteStat.subTopics) remoteStat.subTopics = {};
                    for (const [subKey, localSub] of Object.entries(localStat.subTopics)) {
                        const remoteSub = remoteStat.subTopics[subKey] as any;
                        if (!remoteSub) {
                            remoteStat.subTopics[subKey] = localSub;
                            changed = true;
                        } else {
                            const localSubSeen = (localSub as any).seen || 0;
                            const localSubWrong = (localSub as any).wrong || 0;
                            if (localSubSeen > 0) {
                                remoteSub.seen = (remoteSub.seen || 0) + localSubSeen;
                                remoteSub.wrong = (remoteSub.wrong || 0) + localSubWrong;
                                changed = true;
                            }
                        }
                    }
                }
            }
        }
    }

    // 2. wrongQuestions
    if (local.wrongQuestions) {
        for (const [nodeId, localQList] of Object.entries(local.wrongQuestions)) {
            if (!merged.wrongQuestions[nodeId]) {
                merged.wrongQuestions[nodeId] = localQList;
                changed = true;
            } else {
                const remoteQList = merged.wrongQuestions[nodeId];
                localQList.forEach(localQ => {
                    const exists = remoteQList.some(remoteQ => remoteQ.question === localQ.question);
                    if (!exists) {
                        remoteQList.push(localQ);
                        changed = true;
                    }
                });
            }
        }
    }

    // 3. conqueredRegions
    if (local.conqueredRegions) {
        for (const [subject, localRegions] of Object.entries(local.conqueredRegions)) {
            const subjKey = subject as SubjectType;
            if (!merged.conqueredRegions[subjKey]) {
                merged.conqueredRegions[subjKey] = localRegions;
                changed = true;
            } else {
                const remoteRegions = merged.conqueredRegions[subjKey] || [];
                localRegions.forEach(reg => {
                    if (!remoteRegions.includes(reg)) {
                        remoteRegions.push(reg);
                        changed = true;
                    }
                });
                merged.conqueredRegions[subjKey] = remoteRegions;
            }
        }
    }

    // 4. solvedAgentMissions
    if (local.solvedAgentMissions && local.solvedAgentMissions.length > 0) {
        const mergedMissions = merged.solvedAgentMissions || [];
        local.solvedAgentMissions.forEach(mission => {
            if (!mergedMissions.includes(mission)) {
                mergedMissions.push(mission);
                changed = true;
            }
        });
        merged.solvedAgentMissions = mergedMissions;
    }

    // 5. highScores
    if (local.highScores) {
        for (const [gameKey, localScore] of Object.entries(local.highScores)) {
            const remoteScore = merged.highScores[gameKey] || 0;
            if (localScore > remoteScore) {
                merged.highScores[gameKey] = localScore;
                changed = true;
            }
        }
    }

    // 6. chainData
    if (local.chainData && local.chainData.wateredHistory) {
        if (!merged.chainData) {
            merged.chainData = local.chainData;
            changed = true;
        } else {
            const localHist = local.chainData.wateredHistory || [];
            const remoteHist = merged.chainData.wateredHistory || [];
            const newHist: number[] = [];
            const maxLen = Math.max(localHist.length, remoteHist.length);
            for (let i = 0; i < maxLen; i++) {
                const lVal = localHist[i] || 0;
                const rVal = remoteHist[i] || 0;
                newHist.push(Math.max(lVal, rVal));
            }
            const histChanged = JSON.stringify(newHist) !== JSON.stringify(remoteHist);
            if (histChanged) {
                merged.chainData.wateredHistory = newHist;
                changed = true;
            }
            const localLast = local.chainData.lastWatered || '';
            const remoteLast = merged.chainData.lastWatered || '';
            if (localLast > remoteLast) {
                merged.chainData.lastWatered = localLast;
                changed = true;
            }
        }
    }

    // 7. totalTestsTaken
    if (local.totalTestsTaken && local.totalTestsTaken > (merged.totalTestsTaken || 0)) {
        merged.totalTestsTaken = (merged.totalTestsTaken || 0) + local.totalTestsTaken;
        changed = true;
    }

    // 8. recentOpponents
    if (local.recentOpponents && local.recentOpponents.length > 0) {
        const remoteOpponents = merged.recentOpponents || [];
        const combined = [...remoteOpponents];
        local.recentOpponents.forEach(localOpp => {
            const exists = combined.some(r => r.uid === localOpp.uid);
            if (!exists) {
                combined.push(localOpp);
            }
        });
        // Sort by last dueled descending
        combined.sort((a, b) => (b.lastDueled || 0) - (a.lastDueled || 0));
        merged.recentOpponents = combined.slice(0, 100);
        changed = true;
    }

    return { merged, changed };
};

/**
 * 🛠️ Kendi Kendini İyileştirme (Self-Healing Recovery)
 * Eğer kullanıcının totalTestsTaken değeri 0 ise veya stats/wrongQuestions haritaları boş görünüyorsa,
 * alt koleksiyondaki (subcollection) çözülmüş deneme sınavlarını tarayarak
 * kullanıcının istatistiklerini (stats), gelişim karnesini ve hatalı sorularını (wrongQuestions) baştan kurar.
 */
export const recoverStatsFromSubcollection = async (uid: string) => {
    if (!isFirebaseActive) return;
    try {
        console.log(`%c[RECOVERY] 🔍 Kullanıcı (${uid}) için kendi kendini iyileştirme (self-healing) başlatılıyor...`, 'color:#d97706;font-weight:bold');
        
        // 1. Alt koleksiyondan tüm sınavları çek
        const testsRef = collection(db, "users", uid, "tests");
        const qSnapshot = await getDocs(testsRef);
        
        if (qSnapshot.empty) {
            console.log(`%c[RECOVERY] ℹ️ Alt koleksiyonda deneme sınavı bulunamadı. Kurtarılacak veri yok.`, 'color:#3b82f6');
            return;
        }

        const tests: TestHistoryItem[] = [];
        qSnapshot.forEach(docSnap => {
            const data = docSnap.data();
            tests.push(data as TestHistoryItem);
        });

        // Eskiden yeniye doğru sırala (kronolojik analiz için)
        tests.sort((a, b) => a.date - b.date);
        console.log(`%c[RECOVERY] 📦 ${tests.length} adet sınav tespit edildi. İstatistikler ve hatalı sorular yeniden yapılandırılıyor...`, 'color:#3b82f6');

        // 2. Profil şablonunu oluştur
        const recoveredProfile = JSON.parse(JSON.stringify(defaultProfile)) as UserProfile;
        recoveredProfile.uid = uid;
        recoveredProfile.name = currentUserProfile.name || 'Öğrenci';
        recoveredProfile.stats = {};
        recoveredProfile.wrongQuestions = {};
        recoveredProfile.totalTestsTaken = 0;

        // 3. Her sınavı sırayla analiz et
        for (const test of tests) {
            recoveredProfile.totalTestsTaken += 1;
            const questions = test.questions || [];
            const answers = test.userAnswers || {};
            const subject = test.subject;

            for (let index = 0; index < questions.length; index++) {
                const q = questions[index];
                const selected = answers[index] !== undefined ? answers[index] : (answers as any)[index.toString()];
                const isCorrect = selected !== undefined && selected !== null && Number(selected) === Number(q.correctAnswerIndex);
                const rawTopic = effectiveQuestionTopic(q); // TÜİK → metinden ünite
                const qSubject = resolveQuestionSubject(q, subject);
                const mainTopic = normalizeTopicName(rawTopic, qSubject);
                const subTopic = filterSubTopic((rawTopic !== mainTopic && rawTopic !== 'Genel') ? rawTopic : null, qSubject, mainTopic);
                
                const statsKey = `${qSubject}_${mainTopic.replace(/\s+/g, '_')}`;

                if (!recoveredProfile.stats[statsKey]) {
                    recoveredProfile.stats[statsKey] = {
                        topicName: mainTopic,
                        lesson: qSubject, 
                        totalQuestionsSeen: 0,
                        wrongAnswers: 0,
                        consecutiveRetryCount: 0,
                        lastStudied: test.date || Date.now(),
                        subTopics: {}
                    };
                }

                const stat = recoveredProfile.stats[statsKey];
                stat.totalQuestionsSeen++;
                stat.lastStudied = test.date || Date.now();
                
                if (subTopic) {
                    if (!stat.subTopics) stat.subTopics = {};
                    const subKey = subTopic.replace(/\s+/g, '_');
                    if (!stat.subTopics[subKey]) {
                        stat.subTopics[subKey] = { name: subTopic, seen: 0, wrong: 0 };
                    }
                    stat.subTopics[subKey].seen++;
                    if (!isCorrect) stat.subTopics[subKey].wrong++;
                }

                if (!isCorrect) {
                    stat.wrongAnswers++;

                    const targetNodeId = getTargetNodeId(mainTopic, qSubject);

                    const questionWithId: GeminiTestQuestion = { 
                        ...q, 
                        id: q.id || generateQuestionId(q.question, q.topic),
                        isResolved: false,
                        addedDate: test.date || Date.now(),
                        subject: qSubject,
                        userAnswerIndex: Number.isInteger(Number(selected)) && selected !== null && selected !== undefined ? Number(selected) : undefined
                    } as GeminiTestQuestion;

                    // Preserve resolved state if it was already marked as resolved by the user
                    if (targetNodeId && currentUserProfile.wrongQuestions?.[targetNodeId]) {
                        const matchedExistingQ = currentUserProfile.wrongQuestions[targetNodeId].find(eq => eq.question === q.question);
                        if (matchedExistingQ) {
                            questionWithId.isResolved = matchedExistingQ.isResolved;
                            if (matchedExistingQ.solvedDate) {
                                questionWithId.solvedDate = matchedExistingQ.solvedDate;
                            }
                        }
                    }

                    if (targetNodeId) {
                        if (!recoveredProfile.wrongQuestions[targetNodeId]) {
                            recoveredProfile.wrongQuestions[targetNodeId] = [];
                        }
                        const exists = recoveredProfile.wrongQuestions[targetNodeId].some(existing => existing.question === q.question);
                        if (!exists) {
                            recoveredProfile.wrongQuestions[targetNodeId].push(questionWithId);
                        }
                    }
                }
            }
        }

        // Ana profildeki diğer alanları koru (örn. highScores, conqueredRegions, kapiExamResults)
        // 🛡️ KRİTİK DÜZELTME: wrongQuestions TEK YETKİLİ alt koleksiyondan (wrong_questions) yönetilir.
        // Kullanıcının daha önce sildiği eski test hatalarını geçmiş test kayıtlarından ZORLA DİRİLTME!
        const mergedProfile = {
            ...currentUserProfile,
            stats: recoveredProfile.stats,
            wrongQuestions: currentUserProfile.wrongQuestions || {},
            totalTestsTaken: Math.max(currentUserProfile.totalTestsTaken || 0, recoveredProfile.totalTestsTaken),
            migrationVersion: 3
        };

        // 4. Veritabanına kaydet
        currentUserProfile = mergedProfile;
        await saveUserProfile(mergedProfile);
        notifyProfileSubscribers();
        console.log(`%c[RECOVERY] 🎉 Kendi kendini iyileştirme tamamlandı! Gelişim karnesi istatistikleri güncellendi.`, 'color:#22c55e;font-weight:bold');
    } catch (e) {
        console.error('[RECOVERY] ❌ Kendi kendini iyileştirme başarısız:', e);
    }
};

/**
 * Her gün İstanbul saati ile ilk girişte profilin kritik verilerini
 * (istatistik, kredi, öğrenme stili vb.) alt koleksiyona YEDEK (append-only) olarak yazar.
 * 1MB ana doküman sınırına takılmaz. Mevcut sisteme müdahale etmez.
 */
export const backupProfileToday = async (uid: string, profile: UserProfile) => {
    if (!uid || !isFirebaseActive) return;
    try {
        const todayStr = getTurkeyDateString();
        const localBackupKey = `last_backup_date_${uid}`;
        
        // Cihaz önbelleğinden kontrol (Fazladan Firestore okumasını engeller)
        if (localStorage.getItem(localBackupKey) === todayStr) {
            return; 
        }

        const backupRef = doc(db, "users", uid, "daily_backups", todayStr);
        const snap = await getDoc(backupRef);
        
        if (snap.exists()) {
            // Başka cihazda bugün yedeklenmiş olabilir, cihazın önbelleğini güncelle
            localStorage.setItem(localBackupKey, todayStr);
            return;
        }

        // Güvenli Snapshot (Sadece kritik ve küçük boyutlu, geçmiş içermeyen veriler)
        const backupData: Partial<UserProfile> & { timestamp: number } = {
            stats: profile.stats || {},
            learningStyle: profile.learningStyle || { visual: 0, auditory: 0, analytical: 0 },
            aiUsage: profile.aiUsage || {},
            conqueredRegions: profile.conqueredRegions || {},
            
            // Abonelik ve Paket Bilgileri
            isPremium: profile.isPremium || false,
            premiumStartedAt: profile.premiumStartedAt,
            premiumUntil: profile.premiumUntil,
            activePackageId: profile.activePackageId,
            
            // Haklar ve Kotalar
            freeExamsUsed: profile.freeExamsUsed || 0,
            examUsedCount: profile.examUsedCount || 0,
            examQuotaMonth: profile.examQuotaMonth,
            
            voiceSecondsUsed: profile.voiceSecondsUsed || 0,
            voiceQuotaMonth: profile.voiceQuotaMonth,
            
            podcastUsedCount: profile.podcastUsedCount || 0,
            podcastQuotaMonth: profile.podcastQuotaMonth,
            
            freeGamesUsed: profile.freeGamesUsed || 0,
            
            freeKapiChatUsed: profile.freeKapiChatUsed || 0,
            kapiChatUsedCount: profile.kapiChatUsedCount || 0,
            kapiChatQuotaMonth: profile.kapiChatQuotaMonth,
            
            freeHistoryChatUsed: profile.freeHistoryChatUsed || 0,
            historyChatUsedCount: profile.historyChatUsedCount || 0,
            historyChatQuotaMonth: profile.historyChatQuotaMonth,
            
            timestamp: Date.now()
        };

        await setDoc(backupRef, sanitizeData(backupData));
        localStorage.setItem(localBackupKey, todayStr);
        console.log(`%c[Backup] 🛡️ Profil kritik verileri '${todayStr}' için güvenle yedeklendi (1MB limiti harici).`, 'color:#10b981;font-weight:bold');
    } catch (e) {
        console.warn("[Backup] Yedekleme sırasında hata oluştu:", e);
    }
};

export const initUserListener = (user: any) => {
    if (!user) {
        currentUserProfile = { ...defaultProfile };
        isProfileLoadedFromFirestore = false;
        isChainLoadedFromFirestore = false;
        isWrongsLoadedFromFirestore = false;
        isStatsLoadedFromFirestore = false;
        statsDocExists = false;
        profileBaseline = null;
        statsBaseline = null;
        serverWrongDocs.clear();
        return () => {};
    }
    profileBaseline = null;
    statsBaseline = null;
    // Hesap değişiminde önceki hesabın bulut durumu yeni hesaba yazılmasın
    isProfileLoadedFromFirestore = false;
    isChainLoadedFromFirestore = false;
    isWrongsLoadedFromFirestore = false;
    isStatsLoadedFromFirestore = false;
    statsDocExists = false;
    serverWrongDocs.clear();

    // Önbellekten hemen yükle (Race condition ve boş profil yazma sorunlarını önler)
    const localKey = `user_profile_${user.uid}`;
    const localData = loadFromLocal(localKey);
    if (localData) {
        currentUserProfile = localData;
        // Fotoğraf analizleri yalnızca buluttan (analysis_logs dinleyicisi) gelir; yerel kopya kullanılmaz.
        currentUserProfile.analysisHistory = {};
        if (localData.chainData) {
            currentChainData = {
                wateredHistory: localData.chainData.wateredHistory || [],
                lastWatered: localData.chainData.lastWatered || ''
            };
        }
    } else {
        currentUserProfile = { ...defaultProfile, uid: user.uid, name: user.displayName || 'Öğrenci', analysisHistory: {} };
        currentChainData = { wateredHistory: [], lastWatered: '' };
    }

    let hasCheckedAutoSync = false;

    // 🛠️ Otomatik kurtarma kontrolü: profil VE istatistikler buluttan geldikten sonra bir kez.
    // (İstatistik dokümanı gelmeden çalışırsa "istatistik yok" sanıp gerçek istatistikleri testlerden yeniden yazıyordu.)
    const maybeRunAutoSync = () => {
        if (hasCheckedAutoSync || !isProfileLoadedFromFirestore || !isStatsLoadedFromFirestore) return;
        hasCheckedAutoSync = true;
        const testsRef = collection(db, "users", user.uid, "tests");
        // Yalnızca sayı lazım: bütün sınavları (tanesi ~100 KB) indirmek yerine sunucuda say
        getCountFromServer(testsRef).then((countSnap) => {
            const dbTestsCount = countSnap.data().count;
            const hasNoStats = !currentUserProfile.stats || Object.keys(currentUserProfile.stats).length === 0;
            const totalTests = currentUserProfile.totalTestsTaken || 0;
            
            // Göç Kontrolü: Maden ve Enerji eşleşme düzeltmesi sürümü 3'tür.
            const needsMigration = !currentUserProfile.migrationVersion || currentUserProfile.migrationVersion < 3;

            if (dbTestsCount > totalTests || (dbTestsCount > 0 && (hasNoStats || (needsMigration && dbTestsCount >= totalTests)))) {
                console.log(`%c[AUTO-RECOVERY] 🛠️ Buluttaki test sayısı (${dbTestsCount}) profil test sayısından (${totalTests}) fazla, veriler eksik veya zihin haritası göçü gerekiyor (Sürüm: ${currentUserProfile.migrationVersion || 0} → 3). Kurtarma başlatılıyor...`, 'color:#d97706;font-weight:bold');
                recoverStatsFromSubcollection(user.uid).catch(err => console.error("Self-healing error:", err));
            }
        }).catch(err => console.warn("Auto-healing check error:", err));
    };

    if (isFirebaseActive) {
        const userRef = doc(db, "users", user.uid);
        
        // includeMetadataChanges: önbellekten gelen görüntüyle aynı veri sunucudan onaylanınca da tetiklensin
        // (yoksa "buluttan yüklendi" bayrağı hiç kalkmaz ve kayıtlar engellenir).
        const unsubProfile = onSnapshot(userRef, { includeMetadataChanges: true }, (docSnap) => {
            const fromCache = docSnap.metadata.fromCache;
            // Önbellekte "yok" demek bulutta yok demek değildir (yeni cihaz / çevrimdışı): yeni profil OLUŞTURMA,
            // yoksa sunucuya ulaşınca gerçek profilin üzerine boş profil yazılırdı. Sunucu cevabını bekle.
            if (!docSnap.exists() && fromCache) return;
            if (docSnap.exists()) {
                const profileData = docSnap.data() as UserProfile;
                if (!profileData.recentOpponents) profileData.recentOpponents = [];

                // --- AUTO EXPIRE CHECK ---
                if (profileData.isPremium && profileData.premiumUntil) {
                    const untilMs = typeof profileData.premiumUntil === 'number'
                        ? profileData.premiumUntil
                        : ((profileData.premiumUntil as any)?.seconds ? (profileData.premiumUntil as any).seconds * 1000 : null);

                    if (untilMs && Date.now() > untilMs) {
                        // Yalnızca yerelde Free'ye düşür; premium alanlarını sunucu yönetir (firestore.rules)
                        console.log("⏰ [PRO Expired] Premium süresi doldu! Yerelde Free pakete düşürülüyor...");
                        profileData.isPremium = false;
                        profileData.activePackageId = undefined;
                    }
                }
                
                // ⚠️ KRİTİK: Bellekteki chainData ve wrongQuestions'ı MUTLAKA koru
                // Bunlar izole dinleyicilerden (unsubChain, unsubWrongQuestions) gelir.
                // Ana dokümanda bu alanlar artık YOKTUR (saveUserProfile siler).
                const existingChainData = currentChainData;
                const existingWrongs = currentUserProfile.wrongQuestions || {};

                const existingAnalyses = currentUserProfile.analysisHistory || {};
                // Test geçmişinin tek kaynağı `tests` alt koleksiyonu; bellekte yalnızca bu oturumun testleri durur
                const existingTests = currentUserProfile.testHistory || [];

                // 🚛 Eski alan taşımaları YALNIZCA sunucudan gelen güncel görüntüyle (önbellekteki eski kopya,
                // zaten taşınıp silinmiş alanları yeniden taşıyıp silinen kayıtları diriltebilirdi).
                if (!fromCache) {
                    // Eski zincir ana dokümandaysa: izole zincir dokümanı YOKSA oluştur (varsa asla ezme)
                    const legacyChain = profileData.chainData;
                    if (legacyChain?.wateredHistory?.length) {
                        const chainRef = doc(db, "users", user.uid, "user_data", "chain");
                        getDoc(chainRef).then(s => {
                            if (!s.exists()) return setDoc(chainRef, sanitizeData(legacyChain));
                        }).catch(err => console.error("Chain migration error:", err));
                    }
                    // Eski hata listesi (yalnızca yedek olarak saklanır; tek kaynak wrong_questions)
                    if (profileData.wrongQuestions && Object.keys(profileData.wrongQuestions).length > 0) {
                        const wrongsRef = doc(db, "users", user.uid, "user_data", "wrongQuestions");
                        setDoc(wrongsRef, sanitizeData(profileData.wrongQuestions), { merge: true }).catch(err => console.error("Wrongs migration error:", err));
                    }
                    // 🧹 Ana dokümandan eski chainData ve wrongQuestions'ı sil
                    if (profileData.chainData !== undefined || profileData.wrongQuestions !== undefined) {
                        const deletes: any = {};
                        if (profileData.chainData !== undefined) deletes.chainData = deleteField();
                        if (profileData.wrongQuestions !== undefined) deletes.wrongQuestions = deleteField();
                        updateDoc(userRef, deletes).catch(err => console.error("Temizlik hatası:", err));
                    }
                    const legacyAnalyses = profileData.analysisHistory;
                    if (legacyAnalyses && Object.keys(legacyAnalyses).length > 0) {
                        migrateLegacyAnalysisHistory(user.uid, legacyAnalyses);
                    }
                    if (profileData.testHistory !== undefined) {
                        migrateLegacyTestHistory(user.uid, Array.isArray(profileData.testHistory) ? profileData.testHistory : []);
                    }
                }
                // Sohbet oturumları chat_sessions'tan açılınca yüklenir; profil güncellemesi bellekteki sohbeti silmesin
                const existingChatSessions = currentUserProfile.chatSessions || {};
                const existingChatHistory = currentUserProfile.chatHistory || [];
                const legacyChatSessions = profileData.chatSessions;
                const legacyChatHistory = profileData.chatHistory;
                // İstatistikler user_data/stats dinleyicisinden gelir; o doküman yoksa (henüz taşınmamış) eski alan gösterilir
                const existingStats = currentUserProfile.stats || {};
                const legacyStats = profileData.stats;
                if (!fromCache && legacyStats !== undefined) migrateLegacyStats(user.uid, legacyStats || {});
                const { chainData: _ignoredChain, wrongQuestions: _ignoredWrongs, analysisHistory: _ignoredAnalyses, testHistory: _ignoredTests,
                    chatSessions: _ignoredSessions, chatHistory: _ignoredChatHistory, stats: _ignoredStats, ...safeProfileData } = profileData;
                // Delta yazımı için son görülen hal (önbellek ya da sunucu)
                profileBaseline = baselineOf(safeProfileData);
                currentUserProfile = {
                    ...defaultProfile,
                    ...safeProfileData
                };

                // İzole dinleyicilerden gelen verileri her zaman üstün tut
                currentUserProfile.chainData = existingChainData;
                currentUserProfile.wrongQuestions = existingWrongs;
                currentUserProfile.analysisHistory = existingAnalyses;
                currentUserProfile.testHistory = existingTests;
                currentUserProfile.chatSessions = existingChatSessions;
                currentUserProfile.chatHistory = existingChatHistory;
                currentUserProfile.stats = statsDocExists ? existingStats : (legacyStats || existingStats);

                // Kayıt kilidi yalnızca sunucu onaylı veriyle açılır (önbellekteki eski profil bulutu ezmesin)
                if (!fromCache) isProfileLoadedFromFirestore = true;
                
                // Günlük kritik yedekleme mekanizmasını tetikle (Alt koleksiyon - append only)
                if (!fromCache) backupProfileToday(user.uid, currentUserProfile).catch(console.warn);
                
                // Önbelleği güncelle (Yerel verinin güncel kalmasını sağlar)
                const localKey = `user_profile_${user.uid}`;
                saveToLocal(localKey, currentUserProfile);

                console.log(`%c[onSnapshot] 📥 Firestore'dan profil yüklendi ve yerel önbellek güncellendi`, 'color:#3b82f6;font-weight:bold', {
                    name: currentUserProfile.name,
                    totalTests: currentUserProfile.totalTestsTaken,
                    statsKeys: Object.keys(currentUserProfile.stats || {}).length,
                    testHistoryLen: currentUserProfile.testHistory?.length || 0,
                    wrongQKeys: Object.keys(currentUserProfile.wrongQuestions || {}).length,
                });

                // --- MIGRATION: Merge Local Guest Data into Cloud Profile ---
                try {
                    const guestProfileKey = 'user_profile_guest';
                    const guestProfileRaw = localStorage.getItem(guestProfileKey);
                    if (guestProfileRaw && !fromCache) {
                        const guestProfile = JSON.parse(guestProfileRaw) as UserProfile;
                        const hasGuestData = (guestProfile.totalTestsTaken && guestProfile.totalTestsTaken > 0) ||
                            (guestProfile.stats && Object.keys(guestProfile.stats).length > 0) ||
                            (guestProfile.wrongQuestions && Object.keys(guestProfile.wrongQuestions).length > 0) ||
                            (guestProfile.chainData && guestProfile.chainData.wateredHistory && guestProfile.chainData.wateredHistory.length > 0);

                        if (hasGuestData) {
                            const isBrandNewUser = (!currentUserProfile.totalTestsTaken || currentUserProfile.totalTestsTaken === 0) &&
                                (!currentUserProfile.stats || Object.keys(currentUserProfile.stats).length === 0);

                            if (!isBrandNewUser) {
                                console.log(`%c[MİGRASYON] ℹ️ Kullanıcının zaten bulutta aktif verisi var (${currentUserProfile.totalTestsTaken} test). Bilgisayardaki eski Guest verisi hesaba karıştırılmadan arşivlendi.`, 'color:#a855f7;font-weight:bold');
                                localStorage.setItem('user_profile_guest_backup', guestProfileRaw);
                                localStorage.removeItem(guestProfileKey);
                            } else {
                                console.log(`%c[MİGRASYON] 🚛 Yeni hesap için yerel Guest profili tespit edildi, Firestore profili ile birleştiriliyor...`, 'color:#3b82f6;font-weight:bold');
                                const { merged, changed } = mergeProfiles(guestProfile, currentUserProfile);
                                currentUserProfile = merged;
                                if (merged.chainData) {
                                    currentChainData = merged.chainData;
                                }

                            // 1. Guest test history upload
                            if (guestProfile.testHistory && guestProfile.testHistory.length > 0) {
                                for (const item of guestProfile.testHistory) {
                                    if (item.id) {
                                        const testRef = doc(db, "users", user.uid, "tests", item.id);
                                        setDoc(testRef, sanitizeData(item), { merge: true }).catch(err => {
                                            console.error("[MİGRASYON] Guest test yükleme hatası:", err);
                                        });
                                    }
                                }
                            }

                            // 2. Guest chat sessions upload
                            if (guestProfile.chatSessions && Object.keys(guestProfile.chatSessions).length > 0) {
                                for (const [key, messages] of Object.entries(guestProfile.chatSessions)) {
                                    if (messages && messages.length > 0) {
                                        const sessionRef = doc(db, "users", user.uid, "chat_sessions", key);
                                        setDoc(sessionRef, { messages: sanitizeData(messages) }, { merge: true }).catch(err => {
                                            console.error("[MİGRASYON] Guest sohbet yükleme hatası:", err);
                                        });
                                    }
                                }
                            }

                            // 3. Guest chatHistory (Cezeri legacy) upload
                            if (guestProfile.chatHistory && guestProfile.chatHistory.length > 0) {
                                const sessionRef = doc(db, "users", user.uid, "chat_sessions", "cezeri_global");
                                setDoc(sessionRef, { messages: sanitizeData(guestProfile.chatHistory) }, { merge: true }).catch(err => {
                                    console.error("[MİGRASYON] Guest legacy sohbet yükleme hatası:", err);
                                });
                            }

                            // Save merged profile to Firestore
                            saveUserProfile(currentUserProfile).then(async () => {
                                // Zincir verisini buluta elle kaydet (genel profilden çıkardığımız için)
                                if (currentUserProfile.chainData && isFirebaseActive) {
                                    try {
                                        const chainRef = doc(db, "users", user.uid, "user_data", "chain");
                                        await setDoc(chainRef, sanitizeData(currentUserProfile.chainData), { merge: true });
                                        console.log('%c[MİGRASYON] 🌿 Birleştirilmiş zincir verisi izole Firestore dokümanına yazıldı.', 'color:#22c55e');
                                    } catch (e) {
                                        console.error('[MİGRASYON] 🌿 Zincir verisi buluta yazılırken hata:', e);
                                    }
                                }
                                // Back up and remove guest profile from localStorage
                                localStorage.setItem('user_profile_guest_backup', guestProfileRaw);
                                localStorage.removeItem(guestProfileKey);
                                console.log(`%c[MİGRASYON] 🎉 Yerel Guest profili başarıyla birleştirildi ve yedeklendi!`, 'color:#22c55e;font-weight:bold');
                            }).catch(err => {
                                console.error("[MİGRASYON] Birleştirilmiş profil bulut kayıt hatası:", err);
                            });
                        }
                    }
                }
                } catch (e) {
                    console.error("[MİGRASYON] Yerel veri birleştirme hatası:", e);
                }

                // Google displayName'i her zaman öncelikli olarak kullan
                if (user.displayName && (!currentUserProfile.name || currentUserProfile.name === 'Öğrenci')) {
                    currentUserProfile.name = user.displayName;
                    // Firestore'a da güncelle (kalıcı olsun)
                    updateDoc(userRef, { name: user.displayName }).catch(err => console.warn("İsim güncelleme hatası:", err));
                }
                if (user.email && (!currentUserProfile.email || currentUserProfile.email !== user.email)) {
                    currentUserProfile.email = user.email;
                    updateDoc(userRef, { email: user.email }).catch(err => console.warn("Email güncelleme hatası:", err));
                }
                if (!currentUserProfile.wrongQuestions) currentUserProfile.wrongQuestions = {};
                if (!currentUserProfile.analysisHistory) currentUserProfile.analysisHistory = {};
                if (!currentUserProfile.testHistory) currentUserProfile.testHistory = [];
                if (!currentUserProfile.chatHistory) currentUserProfile.chatHistory = [];
                if (!currentUserProfile.chatSessions) currentUserProfile.chatSessions = {};
                if (!currentUserProfile.dislikedQuestions) currentUserProfile.dislikedQuestions = [];
                if (!currentUserProfile.flashHistory) currentUserProfile.flashHistory = []; // Initialize if missing
                if (!currentUserProfile.conqueredRegions) currentUserProfile.conqueredRegions = {};
                if (!currentUserProfile.kapiExamResults) currentUserProfile.kapiExamResults = {};

                // 🛠️ Otomatik Kendi Kendini İyileştirme (Self-Healing Recovery)
                // Oturum başına sadece bir kez çalışacak şekilde subcollection test sayısını denetle
                maybeRunAutoSync();

                // 🚛 Göç işlemi tetiklenmesi gereken eski tip veri var mı?
                if (!fromCache && ((legacyChatSessions && Object.keys(legacyChatSessions).length > 0) ||
                    (Array.isArray(legacyChatHistory) && legacyChatHistory.length > 0))) {
                    migrateLegacyUserData(user.uid, { ...currentUserProfile, chatSessions: legacyChatSessions || {}, chatHistory: legacyChatHistory || [] })
                        .catch(err => console.error("Göç hatası:", err));
                }

                // 📢 React bileşenlerini bilgilendir (profil güncellendi)
                notifyProfileSubscribers();

                // 🌿 Otomatik sulama kontrolü artık zincir verisi Firestore'dan yüklendikten sonra izole dinleyicide tetiklenmektedir.
            } else {
                console.warn(`%c[onSnapshot] ⚠️ Firestore'da profil BULUNAMADI — yeni profil oluşturuluyor!`, 'color:#ef4444;font-weight:bold');
                const newProfile = { 
                    ...defaultProfile, 
                    uid: user.uid, 
                    name: user.displayName || 'Öğrenci' 
                };
                setDoc(userRef, sanitizeData(newProfile)).catch(err => console.warn("Profil oluşturulamadı:", err));
                currentUserProfile = newProfile;
                profileBaseline = baselineOf(newProfile);
                isProfileLoadedFromFirestore = true;
            }
        });

        // FAVORİLERİ DİNLE VE SENKRONİZE ET
        let legacyFavsChecked = false;
        // includeMetadataChanges: sunucu onayı (fromCache=false) önbellekle aynı veride de gelsin (eski favori taşıması buna bağlı)
        const unsubFavorites = onSnapshot(collection(db, "users", user.uid, FAVORITES), { includeMetadataChanges: true }, (colSnap) => {
            currentFavorites = colSnap.docs
                .map(d => d.data() as FavoriteQuestion)
                .sort((a, b) => (a.dateAdded || 0) - (b.dateAdded || 0));
            if (!colSnap.metadata.fromCache && !legacyFavsChecked) {
                legacyFavsChecked = true;
                migrateLegacyFavorites(user.uid, new Set(colSnap.docs.map(d => d.id)));
            }
        }, (err) => console.warn('[favorites] dinleyici hatası:', err));

        // 🌿 ZİNCİRİ KIRMA DİNLE VE SENKRONİZE ET (İzole Doküman)
        const chainRef = doc(db, "users", user.uid, "user_data", "chain");
        const unsubChain = onSnapshot(chainRef, { includeMetadataChanges: true }, (docSnap) => {
            const today = getTurkeyDateString();
            const fromCache = docSnap.metadata.fromCache;
            // Önbellekte yok ≠ bulutta yok: sunucu cevabını bekle (yoksa aşağıdaki "yoksa oluştur" dalı çalışırdı)
            if (!docSnap.exists() && fromCache) return;
            if (docSnap.exists()) {
                const data = docSnap.data();
                // Tek kaynak bulut: yerel kopya buluta geri yazılmaz (eskiden iki geçmiş birleştirilip geri yazılıyordu)
                currentChainData = {
                    wateredHistory: (data && data.wateredHistory) || [],
                    lastWatered: data.lastWatered || ''
                };
                currentUserProfile.chainData = currentChainData;
                if (!fromCache) isChainLoadedFromFirestore = true;

                // 🌿 Her gün 00:00'dan sonraki ilk girişte otomatik sula
                if (!fromCache && currentChainData.lastWatered !== today) {
                    waterChainToday().then(watered => {
                        if (watered) {
                            console.log(`%c[🌿 Zincir] Bugün ilk giriş — yaprak otomatik sulandı! (${today})`, 'color:#22c55e;font-weight:bold');
                            window.dispatchEvent(new CustomEvent('chain-watered-today'));
                        }
                    }).catch(e => console.warn('Zincir sulama hatası:', e));
                }
            } else {
                // İzole doküman henüz yok: Bellekteki veya ana profildeki veriyi izole dokümana yaz
                if (currentChainData.wateredHistory && currentChainData.wateredHistory.length > 0) {
                    setDoc(chainRef, sanitizeData(currentChainData), { merge: true }).catch(console.warn);
                    isChainLoadedFromFirestore = true;
                } else {
                    currentChainData = { wateredHistory: [], lastWatered: '' };
                    currentUserProfile.chainData = currentChainData;
                    isChainLoadedFromFirestore = true;
                }
                waterChainToday().catch(e => console.warn('Zincir otomatik başlatma hatası:', e));
            }
            // Önbelleği güncelle
            const localKey = `user_profile_${user.uid}`;
            saveToLocal(localKey, currentUserProfile);
            notifyProfileSubscribers();
        });

        // ❌ HATA SORULARINI ALT KOLEKSİYONDAN DİNLE VE SENKRONİZE ET
        let legacyWrongsChecked = false;
        let wrongsHealChecked = false;
        // includeMetadataChanges: önbellekteki veri buluttakiyle aynıyken de sunucu onayı (fromCache=false) gelsin; yoksa aşağıdaki
        // "sunucu onaylı ilk görüntüde bir kez" işleri (eski düğüm taşıma, yanlış düğüm onarımı) hiç çalışmıyordu (2026-10-15)
        const unsubWrongQuestions = onSnapshot(collection(db, "users", user.uid, WRONG_ITEMS), { includeMetadataChanges: true }, (colSnap) => {
            const grouped: Record<string, { q: GeminiTestQuestion; seq: number }[]> = {};
            serverWrongDocs.clear();
            colSnap.forEach((docSnap) => {
                const { nodeId, _seq, ...q } = docSnap.data() as any;
                if (!nodeId) return;
                const seq = Number(_seq) || 0;
                serverWrongDocs.set(docSnap.id, { nodeId, json: stableStringify(wrongItemForCloud(q)), seq });
                (grouped[nodeId] ||= []).push({ q, seq });
            });
            const newWrongQuestions: { [nodeId: string]: GeminiTestQuestion[] } = {};
            for (const [nodeId, items] of Object.entries(grouped)) {
                newWrongQuestions[nodeId] = items.sort((a, b) => a.seq - b.seq).map(x => x.q);
            }
            isWrongsLoadedFromFirestore = true;
            // Eski düğüm dokümanları (eski sürüm / henüz taşınmamış) — sunucu onaylı görüntüden sonra bir kez
            if (!colSnap.metadata.fromCache && !legacyWrongsChecked) {
                legacyWrongsChecked = true;
                migrateLegacyWrongQuestions(user.uid);
            }

            // 🛡️ Firestore wrong_questions alt koleksiyonu TEK YETKİLİ (Single Source of Truth) kaynaktır!
            // Bilgisayardan veya mobilden silinen soruların diğer cihazda geri gelmemesi için
            // yerel nesne doğrudan buluttaki güncel liste ile eşitlenir.
            currentUserProfile.wrongQuestions = newWrongQuestions;
            // Yanlış düğümdeki eski kayıtları (ör. TÜİK → Fiziki) bir kez taşı; yalnızca sunucu onaylı görüntüde
            if (!colSnap.metadata.fromCache && !wrongsHealChecked) {
                wrongsHealChecked = true;
                setTimeout(() => { try { healMisroutedWrongs(); } catch (e) { console.warn('[Auto-Heal] taşıma atlandı:', e); } }, 0);
            }
            
            const localKey = `user_profile_${user.uid}`;
            saveToLocal(localKey, currentUserProfile);
            saveToLocal('kpss_current_user_profile', currentUserProfile);
            notifyProfileSubscribers();
        });

        // 📊 KONU İSTATİSTİKLERİ (user_data/stats)
        const unsubStats = onSnapshot(statsDocRef(user.uid), { includeMetadataChanges: true }, (snap) => {
            const fromCache = snap.metadata.fromCache;
            if (!snap.exists() && fromCache) return; // önbellekte yok ≠ bulutta yok
            statsDocExists = snap.exists();
            if (snap.exists()) currentUserProfile.stats = (snap.data() || {}) as any;
            statsBaseline = baselineOf(snap.exists() ? snap.data() : {});
            if (!fromCache) isStatsLoadedFromFirestore = true;
            saveToLocal(`user_profile_${user.uid}`, currentUserProfile);
            notifyProfileSubscribers();
            maybeRunAutoSync();
        }, (err) => console.warn('[stats] dinleyici hatası:', err));

        // 📸 FOTOĞRAF ANALİZLERİNİ ALT KOLEKSİYONDAN DİNLE (tek kaynak; yerel veriyle birleştirilmez)
        const unsubAnalyses = onSnapshot(collection(db, "users", user.uid, ANALYSIS_LOGS), (colSnap) => {
            const grouped: Record<string, AnalysisLog[]> = {};
            colSnap.forEach((docSnap) => {
                const { nodeId, ...log } = docSnap.data() as AnalysisLog & { nodeId?: string };
                if (!nodeId) return;
                (grouped[nodeId] ||= []).push({ ...log, id: docSnap.id } as AnalysisLog);
            });
            for (const logs of Object.values(grouped)) logs.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
            currentUserProfile.analysisHistory = grouped;

            saveToLocal(`user_profile_${user.uid}`, currentUserProfile);
            notifyProfileSubscribers();
        }, (err) => console.warn('[analysis_logs] dinleyici hatası:', err));

        return () => {
            unsubProfile();
            unsubFavorites();
            unsubChain();
            unsubWrongQuestions();
            unsubAnalyses();
            unsubStats();
        };
    } else {
        // YEREL MOD (OFFLINE)
        const localKey = `user_profile_${user.uid}`;
        const localData = loadFromLocal(localKey);
        
        if (localData) {
            currentUserProfile = localData;
            if (!currentUserProfile.dislikedQuestions) currentUserProfile.dislikedQuestions = [];
            if (!currentUserProfile.flashHistory) currentUserProfile.flashHistory = [];
            if (!currentUserProfile.conqueredRegions) currentUserProfile.conqueredRegions = {};
            if (!currentUserProfile.recentOpponents) currentUserProfile.recentOpponents = [];
        } else {
            currentUserProfile = { ...defaultProfile, uid: user.uid, name: 'Öğrenci' };
            saveToLocal(localKey, currentUserProfile);
        }
        
        const localFavs = loadFromLocal(`user_favorites_${user.uid}`);
        currentFavorites = localFavs || [];
        
        return () => {};
    }
};

export const getUserProfile = (): UserProfile => {
    return currentUserProfile;
};
export const saveUserProfile = async (profile: UserProfile) => {
    // 🛡️ Zincir verisinin bellekte kaybolmasını önlemek için koruma
    if (!profile.chainData && currentUserProfile.chainData) {
        profile.chainData = currentUserProfile.chainData;
    }

    // ═══ DEBUG: Kayıt edilecek profili logla ═══
    console.log(`%c[saveUserProfile] 📝 KAYIT BAŞLIYOR`, 'color:#22c55e;font-weight:bold', {
        name: profile.name,
        uid: profile.uid,
        totalTests: profile.totalTestsTaken,
        statsKeys: Object.keys(profile.stats || {}).length,
        testHistoryLen: profile.testHistory?.length || 0,
        wrongQKeys: Object.keys(profile.wrongQuestions || {}).length,
        chatSessionKeys: Object.keys(profile.chatSessions || {}).length,
        firebaseActive: isFirebaseActive,
        hasUser: !!auth.currentUser,
    });

    if (isFirebaseActive && auth.currentUser) {
        // Yalnızca değişen alanlar yazıldığı için önbellekten (internetsiz) açılmış profil de kaydedebilir;
        // hiç anlık görüntü yoksa neyin değiştiği bilinemez, yazma.
        if (!profileBaseline) {
            console.warn('[userService] ⚠️ Profil henüz yüklenmedi (ne önbellekte ne bulutta). Bulut kaydı atlandı.');
            return;
        }
        if (profile.uid !== auth.currentUser.uid) {
            console.warn('[userService] ⚠️ Profil UID ve aktif kullanıcı UID eşleşmiyor! Kayıt iptal edildi.', { profileUid: profile.uid, authUid: auth.currentUser.uid });
            return;
        }
    }

    currentUserProfile = profile;
    if (isFirebaseActive && auth.currentUser) {
        const userRef = doc(db, "users", auth.currentUser.uid);
        try {
            let safeProfile = sanitizeData(profile);
            
            // 🌿 Zincir verisinin ezilmesini önlemek için chainData alanını genel profil kaydından çıkarıyoruz.
            // Zincir sulama işlemleri doğrudan 'waterChainToday' fonksiyonu üzerinden 'chainData' alanını günceller.
            delete safeProfile.chainData;

            // 🔒 Premium alanlarını yalnızca sunucu yazar (firestore.rules bunları istemciden reddeder).
            // Burada bırakılırsa (ör. yerelde süresi dolmuş isPremium:false) tüm profil kaydı reddedilir.
            for (const key of SERVER_ONLY_PROFILE_FIELDS) delete (safeProfile as any)[key];

            // ❌ Alt koleksiyonlara taşınmış alanları ana profil dokümanı kaydından çıkarıyoruz (Göç Döngüsünü Engeller).
            delete safeProfile.chatSessions;
            delete safeProfile.chatHistory;

            // ═══ BOYUT OPTİMİZASYONU (Kayıt SİLMEZ — sadece görselleri temizler) ═══
            // Base64 görselleri sil (Storage URL'ler kalır), açıklamaları kısalt
            // HİÇBİR kayıt (testHistory, flashHistory, chatHistory, stats, wrongQuestions) SİLİNMEZ

            // 1. testHistory ana dokümana yazılmaz: her sınav `tests/{id}` alt koleksiyonunda (tek sınav ~100 KB olabilir).
            delete safeProfile.testHistory;
            // Konu istatistikleri kendi dokümanında (user_data/stats)
            if (safeProfile.stats) {
                writeStatsDoc(auth.currentUser.uid, safeProfile.stats).catch(err => console.error('[saveUserProfile] İstatistik kayıt hatası:', err));
            }
            delete safeProfile.stats;
            delete safeProfile.flashHistory; // flash_sessions alt koleksiyonunda

            // 2. analysisHistory ana dokümana yazılmaz: her analiz analysis_logs alt koleksiyonunda ayrı dokümandır.
            delete safeProfile.analysisHistory;

            // 3. wrongQuestions → wrong_items'a yalnızca yeni/değişen sorular (bulut yüklendikten sonra; silme yok)
            if (safeProfile.wrongQuestions) {
                syncWrongNodes(auth.currentUser.uid, safeProfile.wrongQuestions, Object.keys(safeProfile.wrongQuestions)).catch(err => {
                    console.error('[userService] Hatalı soru yazma hatası:', err);
                });
            }
            // Ana dokümanın şişmesini önlemek için wrongQuestions alanı ana dokümana hiç yazılmaz
            delete safeProfile.wrongQuestions;

            // Erken uyarı: console.error → Sentry e-postası (1 MB'a dayanınca yazma reddediliyor, veri kayboluyor)
            const estimatedSize = new Blob([JSON.stringify(safeProfile)]).size;
            if (estimatedSize > 600_000 && !profileSizeAlerted) {
                profileSizeAlerted = true;
                console.error(`[userService] Profil dokümanı büyüyor: ~${(estimatedSize / 1024).toFixed(0)} KB (sınır 1024 KB). En büyük alanlar:`,
                    Object.entries(safeProfile).map(([k, v]) => [k, JSON.stringify(v ?? null).length]).sort((a: any, b: any) => b[1] - a[1]).slice(0, 5));
            }

            // Yalnızca son görülen halden farklı alanlar (dokunulmayan alanlar buluttaki yeni değeri ezmesin)
            const changed: Record<string, any> = {};
            for (const [key, value] of Object.entries(safeProfile)) {
                if (profileBaseline?.get(key) !== stableStringify(value ?? null)) changed[key] = value;
            }
            if (Object.keys(changed).length > 0) {
                // Beklenmez: yazma kalıcı önbellekte kuyruğa girer; internetsizken await sunucu onayına kadar asılı kalırdı.
                updateDoc(userRef, changed).then(() => {
                    console.log(`%c[saveUserProfile] ✅ Firestore'a yazıldı (${Object.keys(changed).join(', ')})`, 'color:#22c55e');
                }).catch(e => console.error("%c[saveUserProfile] ❌ Bulut Kayıt Hatası:", 'color:#ef4444;font-weight:bold', e));
            }
        } catch (e) {
            console.error("%c[saveUserProfile] ❌ Bulut Kayıt Hatası:", 'color:#ef4444;font-weight:bold', e);
        }
    } else {
        console.warn(`%c[saveUserProfile] ⚠️ Firestore YAZILMADI — isFirebaseActive:${isFirebaseActive}, hasUser:${!!auth.currentUser}`, 'color:#f59e0b;font-weight:bold');
    }
    const uid = auth.currentUser ? auth.currentUser.uid : (auth as any).currentUser?.uid;
    if(uid) saveToLocal(`user_profile_${uid}`, profile);
};

export const addRecentOpponent = async (opponentId: string, opponentName: string) => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const userRef = doc(db, "users", uid);
    
    const profile = { ...currentUserProfile };
    const currentOpponents = profile.recentOpponents || [];
    
    const exists = currentOpponents.find((o: any) => o.uid === opponentId);
    let nextOpponents: any[] = [];
    if (exists) {
        nextOpponents = currentOpponents.map((o: any) => o.uid === opponentId ? { uid: o.uid, name: toPublicName(opponentName) || opponentName, lastDueled: Date.now() } : o);
    } else {
        nextOpponents = [{ uid: opponentId, name: toPublicName(opponentName) || opponentName, lastDueled: Date.now() }, ...currentOpponents].slice(0, 100);
    }
    
    profile.recentOpponents = nextOpponents;
    currentUserProfile = profile;
    
    if (isFirebaseActive) {
        try {
            await updateDoc(userRef, {
                recentOpponents: sanitizeData(nextOpponents)
            });
        } catch (e) {
            console.error("[userService] Failed to save recent opponent:", e);
        }
    } else {
        saveToLocal(`user_profile_${uid}`, profile);
    }
    
    notifyProfileSubscribers();
};

export const getHighScore = (gameKey: string): number => {
    return currentUserProfile.highScores?.[gameKey] || 0;
};

export interface LeaderboardEntry {
    name: string;
    score: number;
    uid: string;
}

export const getLeaderboard = async (gameKey: string): Promise<LeaderboardEntry[]> => {
    if (!isFirebaseActive) {
        return [{
            name: currentUserProfile.name || 'Öğrenci',
            score: currentUserProfile.highScores?.[gameKey] || 0,
            uid: currentUserProfile.uid || 'local'
        }];
    }
    try {
        // Kendi rekoru tabloda yoksa ekle (bu koleksiyondan önce kaydedilmiş rekorlar için)
        const myScore = currentUserProfile.highScores?.[gameKey];
        if (myScore && auth.currentUser) await writeLeaderboardScore(gameKey, myScore).catch(console.warn);
        const snap = await getDocs(query(collection(db, 'leaderboards', gameKey, 'scores'), orderBy('score', 'desc'), firestoreLimit(10)));
        return snap.docs.map(d => ({ name: toPublicName(d.data().name) || 'Öğrenci', score: Number(d.data().score), uid: d.id }));
    } catch (e) {
        console.error("Leaderboard yükleme hatası:", e);
        return [{
            name: currentUserProfile.name || 'Öğrenci',
            score: currentUserProfile.highScores?.[gameKey] || 0,
            uid: currentUserProfile.uid || 'local'
        }];
    }
};

export const saveHighScore = (gameKey: string, score: number): number => {
    const currentHigh = currentUserProfile.highScores?.[gameKey] || 0;
    if (score > currentHigh) {
        const updatedProfile = { ...currentUserProfile };
        if (!updatedProfile.highScores) updatedProfile.highScores = {};
        updatedProfile.highScores[gameKey] = score;
        saveUserProfile(updatedProfile);
        writeLeaderboardScore(gameKey, score).catch(err => console.warn('[Leaderboard] skor yazılamadı:', err));
        return score;
    }
    return currentHigh;
};

const writeLeaderboardScore = async (gameKey: string, score: number) => {
    if (!isFirebaseActive || !auth.currentUser) return;
    if (gameKey.includes('/')) return; // doküman yolu bölünmesin
    await setDoc(doc(db, 'leaderboards', gameKey, 'scores', auth.currentUser.uid), {
        name: toPublicName(currentUserProfile.name || auth.currentUser.displayName) || 'Öğrenci',
        score: Number(score),
        updatedAt: Date.now(),
    });
};

// T.G.D sıralaması: services/tgdService.ts (sonucu yalnızca sunucu yazar: functions/src/tgdFunctions.ts › tgdSubmit)

export const getConqueredRegions = (subject: SubjectType): string[] => {
    return currentUserProfile.conqueredRegions?.[subject] || [];
};

export const conquerRegion = async (subject: SubjectType, regionName: string): Promise<void> => {
    const profile = { ...currentUserProfile };
    if (!profile.conqueredRegions) profile.conqueredRegions = {};
    if (!profile.conqueredRegions[subject]) profile.conqueredRegions[subject] = [];

    if (!profile.conqueredRegions[subject].includes(regionName)) {
        profile.conqueredRegions[subject].push(regionName);
        
        currentUserProfile = profile;

        if (isFirebaseActive && auth.currentUser) {
            try {
                const userRef = doc(db, "users", auth.currentUser.uid);
                await updateDoc(userRef, {
                    [`conqueredRegions.${subject}`]: arrayUnion(regionName)
                });
                console.log(`%c[Bölge Fethi] ✅ Firestore conqueredRegions güncellendi → "${regionName}"`, 'color:#22c55e');
            } catch (e) {
                console.error(`[Bölge Fethi] ❌ Firestore yazma hatası:`, e);
            }
        }
        
        const uid = auth.currentUser ? auth.currentUser.uid : (auth as any).currentUser?.uid || 'guest';
        if (uid) saveToLocal(`user_profile_${uid}`, currentUserProfile);
        
        // Notify subscribers about the update
        notifyProfileSubscribers();
    }
};

export const saveTestHistory = async (
    subject: SubjectType, 
    score: number, 
    questions: GeminiTestQuestion[], 
    userAnswers: {[key: number]: number},
    testType: 'ai_generated' | 'admin_manual' | 'most_liked' | 'favorites_mixed' | 'multiplayer_duel' | 'mistake_repair',
    testTitle?: string,
    testId?: string
) => {
    const profile = JSON.parse(JSON.stringify(currentUserProfile)) as UserProfile;

    // Soruları kaydet: Eğer base64 görsel varsa Storage'a yükle ve URL'e dönüştür
    const lightQuestions: GeminiTestQuestion[] = await Promise.all(
        questions.map(async (q) => {
            const qSubject = resolveQuestionSubject(q, subject);
            let finalImg = q.image || undefined;
            if (finalImg && finalImg.startsWith('data:') && isFirebaseActive) {
                try {
                    const uploaded = await uploadBase64Image(qSubject, finalImg);
                    if (uploaded) finalImg = uploaded;
                } catch (e) {
                    console.warn('[saveTestHistory] Görsel yükleme uyarısı:', e);
                }
            }
            return {
                ...q,
                subject: qSubject,
                image: finalImg,
                explanation: q.explanation ? q.explanation.slice(0, 1500) : undefined
            };
        })
    );

    const examId = testId || `exam_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
    const newHistoryItem: TestHistoryItem = {
        id: examId,
        date: Date.now(),
        subject,
        score,
        totalQuestions: questions.length,
        questions: lightQuestions,
        userAnswers,
        testType,
        testTitle: testTitle || 'Deneme Sınavı'
    };

    // Ana profili de güncel tut ama sadece son 5 tanesini gömülü sakla (offline fallback ve hafiflik için)
    if (!profile.testHistory) profile.testHistory = [];
    const existingIndex = profile.testHistory.findIndex(t => t.id === examId);
    if (existingIndex !== -1) {
        profile.testHistory[existingIndex] = newHistoryItem;
    } else {
        profile.testHistory.unshift(newHistoryItem);
        if (profile.testHistory.length > 5) {
            profile.testHistory = profile.testHistory.slice(0, 5);
        }
    }

    // Yerel önbelleğe anında yaz
    currentUserProfile.testHistory = profile.testHistory;
    const uid = auth.currentUser ? auth.currentUser.uid : (auth as any).currentUser?.uid;
    if (uid) saveToLocal(`user_profile_${uid}`, currentUserProfile);

    await saveUserProfile(profile);

    // ═══ Firestore Alt Koleksiyonuna (tests) Kaydet ═══
    if (isFirebaseActive && auth.currentUser) {
        try {
            const testRef = doc(db, "users", auth.currentUser.uid, "tests", newHistoryItem.id);
            // Beklenmez: kalıcı önbellekte kuyruğa girer (internetsizken await sonuç ekranını asılı bırakırdı)
            setDoc(testRef, sanitizeData(newHistoryItem), { merge: true })
                .then(() => console.log(`%c[Test geçmişi] ✅ Alt koleksiyona kaydedildi → ${newHistoryItem.id}`, 'color:#22c55e'))
                .catch(e => console.error('[Test geçmişi] Alt koleksiyona yazma hatası:', e));

            // YENİ: Hata Onarım Sınavındaki kaliteli paralel soruları ARKA PLANDA (non-blocking) ORTAK havuza aktar
            if (testType === 'mistake_repair') {
                const currentUid = auth.currentUser.uid;
                const currentEmail = auth.currentUser.email || 'guest';
                const globalCollectionName = `global_questions_${subject}`;

                // Arka plan işlemi: Ana sınav kaydını bekletmez, hata olursa ana akışı bozmaz
                (async () => {
                    try {
                        const poolPromises = questions.map(async (q) => {
                            const repairedQId = `q_repaired_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
                            const qData = {
                                id: repairedQId,
                                question: q.question,
                                options: q.options,
                                correctAnswerIndex: q.correctAnswerIndex,
                                topic: q.topic || 'Genel',
                                subTopic: q.subTopic || null,
                                difficulty: q.difficulty || 'Orta',
                                explanation: q.explanation || '',
                                image: q.image || null,
                                subject,
                                generatedBy: currentUid,
                                generatedAt: Date.now(),
                                model: 'gemini-3.1-pro-preview',
                                likeCount: 0,
                                solveCount: 0,
                                correctRate: 0,
                                gameMode: 'mistake_repair',
                                source: 'mistake_repair',
                                status: 'pending_review'
                            };

                            await Promise.allSettled([
                                setDoc(doc(db, globalCollectionName, repairedQId), sanitizeData(qData)),
                                setDoc(doc(db, "ai_repaired_questions", repairedQId), sanitizeData({
                                    ...qData,
                                    createdByEmail: currentEmail
                                }))
                            ]);
                        });

                        await Promise.allSettled(poolPromises);
                        console.log(`%c[Havuz] ✅ ${questions.length} adet paralel soru havuzlara aktarıldı.`, 'color:#22c55e');
                    } catch (poolErr) {
                        console.warn('[Havuz] Arka plan soru aktarma uyarısı:', poolErr);
                    }
                })();
            }
        } catch (e) {
            console.error('[Test geçmişi] Alt koleksiyona yazma hatası:', e);
        }
    }

    return newHistoryItem;
};

export const saveKapiExamResultInProfile = async (
    examId: string, 
    score: number, 
    answers: {[key: number]: number}
) => {
    const profile = JSON.parse(JSON.stringify(currentUserProfile)) as UserProfile;
    if (!profile.kapiExamResults) profile.kapiExamResults = {};
    profile.kapiExamResults[examId] = { score, date: Date.now(), answers };
    await saveUserProfile(profile);
};

export const getTestHistory = async (subject?: SubjectType): Promise<TestHistoryItem[]> => {
    const localHist = currentUserProfile.testHistory || [];

    if (!isFirebaseActive || !auth.currentUser) {
        // Çevrimdışı modda yerel profilden oku
        return subject ? localHist.filter(h => !subject || h.subject === subject || (!h.subject && subject === 'geography')) : localHist;
    }

    try {
        const testsRef = collection(db, "users", auth.currentUser.uid, "tests");
        const qSnapshot = await getDocs(testsRef);
        const list: TestHistoryItem[] = [];
        qSnapshot.forEach(docSnap => {
            list.push(docSnap.data() as TestHistoryItem);
        });
        
        // Tek kaynak bulut: yerel kopya eklenmez (başka cihazda silinen test geri gelmesin)
        const combinedList = list.filter(item => item.id);
        
        // Tarihe göre yeniden eskiye sıralayalım
        combinedList.sort((a, b) => (b.date || 0) - (a.date || 0));
        
        console.log(`%c[Test geçmişi] 📂 Toplam ${combinedList.length} adet test geçmişi yüklendi`, 'color:#3b82f6');
        return subject ? combinedList.filter(h => !subject || h.subject === subject || (!h.subject && subject === 'geography')) : combinedList;
    } catch (e) {
        console.error('[Test geçmişi] Alt koleksiyondan yükleme hatası:', e);
        // Hata durumunda yerel cache'den fallback yap
        return subject ? localHist.filter(h => !subject || h.subject === subject || (!h.subject && subject === 'geography')) : localHist;
    }
};

export const deleteTestHistoryItem = async (testId: string): Promise<boolean> => {
    const profile = { ...currentUserProfile };
    if (profile.testHistory) {
        profile.testHistory = profile.testHistory.filter(item => item.id !== testId);
    }
    currentUserProfile = profile;

    const uid = auth.currentUser ? auth.currentUser.uid : (auth as any).currentUser?.uid;
    if (uid) saveToLocal(`user_profile_${uid}`, currentUserProfile);

    if (isFirebaseActive && auth.currentUser) {
        try {
            const testRef = doc(db, "users", auth.currentUser.uid, "tests", testId);
            await deleteDoc(testRef);
            await saveUserProfile(profile);
            console.log(`%c[Test geçmişi] 🗑️ Silindi → ${testId}`, 'color:#ef4444');
            return true;
        } catch (e) {
            console.error('[Test geçmişi] Silme hatası:', e);
            return false;
        }
    }
    return true;
};

// ═══ TARİH KARAKTERLERİ ALT KOLEKSİYON SOHBET YÖNETİMİ (users/{uid}/character_chats/{characterId}) ═══

/**
 * Kullanıcının tarih karakteri (Attila, Fatih vb.) ile olan sohbetini 
 * users/{uid}/character_chats/{characterId} alt koleksiyonuna bağımsız doküman olarak kaydeder.
 * Ana profil dokümanını şişirmez, cihazlar arası senkronize eder.
 */
export const saveCharacterChatToCloud = async (characterId: string, messages: ChatMessage[]): Promise<void> => {
    if (!characterId || !messages || messages.length === 0) return;
    if (!isFirebaseActive || !auth.currentUser) return;

    try {
        const uid = auth.currentUser.uid;
        const chatDocRef = doc(db, "users", uid, "character_chats", characterId);
        // Son 60 mesaja kadar zengin hafızayı kaydet
        const payload = {
            characterId,
            messages: sanitizeData(messages.slice(-60)),
            updatedAt: Date.now()
        };
        await setDoc(chatDocRef, payload, { merge: true });
        console.log(`%c[Karakter Sohbeti] ☁️ Buluta kaydedildi: ${characterId} (${messages.length} mesaj)`, 'color:#10b981');
    } catch (err) {
        console.warn(`[Karakter Sohbeti] Bulut kayıt uyarısı (${characterId}):`, err);
    }
};

/**
 * Kullanıcının tarih karakteri ile olan sohbetini users/{uid}/character_chats/{characterId}
 * alt koleksiyonundan çeker.
 */
export const loadCharacterChatFromCloud = async (characterId: string): Promise<ChatMessage[] | null> => {
    if (!characterId || !isFirebaseActive || !auth.currentUser) return null;

    try {
        const uid = auth.currentUser.uid;
        const chatDocRef = doc(db, "users", uid, "character_chats", characterId);
        const snap = await getDoc(chatDocRef);
        if (snap.exists()) {
            const data = snap.data();
            if (Array.isArray(data.messages) && data.messages.length > 0) {
                return data.messages as ChatMessage[];
            }
        }
    } catch (err) {
        console.warn(`[Karakter Sohbeti] Buluttan çekme hatası (${characterId}):`, err);
    }
    return null;
};

/**
 * users/{uid}/character_chats/{characterId} alt koleksiyonundaki sohbeti siler.
 */
export const clearCharacterChatFromCloud = async (characterId: string): Promise<void> => {
    if (!characterId || !isFirebaseActive || !auth.currentUser) return;

    try {
        const uid = auth.currentUser.uid;
        const chatDocRef = doc(db, "users", uid, "character_chats", characterId);
        await deleteDoc(chatDocRef);
        console.log(`%c[Karakter Sohbeti] 🗑️ Buluttan silindi: ${characterId}`, 'color:#ef4444');
    } catch (err) {
        console.warn(`[Karakter Sohbeti] Buluttan silme hatası (${characterId}):`, err);
    }
};

export const analyzeTestResults = async (questions: GeminiTestQuestion[], answers: {[key: number]: number}, subject: SubjectType) => {
    emitAnswered(questions
        .map((q, i) => ({ q, a: answers[i] }))
        .filter(x => typeof x.a === 'number' && x.a >= 0)
        .map(x => ({ subject: resolveQuestionSubject(x.q, subject), correct: x.a === x.q.correctAnswerIndex })));
    const profile = JSON.parse(JSON.stringify(currentUserProfile)) as UserProfile;
    if (!profile.stats) profile.stats = {};
    if (!profile.wrongQuestions) profile.wrongQuestions = {};
    if (!profile.learningStyle) profile.learningStyle = { visual: 10, auditory: 10, analytical: 10 };

    profile.totalTestsTaken += 1;
    const crossRemoved: { nodeId: string; q: any }[] = [];

    for (let index = 0; index < questions.length; index++) {
        const q = questions[index];
        const selected = answers[index];
        const isCorrect = selected === q.correctAnswerIndex;
        const rawTopic = effectiveQuestionTopic(q); // TÜİK → metinden ünite
        // 🛡️ İLK KAYIT ZIRHI: Soru objesinden, konusundan veya metninden gerçek dersi %100 kesinlikle tespit et:
        const qSubject: SubjectType = resolveQuestionSubject(q, subject);
        // 🎯 KATMAN 1: Ana Ünite Başlığı — Soru içeriği ve blueprint ile %100 kesin eşle
        const mainTopic = resolveQuestionTopic(q, rawTopic, qSubject);
        console.log(`%c[analyzeTestResults] Soru #${index} | Konu: "${rawTopic}" | Eşleşen Ana Ünite: "${mainTopic}" | Doğru mu: ${isCorrect} (${qSubject})`, 'color:#3b82f6; font-weight:bold;');
        
        // 🎯 KATMAN 2: Alt Başlık — rawTopic farklıysa alt başlık olarak sakla
        const subTopic = filterSubTopic((rawTopic !== mainTopic && rawTopic !== 'Genel') ? rawTopic : null, qSubject, mainTopic);
        
        const statsKey = `${qSubject}_${mainTopic.replace(/\s+/g, '_')}`;

        if (!profile.stats[statsKey]) {
            profile.stats[statsKey] = {
                topicName: mainTopic,
                lesson: qSubject, 
                totalQuestionsSeen: 0,
                wrongAnswers: 0,
                consecutiveRetryCount: 0,
                lastStudied: Date.now(),
                subTopics: {}
            };
        }

        const stat = profile.stats[statsKey];
        stat.totalQuestionsSeen++;
        stat.lastStudied = Date.now();
        
        // Alt başlık istatistikleri
        if (subTopic) {
            if (!stat.subTopics) stat.subTopics = {};
            const subKey = subTopic.replace(/\s+/g, '_');
            if (!stat.subTopics[subKey]) {
                stat.subTopics[subKey] = { name: subTopic, seen: 0, wrong: 0 };
            }
            stat.subTopics[subKey].seen++;
            if (!isCorrect) stat.subTopics[subKey].wrong++;
        }

        if (!isCorrect) {
            stat.wrongAnswers++;
            
            // === RESİM BULUTA YÜKLEME KONTROLÜ ===
            let questionImage = q.image;
            if (questionImage && questionImage.startsWith('data:')) {
                try {
                    const storageUrl = await uploadBase64Image(qSubject, questionImage);
                    if (storageUrl) {
                        questionImage = storageUrl;
                        console.log(`%c[Storage] Soru görseli zihin haritası için yüklendi → ${storageUrl}`, 'color:#22c55e');
                    }
                } catch (e) {
                    console.warn('[Storage] Soru görseli yüklenemedi:', e);
                }
            }

            // === YANLIŞLARI ZİHİN HARİTASINA DÜŞÜR (TÜM DERSLER) ===
            const qId = q.id || generateQuestionId(q.question, q.topic);
            const questionWithId: GeminiTestQuestion = { 
                ...q, 
                image: questionImage || undefined,
                id: qId,
                isResolved: false,
                addedDate: Date.now(),
                subject: qSubject,
                userAnswerIndex: typeof selected === 'number' ? selected : undefined
            } as GeminiTestQuestion;

            const targetNodeId = getTargetNodeId(mainTopic, qSubject);

            if (targetNodeId) {
                if (!profile.wrongQuestions![targetNodeId]) profile.wrongQuestions![targetNodeId] = [];
                
                // 🧹 ÇAPRAZ DÜĞÜM TEMİZLİĞİ: Bu soru daha önce yanlışlıkla başka bir düğüme eklendiyse oradan temizle!
                Object.keys(profile.wrongQuestions!).forEach(otherNode => {
                    if (otherNode !== targetNodeId && profile.wrongQuestions![otherNode]) {
                        profile.wrongQuestions![otherNode] = pullWrongs(profile.wrongQuestions![otherNode], otherNode,
                            existing => !(existing.question !== q.question && (!qId || existing.id !== qId)), crossRemoved);
                    }
                });

                const exists = profile.wrongQuestions![targetNodeId].some(existing => existing.question === q.question || (existing.id && existing.id === qId));
                if (!exists) {
                    profile.wrongQuestions![targetNodeId].push(questionWithId);
                    console.log(`%c[analyzeTestResults] 🔴 Hata Zihin Haritasına Eklendi → Node ID: "${targetNodeId}" (${qSubject}) | Soru: "${q.question.substring(0, 40)}..."`, 'color:#ef4444; font-weight:bold;');
                } else {
                    console.log(`%c[analyzeTestResults] ℹ️ Hata Zaten Kayıtlı → Node ID: "${targetNodeId}"`, 'color:#f59e0b;');
                }
            } else {
                console.warn(`%c[analyzeTestResults] ⚠️ Hata Kaydı Yapılamadı! targetNodeId tespit edilemedi. Konu: "${rawTopic}" | Eşleşen Ana Ünite: "${mainTopic}"`, 'color:#f59e0b; font-weight:bold;');
            }
        }

        if (mainTopic.toLowerCase().includes("harita") || mainTopic.toLowerCase().includes("konum")) {
            profile.learningStyle.visual += isCorrect ? 2 : 0.5;
        } else if (mainTopic.toLowerCase().includes("ekonomi") || mainTopic.toLowerCase().includes("analiz")) {
            profile.learningStyle.analytical += isCorrect ? 2 : 0.5;
        } else {
            profile.learningStyle.auditory += isCorrect ? 2 : 0.5;
        }
    }

    await saveUserProfile(profile);
    if (isFirebaseActive && auth.currentUser) removeWrongDocs(auth.currentUser.uid, crossRemoved).catch(console.warn);

    return profile;
};

export const markWrongQuestionResolved = async (nodeId: string, questionId: string) => {
    const profile = JSON.parse(JSON.stringify(currentUserProfile)) as UserProfile;
    if (!profile.wrongQuestions || !profile.wrongQuestions[nodeId]) return;

    profile.wrongQuestions[nodeId] = profile.wrongQuestions[nodeId].map(q => {
        if (q.id === questionId) {
            return { ...q, isResolved: true, solvedDate: Date.now() };
        }
        return q;
    });
    
    await saveUserProfile(profile);
};

export const deleteWrongQuestion = async (nodeId: string, questionId: string): Promise<boolean> => {
    try {
        if (!currentUserProfile.wrongQuestions || !currentUserProfile.wrongQuestions[nodeId]) return false;

        const removed: { nodeId: string; q: any }[] = [];
        currentUserProfile.wrongQuestions[nodeId] = pullWrongs(currentUserProfile.wrongQuestions[nodeId], nodeId, q => q.id === questionId, removed);
        if (currentUserProfile.wrongQuestions[nodeId].length === 0) {
            delete currentUserProfile.wrongQuestions[nodeId];
        }

        // Firestore'dan yalnızca bu soruyu sil
        if (isFirebaseActive && auth.currentUser) {
            await removeWrongDocs(auth.currentUser.uid, removed);
        }

        // Local cache güncelle ve bildir
        if (auth.currentUser) {
            saveToLocal(`user_profile_${auth.currentUser.uid}`, currentUserProfile);
        }
        saveToLocal('kpss_current_user_profile', currentUserProfile);
        notifyProfileSubscribers();

        console.log(`%c[User Service] 🗑️ Soru kalıcı olarak silindi → Node: ${nodeId}, Soru: ${questionId}`, 'color:#ef4444;font-weight:bold');
        return true;
    } catch (e) {
        console.error('[User Service] Soru silinirken hata oluştu:', e);
        return false;
    }
};

export const deleteAnalysisLog = async (nodeId: string, logId: string): Promise<boolean> => {
    try {
        if (!currentUserProfile.analysisHistory || !currentUserProfile.analysisHistory[nodeId]) return false;

        currentUserProfile.analysisHistory[nodeId] = currentUserProfile.analysisHistory[nodeId].filter(log => log.id !== logId);

        if (isFirebaseActive && auth.currentUser) {
            await deleteAnalysisLogDocs(auth.currentUser.uid, [logId]);
        }

        if (auth.currentUser) {
            saveToLocal(`user_profile_${auth.currentUser.uid}`, currentUserProfile);
        }
        saveToLocal('kpss_current_user_profile', currentUserProfile);
        notifyProfileSubscribers();

        console.log(`%c[User Service] 🗑️ Analiz silindi → Node: ${nodeId}, Log: ${logId}`, 'color:#ef4444');
        return true;
    } catch (e) {
        console.error('[User Service] Analiz silinirken hata oluştu:', e);
        return false;
    }
};

/**
 * Verilen nodeId'nin hangi derse (SubjectType) ait olduğunu tespit eder.
 */
export const getNodeSubject = (nodeId: string): SubjectType => {
    if (!nodeId) return 'geography';
    const lower = nodeId.toLowerCase();
    if (lower.startsWith('history_') || lower.startsWith('hist_') || /^u\d+/i.test(lower)) return 'history';
    if (lower.startsWith('cit_') || lower.startsWith('citizenship_')) return 'citizenship';
    if (lower.startsWith('trk_') || lower.startsWith('turkish_')) return 'turkish';
    if (lower.startsWith('mat_') || lower.startsWith('math_')) return 'math';
    if (lower.startsWith('geo_') || [
        'cografi_konum', 'fiziki_ozellikler', 'iklim', 'nufus', 'tarim',
        'hayvancilik', 'madenler_enerji', 'sanayi', 'ulasim_ticaret',
        'su_toprak', 'bolgeler', 'yersekilleri', 'iklim_bitki', 'nufus_yerlesme'
    ].includes(lower)) {
        return 'geography';
    }
    return 'geography';
};

/**
 * Kullanıcının uygulama içi testlerden biriken hatalı sorularını
 * (belirtilen ders veya tüm dersler için) Firestore alt koleksiyonundan,
 * yerel depolamadan ve bellekten kalıcı olarak sıfırlar.
 * Not: Kullanıcının kamera/galeriden yüklediği fotoğraflı soru analizlerine (analysisHistory) DOKUNMAZ!
 */
export const resetAllWrongQuestions = async (subject?: SubjectType): Promise<boolean> => {
    try {
        if (subject) {
            if (currentUserProfile.wrongQuestions) {
                Object.keys(currentUserProfile.wrongQuestions).forEach(nodeId => {
                    if (getNodeSubject(nodeId) === subject) {
                        delete currentUserProfile.wrongQuestions![nodeId];
                    }
                });
            }
        } else {
            currentUserProfile.wrongQuestions = {};
        }

        if (isFirebaseActive && auth.currentUser) {
            const uid = auth.currentUser.uid;
            // Soru başına dokümanlar: buluttaki son halde bu derse ait olanların hepsi
            const itemIds = [...serverWrongDocs.entries()]
                .filter(([, cur]) => !subject || getNodeSubject(cur.nodeId) === subject)
                .map(([id]) => id);
            for (let i = 0; i < itemIds.length; i += 400) {
                const batch = writeBatch(db);
                itemIds.slice(i, i + 400).forEach(id => batch.delete(doc(db, "users", uid, WRONG_ITEMS, id)));
                await batch.commit();
            }

            // Eski yapı (henüz taşınmamış düğüm dokümanları)
            const wrongQuestionsRef = collection(db, "users", uid, "wrong_questions");
            const snap = await getDocs(wrongQuestionsRef);

            if (!snap.empty) {
                const batch = writeBatch(db);
                let deletedCount = 0;
                snap.forEach(docSnap => {
                    const nodeId = docSnap.id;
                    if (!subject || getNodeSubject(nodeId) === subject) {
                        batch.delete(docSnap.ref);
                        deletedCount++;
                    }
                });
                if (deletedCount > 0) {
                    await batch.commit();
                    console.log(`%c[resetAllWrongQuestions] 🗑️ Firestore wrong_questions subcollection temizlendi (${deletedCount} düğüm silindi, ders: ${subject || 'tüm dersler'}).`, 'color:#22c55e');
                }
            }

            if (!subject) {
                // Ana kullanıcı dokümanındaki olası eski wrongQuestions alanını da temizle
                const userRef = doc(db, "users", uid);
                await updateDoc(userRef, {
                    wrongQuestions: deleteField(),
                    wrongQuestionsClearedAt: Date.now()
                }).catch(err => {
                    console.warn("[resetAllWrongQuestions] Ana doküman wrongQuestions temizleme uyarısı:", err);
                });
            } else {
                await saveUserProfile(currentUserProfile);
            }
        }

        if (auth.currentUser) {
            saveToLocal(`user_profile_${auth.currentUser.uid}`, currentUserProfile);
        }
        saveToLocal('kpss_current_user_profile', currentUserProfile);
        notifyProfileSubscribers();

        console.log(`%c[User Service] 🗑️ Hatalı sorular başarıyla sıfırlandı (${subject || 'tüm dersler'}).`, 'color:#22c55e;font-weight:bold;');
        return true;
    } catch (e) {
        console.error('[User Service] resetAllWrongQuestions hatası:', e);
        return false;
    }
};

/**
 * Kullanıcının kamera/galeriden yüklediği fotoğraflı soru analizlerini (analysisHistory)
 * belirtilen ders veya tüm dersler için kalıcı olarak sıfırlar.
 */
export const resetAllAnalysisHistory = async (subject?: SubjectType): Promise<boolean> => {
    try {
        if (!currentUserProfile.analysisHistory) return true;

        const removedIds: string[] = [];
        Object.keys(currentUserProfile.analysisHistory).forEach(nodeId => {
            if (!subject || getNodeSubject(nodeId) === subject) {
                (currentUserProfile.analysisHistory![nodeId] || []).forEach(log => removedIds.push(String(log.id)));
                delete currentUserProfile.analysisHistory![nodeId];
            }
        });

        if (isFirebaseActive && auth.currentUser) {
            await deleteAnalysisLogDocs(auth.currentUser.uid, removedIds);
        }

        if (auth.currentUser) {
            saveToLocal(`user_profile_${auth.currentUser.uid}`, currentUserProfile);
        }
        saveToLocal('kpss_current_user_profile', currentUserProfile);
        notifyProfileSubscribers();

        console.log(`%c[User Service] 🗑️ Fotoğraf analiz geçmişi sıfırlandı (${subject || 'tümü'}).`, 'color:#22c55e;font-weight:bold;');
        return true;
    } catch (e) {
        console.error('[User Service] resetAllAnalysisHistory hatası:', e);
        return false;
    }
};

export const getAllUnresolvedQuestions = (): { question: GeminiTestQuestion, nodeId: string }[] => {
    const profile = currentUserProfile;
    if (!profile.wrongQuestions) return [];

    let allQuestions: { question: GeminiTestQuestion, nodeId: string }[] = [];

    Object.entries(profile.wrongQuestions).forEach(([nodeId, questions]) => {
        questions.forEach(q => {
            if (!q.isResolved) {
                allQuestions.push({ question: q, nodeId: nodeId });
            }
        });
    });

    for (let i = allQuestions.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [allQuestions[i], allQuestions[j]] = [allQuestions[j], allQuestions[i]];
    }

    return allQuestions;
};

export const saveAnalysisLog = async (
    nodeId: string, 
    imageBase64: string, 
    text: string, 
    fileName?: string, 
    imageHash?: string,
    topic?: string,
    subTopic?: string
) => {
    let finalImageUrl = imageBase64;
    
    // 🛡️ DERS TESPİTİ: Soru metninden veya konusundan kesin dersi tespit et
    const initialSubject = getNodeSubject(nodeId);
    const resolvedSubject: SubjectType = resolveQuestionSubject({ topic, question: text }, initialSubject);

    // Eğer konu/metin başka derse aitse doğru düğüme yönlendir:
    let targetNodeId = nodeId;
    if (resolvedSubject !== initialSubject) {
        const correctNode = getTargetNodeId(topic || 'Genel', resolvedSubject);
        if (correctNode) {
            console.log(`%c[saveAnalysisLog] 🛡️ Düğüm yönlendirildi: "${nodeId}" (${initialSubject}) → "${correctNode}" (${resolvedSubject})`, 'color:#10b981;font-weight:bold;');
            targetNodeId = correctNode;
        }
    }

    // Eğer base64 ise Firebase Storage'a yükle ve bulut URL'sini al
    if (imageBase64 && imageBase64.startsWith('data:')) {
        try {
            const storageUrl = await uploadBase64Image(resolvedSubject, imageBase64);
            if (storageUrl) {
                finalImageUrl = storageUrl;
                console.log(`%c[Storage] Analiz görseli yüklendi → ${storageUrl}`, 'color:#22c55e');
            }
        } catch (e) {
            console.warn('[Storage] Analiz görseli yüklenemedi, base64 saklanıyor:', e);
        }
    }

    if (!currentUserProfile.analysisHistory) currentUserProfile.analysisHistory = {};
    if (!currentUserProfile.analysisHistory[targetNodeId]) currentUserProfile.analysisHistory[targetNodeId] = [];

    const finalTopic = topic || normalizeTopicName(targetNodeId, resolvedSubject, true);
    const cleanSubTopic = filterSubTopic(subTopic, resolvedSubject, finalTopic);

    const newLog: AnalysisLog = {
        id: Date.now().toString(),
        imageUrl: finalImageUrl,
        analysisText: text,
        timestamp: Date.now(),
        fileName: fileName || undefined,
        imageHash: imageHash || undefined,
        topic: finalTopic || undefined,
        subTopic: cleanSubTopic
    };

    // 🧹 ÇAPRAZ DÜĞÜM TEMİZLİĞİ: Bu analiz logu yanlışlıkla başka bir düğüme eklendiyse oradan temizle
    const crossNodeRemovedIds: string[] = [];
    Object.keys(currentUserProfile.analysisHistory).forEach(otherNode => {
        if (otherNode !== targetNodeId && currentUserProfile.analysisHistory![otherNode]) {
            currentUserProfile.analysisHistory![otherNode] = currentUserProfile.analysisHistory![otherNode].filter(log => {
                const keep = (imageHash && log.imageHash ? log.imageHash !== imageHash : true) &&
                       (text && log.analysisText ? log.analysisText.trim() !== text.trim() : true);
                if (!keep) crossNodeRemovedIds.push(String(log.id));
                return keep;
            });
        }
    });

    currentUserProfile.analysisHistory[targetNodeId].unshift(newLog); 

    // 📊 GELİŞİM KARNESİ ENTEGRASYONU:
    // Analiz edilen her fotoğrafik soru, öğrencinin çözüp takıldığı/hata yaptığı bir soru olduğundan
    // Gelişim Karnesi'ne (+1 soru görüldü, +1 hata yapıldı) olarak doğrudan işlenir.
    if (!currentUserProfile.stats) currentUserProfile.stats = {};
    const statsKey = `${resolvedSubject}_${finalTopic.replace(/\s+/g, '_')}`;

    if (!currentUserProfile.stats[statsKey]) {
        currentUserProfile.stats[statsKey] = {
            topicName: finalTopic,
            lesson: resolvedSubject,
            totalQuestionsSeen: 0,
            wrongAnswers: 0,
            consecutiveRetryCount: 0,
            lastStudied: Date.now(),
            subTopics: {}
        };
    }

    const stat = currentUserProfile.stats[statsKey];
    stat.totalQuestionsSeen = (stat.totalQuestionsSeen || 0) + 1;
    stat.wrongAnswers = (stat.wrongAnswers || 0) + 1;
    stat.lastStudied = Date.now();

    if (cleanSubTopic) {
        if (!stat.subTopics) stat.subTopics = {};
        const subKey = cleanSubTopic.replace(/\s+/g, '_');
        if (!stat.subTopics[subKey]) {
            stat.subTopics[subKey] = { name: cleanSubTopic, seen: 0, wrong: 0 };
        }
        stat.subTopics[subKey].seen = (stat.subTopics[subKey].seen || 0) + 1;
        stat.subTopics[subKey].wrong = (stat.subTopics[subKey].wrong || 0) + 1;
    }

    const uid = auth.currentUser?.uid;
    if (isFirebaseActive && uid) {
        // Analiz kendi dokümanına yazılır. Hata yukarı fırlatılır: toplu analizde kayıt "tamamlandı" görünüp
        // kaybolmasın, "hata" olarak işaretlenip tekrar denenebilsin.
        try {
            await writeAnalysisLogs(uid, [{ nodeId: targetNodeId, log: newLog }]);
        } catch (err) {
            currentUserProfile.analysisHistory[targetNodeId] = currentUserProfile.analysisHistory[targetNodeId].filter(l => l.id !== newLog.id);
            stat.totalQuestionsSeen = Math.max(0, stat.totalQuestionsSeen - 1);
            stat.wrongAnswers = Math.max(0, stat.wrongAnswers - 1);
            notifyProfileSubscribers();
            throw err;
        }
        if (crossNodeRemovedIds.length) deleteAnalysisLogDocs(uid, crossNodeRemovedIds).catch(console.warn);
        setDoc(statsDocRef(uid), { [statsKey]: sanitizeData(stat) }, { merge: true })
            .catch(err => console.warn('[saveAnalysisLog] Gelişim karnesi güncellenemedi:', err));
        saveToLocal(`user_profile_${uid}`, currentUserProfile);
        notifyProfileSubscribers();
    } else {
        await saveUserProfile(currentUserProfile);
    }
};

export const getWeakestTopics = (limit: number = 4, subject?: SubjectType): TopicStat[] => {
    const allStats = Object.values(currentUserProfile.stats);
    const filteredStats = subject ? allStats.filter(s => s.lesson === subject) : allStats;

    // 🎯 Eski stat'leri normalize et — "Doğal Afetler" → blueprint karşılığı
    return filteredStats
        .sort((a, b) => (b.wrongAnswers / (b.totalQuestionsSeen || 1)) - (a.wrongAnswers / (a.totalQuestionsSeen || 1)))
        .slice(0, limit)
        .map(stat => ({
            ...stat,
            topicName: stat.lesson
                ? normalizeTopicName(stat.topicName, stat.lesson as SubjectType)
                : stat.topicName
        }));
};

export const getAdaptiveTestConfig = (subject?: SubjectType) => {
    const profile = currentUserProfile;
    const weakTopics = getWeakestTopics(4, subject);
    
    if (weakTopics.length === 0) return null;

    const distribution: { topic: string, count: number }[] = [];
    let usedCount = 0;
    const totalTarget = 18; 

    weakTopics.forEach(t => {
        const multiplier = 2 + (0.5 * t.consecutiveRetryCount);
        let count = Math.round(2 * multiplier);
        if (count > 6) count = 6;
        t.consecutiveRetryCount++;
        
        distribution.push({ topic: t.topicName, count });
        usedCount += count;
    });

    saveUserProfile(profile); 

    if (usedCount < totalTarget) {
        distribution.push({ 
            topic: "Genel Karma", 
            count: totalTarget - usedCount 
        });
    }

    return distribution;
};

// ═══════════════════════════════════════════════════════════════
// 💬 DERS BAZLI SOHBET OTURUMLARI (Cezeri + Kapi Hoca)
// sessionKey format: "cezeri_global" | "kapiHoca_geography" | "kapiHoca_history" ...
// Her oturum max 100 mesaj tutar. Hem localStorage hem Firestore'a yazılır.
// ═══════════════════════════════════════════════════════════════

export const getChatSessionHistory = async (sessionKey: string): Promise<GlobalChatMessage[]> => {
    // 1. Çevrimdışı veya girişsiz modda yerel cache'den oku
    if (!isFirebaseActive || !auth.currentUser) {
        if (currentUserProfile.chatSessions?.[sessionKey]) {
            return currentUserProfile.chatSessions[sessionKey];
        }
        return [];
    }

    try {
        const sessionRef = doc(db, "users", auth.currentUser.uid, "chat_sessions", sessionKey);
        const docSnap = await getDoc(sessionRef);
        if (docSnap.exists()) {
            const data = docSnap.data();
            console.log(`💬 [ChatSession] YÜKLEME (Bulut) → "${sessionKey}" | ${data.messages?.length || 0} mesaj bulundu`);
            // Bellek içi cache'i de güncelle ki offline kalındığında hazır olsun
            if (!currentUserProfile.chatSessions) currentUserProfile.chatSessions = {};
            const messages: GlobalChatMessage[] = Array.isArray(data.messages) ? data.messages : [];
            if (messages.length > CHAT_SESSION_MAX) trimChatSession(sessionRef);
            currentUserProfile.chatSessions[sessionKey] = messages.slice(-CHAT_SESSION_MAX);
            return currentUserProfile.chatSessions[sessionKey];
        }
    } catch (e) {
        console.error(`💬 [ChatSession] Yükleme hatası:`, e);
    }

    // 2. Legacy fallback: Alt koleksiyonda yoksa ana belgeden oku
    if (currentUserProfile.chatSessions?.[sessionKey]) {
        const msgs = currentUserProfile.chatSessions[sessionKey];
        console.log(`💬 [ChatSession] YÜKLEME (Yerel) → "${sessionKey}" | ${msgs.length} mesaj bulundu`);
        return msgs;
    }
    if (sessionKey === 'cezeri_global' && currentUserProfile.chatHistory?.length) {
        console.log(`💬 [ChatSession] LEGACY YÜKLEME (Yerel) → cezeri_global | ${currentUserProfile.chatHistory.length} mesaj`);
        return currentUserProfile.chatHistory;
    }
    console.log(`💬 [ChatSession] YÜKLEME → "${sessionKey}" | Geçmiş yok, yeni oturum`);
    return [];
};

const CHAT_SESSION_MAX = 100;
/** Buluttaki oturumu en yeni 100 mesaja indirir (transaction: aynı anda eklenen mesaj kaybolmaz) */
const trimChatSession = (sessionRef: ReturnType<typeof doc>) =>
    runTransaction(db, async (tx) => {
        const snap = await tx.get(sessionRef);
        const messages = snap.data()?.messages;
        if (Array.isArray(messages) && messages.length > CHAT_SESSION_MAX) {
            tx.update(sessionRef, { messages: messages.slice(-CHAT_SESSION_MAX) });
        }
    }).catch(e => console.warn('[ChatSession] kırpma hatası:', e));

export const saveChatSessionMessage = async (sessionKey: string, msg: GlobalChatMessage) => {
    // ═══ GÜVENLİ KAYIT: Sadece yerel olarak bellek içi güncelle ═══
    if (!currentUserProfile.chatSessions) currentUserProfile.chatSessions = {};
    if (!currentUserProfile.chatSessions[sessionKey]) currentUserProfile.chatSessions[sessionKey] = [];

    currentUserProfile.chatSessions[sessionKey].push(msg);
    // Max 100 mesaj per session
    if (currentUserProfile.chatSessions[sessionKey].length > 100) {
        currentUserProfile.chatSessions[sessionKey].shift();
    }

    console.log(`💬 [ChatSession] KAYIT → "${sessionKey}" | role:${msg.role} | toplam:${currentUserProfile.chatSessions[sessionKey].length}/100 | text:"${msg.text.substring(0, 60)}..."`);

    if (sessionKey === 'cezeri_global') {
        currentUserProfile.chatHistory = currentUserProfile.chatSessions[sessionKey];
    }

    // ═══ Firestore: mesajı buluttaki listeye ATOMİK ekle ═══
    // Eskiden bellekteki bütün liste yazılıyordu: bellek boş/eskiyse (profil anlık görüntüsü sıfırlamışsa)
    // buluttaki geçmiş tek mesajla eziliyordu.
    if (isFirebaseActive && auth.currentUser) {
        try {
            const sessionRef = doc(db, "users", auth.currentUser.uid, "chat_sessions", sessionKey);
            await setDoc(sessionRef, { messages: arrayUnion(sanitizeData(msg)) }, { merge: true });
            // 100 mesaj sınırı buluttaki listeye de uygulanır (en yeni 100 kalır)
            if (currentUserProfile.chatSessions[sessionKey].length >= 100) trimChatSession(sessionRef);
            console.log(`💬 [ChatSession] ✅ Alt koleksiyona kaydedildi → "${sessionKey}"`);
        } catch (e) {
            console.error(`💬 [ChatSession] ❌ Alt koleksiyon yazma hatası:`, e);
        }
    }

    // localStorage: Yerel cache
    const uid = auth.currentUser ? auth.currentUser.uid : (auth as any).currentUser?.uid;
    if (uid) saveToLocal(`user_profile_${uid}`, currentUserProfile);
};

// Legacy wrappers (geriye uyumluluk — Cezeri)
export const getGlobalChatHistory = async (): Promise<GlobalChatMessage[]> => {
    return getChatSessionHistory('cezeri_global');
};

export const saveGlobalChatMessage = async (msg: GlobalChatMessage) => {
    await saveChatSessionMessage('cezeri_global', msg);
};

export const clearChatSession = async (sessionKey: string): Promise<void> => {
    if (!currentUserProfile.chatSessions) currentUserProfile.chatSessions = {};
    currentUserProfile.chatSessions[sessionKey] = [];

    if (sessionKey === 'cezeri_global') {
        currentUserProfile.chatHistory = [];
    }

    if (isFirebaseActive && auth.currentUser) {
        try {
            const sessionRef = doc(db, "users", auth.currentUser.uid, "chat_sessions", sessionKey);
            await setDoc(sessionRef, { messages: [] }, { merge: true });
            console.log(`💬 [ChatSession] 🗑️ Sohbet geçmişi sıfırlandı → "${sessionKey}"`);
        } catch (e) {
            console.error(`💬 [ChatSession] ❌ Sıfırlama hatası:`, e);
        }
    }

    const uid = auth.currentUser ? auth.currentUser.uid : (auth as any).currentUser?.uid;
    if (uid) saveToLocal(`user_profile_${uid}`, currentUserProfile);
};

// --- ZİNCİR (CHAIN/STREAK) YÖNETİMİ ---

const SEASON_START = '2025-09-01';

/** Türkiye Saati (UTC+3) ile Date objesi üretir (tarayıcı saatinden bağımsız) */
export const getTurkeyDate = (dateObj: Date = new Date()): Date => {
    const utc = dateObj.getTime() + (dateObj.getTimezoneOffset() * 60000);
    return new Date(utc + (3600000 * 3));
};

/** Tarihi Türkiye saatine göre YYYY-MM-DD formatında döner */
export const getTurkeyDateString = (dateObj: Date = new Date()): string => {
    const trDate = getTurkeyDate(dateObj);
    const year = trDate.getFullYear();
    const month = String(trDate.getMonth() + 1).padStart(2, '0');
    const day = String(trDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getDaysPassed = (): number => {
    const seasonStart = new Date(SEASON_START);
    // Compare dates based on local calendar timezone
    const sYear = seasonStart.getUTCFullYear();
    const sMonth = seasonStart.getUTCMonth();
    const sDate = seasonStart.getUTCDate();
    const seasonStartLocal = new Date(sYear, sMonth, sDate);
    
    const trDate = getTurkeyDate();
    const trDateLocal = new Date(trDate.getFullYear(), trDate.getMonth(), trDate.getDate());
    
    return Math.round((trDateLocal.getTime() - seasonStartLocal.getTime()) / (1000 * 60 * 60 * 24));
};

export const getChainData = (): { wateredHistory: number[]; lastWatered: string } => {
    return currentChainData;
};

/** Bugün zinciri sula. true döndürürse yeni sulanmış demektir (toast göster). */
export const waterChainToday = async (): Promise<boolean> => {
    if (isFirebaseActive && auth.currentUser) {
        if (!isProfileLoadedFromFirestore) {
            console.warn('[🌿 Zincir] Profil henüz Firestore\'dan yüklenmedi. Sulama ertelendi.');
            return false;
        }
        if (!isChainLoadedFromFirestore) {
            console.warn('[🌿 Zincir] İzole zincir verisi henüz Firestore\'dan yüklenmedi. Sulama ertelendi.');
            return false;
        }
        if (currentUserProfile.uid !== auth.currentUser.uid) {
            console.warn('[🌿 Zincir] Profil henüz tam yüklenmedi veya kullanıcı uyuşmazlığı var. Sulama ertelendi.');
            return false;
        }

        // Veritabanına doğrudan bağlanarak son veriyi çek (Veri kaybını önleme garantisi)
        try {
            const chainRef = doc(db, "users", auth.currentUser.uid, "user_data", "chain");
            const docSnap = await getDoc(chainRef);
            if (docSnap.exists()) {
                const freshChain = docSnap.data();
                if (freshChain && freshChain.wateredHistory) {
                    currentChainData = {
                        wateredHistory: freshChain.wateredHistory || [],
                        lastWatered: freshChain.lastWatered || ''
                    };
                    currentUserProfile.chainData = currentChainData;
                    console.log('[🌿 Zincir] Buluttan en güncel zincir verisi başarıyla çekildi.');
                    // Önbelleği güncelle ve aboneleri bilgilendir
                    saveToLocal(`user_profile_${auth.currentUser.uid}`, currentUserProfile);
                    notifyProfileSubscribers();
                }
            }
        } catch (e) {
            console.error('[🌿 Zincir] Buluttan son zincir verisi çekilirken hata:', e);
        }
    }
    
    const today = getTurkeyDateString();
    const chainData = currentChainData;
    
    if (chainData.lastWatered === today) {
        return false; // Zaten sulanmış
    }
    
    const daysPassed = getDaysPassed();
    const history = [...chainData.wateredHistory];
    
    // Eksik günleri 0 ile doldur
    while (history.length < daysPassed) {
        history.push(0);
    }
    history[daysPassed] = 1; // Bugünü sula
    
    const newChainData = {
        wateredHistory: history,
        lastWatered: today
    };
    
    // Memory güncelle
    currentChainData = newChainData;
    currentUserProfile.chainData = currentChainData;
    
    // ═══ Firestore: SADECE chainData alanını güncelle (profil EZİLMEZ!) ═══
    if (isFirebaseActive && auth.currentUser) {
        try {
            const chainRef = doc(db, "users", auth.currentUser.uid, "user_data", "chain");
            await setDoc(chainRef, sanitizeData(newChainData), { merge: true });
            
            // 🛡️ YEDEKLEME MEKANİZMASI: Sadece eklenebilir (append-only) log kaydı
            const logRef = doc(db, "users", auth.currentUser.uid, "chain_logs", today);
            await setDoc(logRef, {
                date: today,
                action: "watered",
                timestamp: serverTimestamp(),
                daysPassed: daysPassed
            }, { merge: true });
            
            console.log(`%c[🌿 Zincir] ✅ Firestore chainData güncellendi — gün:${daysPassed}, toplam yaprak:${history.filter(h => h === 1).length}`, 'color:#22c55e');
            console.log(`%c[🌿 Zincir] 🛡️ Yedekleme logu başarıyla oluşturuldu: ${today}`, 'color:#3b82f6');
        } catch (e) {
            console.error('Zincir Firestore hatası:', e);
        }
    }
    
    // localStorage
    const uid = auth.currentUser ? auth.currentUser.uid : (auth as any).currentUser?.uid;
    if (uid) saveToLocal(`user_profile_${uid}`, currentUserProfile);
    
    return true; // Yeni sulandı
};

/** Toplam sulanan (yeşertilen) gün sayısını hesapla. */
export const calculateStreak = (): number => {
    const history = currentChainData?.wateredHistory || currentUserProfile?.chainData?.wateredHistory || [];
    const wateredCount = history.filter(h => h === 1).length;
    return Math.max(1, wateredCount);
};

// ══════════════════════════════════════════════════════════════
// 🚑 PAYLAŞIMLI CHECK-UP SİSTEMİ
// Admin üretir → Firestore'a kaydeder → Tüm kullanıcılar çözer
// Koleksiyon: checkup_exams/{subject}/exams/{auto-id}
// ══════════════════════════════════════════════════════════════

export interface CheckupExam {
    id?: string;
    subject: SubjectType;
    questions: GeminiTestQuestion[];
    createdAt: any; // Firestore Timestamp
    createdBy: string;
    questionCount: number;
}

/** Admin: Üretilen check-up sorularını Firestore'a kaydet */
export const saveCheckupExam = async (subject: SubjectType, questions: GeminiTestQuestion[]): Promise<string | null> => {
    if (!isFirebaseActive || !auth.currentUser) {
        console.error('[CHECKUP] Firebase aktif değil veya giriş yapılmamış');
        return null;
    }

    try {
        const examId = `checkup_${subject}_${Date.now()}`;
        const examRef = doc(db, 'checkup_exams', examId);

        // Soruları hafiflet — base64 görselleri temizle
        const lightQuestions = questions.map(q => ({
            ...q,
            image: q.image?.startsWith('data:') ? undefined : q.image,
            explanation: q.explanation ? q.explanation.slice(0, 500) : undefined
        }));

        const examData: CheckupExam = {
            subject,
            questions: sanitizeData(lightQuestions),
            createdAt: serverTimestamp(),
            createdBy: auth.currentUser.email || auth.currentUser.uid,
            questionCount: questions.length
        };

        await setDoc(examRef, examData);
        console.log(`%c[CHECKUP] ✅ ${questions.length} soru Firestore'a kaydedildi → ${examId}`, 'color:#22c55e;font-weight:bold');
        return examId;
    } catch (e) {
        console.error('[CHECKUP] ❌ Kayıt hatası:', e);
        return null;
    }
};

/** Herkes: Belirli ders için en son admin check-up sınavını yükle */
export const loadLatestCheckupExam = async (subject: SubjectType): Promise<CheckupExam | null> => {
    console.log(`%c[CHECKUP-DEBUG] 🔍 loadLatestCheckupExam BAŞLADI — subject: ${subject}, isFirebaseActive: ${isFirebaseActive}, user: ${auth.currentUser?.email || auth.currentUser?.uid || 'YOK'}`, 'color:#ff6b6b;font-weight:bold;font-size:12px');
    
    if (!isFirebaseActive) {
        console.warn('[CHECKUP-DEBUG] ❌ Firebase aktif değil — return null');
        return null;
    }

    try {
        const examsRef = collection(db, 'checkup_exams');
        console.log('[CHECKUP-DEBUG] 📂 Collection ref oluşturuldu: checkup_exams');
        
        const q = query(
            examsRef,
            orderBy('createdAt', 'desc'),
            firestoreLimit(20)
        );
        console.log('[CHECKUP-DEBUG] 🔎 Query oluşturuldu (orderBy createdAt desc, limit 20)');

        const snapshot = await getDocs(q);
        console.log(`%c[CHECKUP-DEBUG] 📊 Snapshot döndü — empty: ${snapshot.empty}, docs.length: ${snapshot.docs.length}`, 'color:#3b82f6;font-weight:bold');
        
        if (snapshot.empty) {
            console.log('[CHECKUP-DEBUG] ⚠️ Koleksiyon BOŞ — hiç checkup_exams dokümanı yok');
            return null;
        }

        // Tüm dokümanları logla
        snapshot.docs.forEach((docSnap, idx) => {
            const d = docSnap.data();
            console.log(`[CHECKUP-DEBUG] 📄 Doc[${idx}]: id=${docSnap.id}, subject=${d.subject}, questionCount=${d.questionCount}, createdBy=${d.createdBy}`);
        });

        // Subject'e göre filtrele
        for (const docSnap of snapshot.docs) {
            const data = docSnap.data() as CheckupExam;
            if (data.subject === subject) {
                console.log(`%c[CHECKUP-DEBUG] ✅ EŞLEŞME BULUNDU: ${docSnap.id} — ${data.questionCount} soru`, 'color:#22c55e;font-weight:bold;font-size:12px');
                return { ...data, id: docSnap.id };
            }
        }

        console.log(`[CHECKUP-DEBUG] ⚠️ ${subject} dersi için eşleşen doküman YOK`);
        return null;
    } catch (e: any) {
        console.error(`%c[CHECKUP-DEBUG] 💥 HATA: ${e?.code || 'unknown'} — ${e?.message || e}`, 'color:#ef4444;font-weight:bold;font-size:14px');
        return null;
    }
};

/** Herkes: Belirli ders için tüm check-up sınavlarını yükle (yeniden eskiye sıralı) */
export const loadAllCheckupExams = async (subject: SubjectType): Promise<CheckupExam[]> => {
    if (!isFirebaseActive) return [];
    try {
        const examsRef = collection(db, 'checkup_exams');
        const q = query(
            examsRef,
            orderBy('createdAt', 'desc'),
            firestoreLimit(50)
        );
        const snapshot = await getDocs(q);
        const list: CheckupExam[] = [];
        snapshot.docs.forEach(docSnap => {
            const data = docSnap.data() as CheckupExam;
            if (data.subject === subject) {
                list.push({ ...data, id: docSnap.id });
            }
        });
        return list;
    } catch (e) {
        console.error('[CHECKUP] Tüm checkup yükleme hatası:', e);
        return [];
    }
};

export const getSolvedAgentMissions = (): string[] => {
    return currentUserProfile.solvedAgentMissions || [];
};

export const saveSolvedAgentMission = (missionKey: string) => {
    const profile = { ...currentUserProfile };
    if (!profile.solvedAgentMissions) profile.solvedAgentMissions = [];
    if (!profile.solvedAgentMissions.includes(missionKey)) {
        profile.solvedAgentMissions.push(missionKey);
        if (profile.solvedAgentMissions.length > 200) {
            profile.solvedAgentMissions.shift();
        }
        saveUserProfile(profile);
    }
};

export const saveAgent81Session = async (session: Agent81Session): Promise<void> => {
    if (!isFirebaseActive || !auth.currentUser) return;
    try {
        const sessionRef = doc(db, 'agent81_sessions', session.id);
        const dataToSave = {
            ...session,
            userId: auth.currentUser.uid
        };
        await setDoc(sessionRef, dataToSave);
        console.log(`%c[saveAgent81Session] ✅ Oturum kaydedildi/güncellendi: ${session.id}`, 'color:#22c55e');
    } catch (e) {
        console.error("[saveAgent81Session] ❌ Oturum kayıt hatası:", e);
    }
};

export const getAgent81Sessions = async (subject: SubjectType): Promise<Agent81Session[]> => {
    if (!isFirebaseActive || !auth.currentUser) return [];
    try {
        const uid = auth.currentUser.uid;
        const colRef = collection(db, 'agent81_sessions');
        const q = query(
            colRef,
            where('userId', '==', uid),
            where('subject', '==', subject)
        );
        const snapshot = await getDocs(q);
        const sessions: Agent81Session[] = [];
        snapshot.docs.forEach(docSnap => {
            sessions.push({
                ...docSnap.data(),
                id: docSnap.id
            } as Agent81Session);
        });
        // Bellekte sıralama yaparak Firestore composite index ihtiyacını ortadan kaldırıyoruz
        sessions.sort((a, b) => b.createdAt - a.createdAt);
        console.log(`%c[getAgent81Sessions] 📂 ${sessions.length} kayıtlı oturum yüklendi (${subject})`, 'color:#3b82f6');
        return sessions;
    } catch (e) {
        console.error("[getAgent81Sessions] ❌ Oturum yükleme hatası:", e);
        return [];
    }
};

export const saveTravelerRecord = async (record: TravelerRecord): Promise<void> => {
    if (!isFirebaseActive) return;
    try {
        let finalImage = record.clueImage;
        if (finalImage && finalImage.startsWith('data:image')) {
            console.log(`%c[saveTravelerRecord] 📤 Büyük base64 seyahat görseli tespit edildi, Storage'a yükleniyor...`, 'color:#3b82f6');
            const uploadedUrl = await uploadBase64Image(record.subject, finalImage);
            if (uploadedUrl) {
                finalImage = uploadedUrl;
            }
        }

        const recordRef = doc(db, 'traveler_records', record.id);
        await setDoc(recordRef, {
            ...record,
            clueImage: finalImage
        });
        console.log(`%c[saveTravelerRecord] ✅ Seyahat kaydı eklendi: ${record.id}`, 'color:#22c55e');
    } catch (e) {
        console.error("[saveTravelerRecord] ❌ Seyahat kaydetme hatası:", e);
    }
};

export const updateTravelerRecordAudio = async (recordId: string, audioUrl: string): Promise<void> => {
    if (!isFirebaseActive) return;
    try {
        const recordRef = doc(db, 'traveler_records', recordId);
        await updateDoc(recordRef, {
            clueAudio: audioUrl
        });
        console.log(`%c[updateTravelerRecordAudio] ✅ Seyahat ses kaydı güncellendi: ${recordId}`, 'color:#22c55e');
    } catch (e) {
        console.error("[updateTravelerRecordAudio] ❌ Ses kaydı güncelleme hatası:", e);
    }
};


export const getTravelerRecords = async (subject: SubjectType): Promise<TravelerRecord[]> => {
    if (!isFirebaseActive) return [];
    try {
        const colRef = collection(db, 'traveler_records');
        const q = query(
            colRef,
            where('subject', '==', subject)
        );
        const snapshot = await getDocs(q);
        const records: TravelerRecord[] = [];
        snapshot.docs.forEach(docSnap => {
            records.push({
                ...docSnap.data(),
                id: docSnap.id
            } as TravelerRecord);
        });
        // Sıralama
        records.sort((a, b) => b.createdAt - a.createdAt);
        console.log(`%c[getTravelerRecords] 📂 ${records.length} seyahat arşivi yüklendi (${subject})`, 'color:#3b82f6');
        return records;
    } catch (e) {
        console.error("[getTravelerRecords] ❌ Seyahat arşiv yükleme hatası:", e);
        return [];
    }
};

export const saveRouteRecord = async (record: RouteRecord): Promise<void> => {
    if (!isFirebaseActive) return;
    try {
        const recordRef = doc(db, 'route_records', record.id);
        await setDoc(recordRef, record);
        console.log(`%c[saveRouteRecord] ✅ Rota kaydı eklendi: ${record.id}`, 'color:#22c55e');
    } catch (e) {
        console.error("[saveRouteRecord] ❌ Rota kaydetme hatası:", e);
    }
};

export const getRouteRecords = async (subject: SubjectType): Promise<RouteRecord[]> => {
    if (!isFirebaseActive) return [];
    try {
        const colRef = collection(db, 'route_records');
        const q = query(
            colRef,
            where('subject', '==', subject)
        );
        const snapshot = await getDocs(q);
        const records: RouteRecord[] = [];
        snapshot.docs.forEach(docSnap => {
            records.push({
                ...docSnap.data(),
                id: docSnap.id
            } as RouteRecord);
        });
        records.sort((a, b) => b.createdAt - a.createdAt);
        console.log(`%c[getRouteRecords] 📂 ${records.length} rota arşivi yüklendi (${subject})`, 'color:#3b82f6');
        return records;
    } catch (e) {
        console.error("[getRouteRecords] ❌ Rota arşiv yükleme hatası:", e);
        return [];
    }
};

export const getUserMemory = async (subject: SubjectType): Promise<UserMemory | null> => {
    if (!isFirebaseActive || !auth.currentUser) return null;
    try {
        const uid = auth.currentUser.uid;
        const memoryDocRef = doc(db, 'user_memories', `${uid}_${subject}`);
        const snap = await getDoc(memoryDocRef);
        if (snap.exists()) {
            return snap.data() as UserMemory;
        }
        return null;
    } catch (e) {
        console.error("[getUserMemory] ❌ Hafıza okuma hatası:", e);
        return null;
    }
};

export const saveUserMemory = async (memory: UserMemory): Promise<void> => {
    if (!isFirebaseActive || !auth.currentUser) return;
    try {
        const uid = auth.currentUser.uid;
        const memoryDocRef = doc(db, 'user_memories', `${uid}_${memory.subject}`);
        await setDoc(memoryDocRef, {
            ...memory,
            uid
        });
        console.log(`%c[saveUserMemory] ✅ Hafıza bankası güncellendi: ${memory.subject}`, 'color:#22c55e');
    } catch (e) {
        console.error("[saveUserMemory] ❌ Hafıza yazma hatası:", e);
    }
};

/**
 * Zihin Ağı'ndaki henüz onarılmamış hatalı soru ve fotoğraf analiz sayılarını modlarına göre döndürür.
 */
export const getWrongQuestionCountsByMode = (subject: SubjectType): { all: number, tests: number, analyses: number, stubborn: number } => {
    const profile = currentUserProfile;
    let tests = 0;
    let analyses = 0;
    let stubborn = 0;

    if (profile.wrongQuestions) {
        Object.entries(profile.wrongQuestions).forEach(([nodeId, questions]) => {
            if (getNodeSubject(nodeId) === subject && Array.isArray(questions)) {
                questions.forEach(q => {
                    if (!q.isRepaired && !q.isResolved) {
                        // 🛡️ DERS İZOLASYONU: Başka dersin sorusu sayaca yansımasın
                        if (q.subject && q.subject !== subject) return;
                        const detectedSub = getSubjectFromTopic(q.topic || '');
                        if (detectedSub && detectedSub !== subject) return;

                        tests++;
                        if (q.isStubborn) stubborn++;
                    }
                });
            }
        });
    }

    if (profile.analysisHistory) {
        Object.entries(profile.analysisHistory).forEach(([nodeId, logs]) => {
            if (getNodeSubject(nodeId) === subject && Array.isArray(logs)) {
                logs.forEach(log => {
                    if (!(log as any).isRepaired && !(log as any).isResolved) {
                        const logTopic = (log as any).topic || '';
                        const detectedSub = getSubjectFromTopic(logTopic);
                        if (detectedSub && detectedSub !== subject) return;

                        analyses++;
                        if ((log as any).isStubborn) stubborn++;
                    }
                });
            }
        });
    }

    return {
        all: tests + analyses,
        tests,
        analyses,
        stubborn
    };
};

/**
 * Yanlış düğüme düşmüş hatalı soruları doğru düğüme taşır (önce doğru düğüme yazar, sonra eskisini siler):
 * başka dersin düğümünde kalmış sorular ve eskiden hep Fiziki Özellikler'e düşen TÜİK soruları.
 * Onarım sınavı açılırken ve hatalı sorular buluttan sunucu onaylı ilk yüklendiğinde (oturum başına bir kez) çalışır.
 */
const healMisroutedWrongs = (): void => {
    const profile = currentUserProfile;
    // 🧹 OTO-ŞİFALANDIRMA (Auto-Healing Migration):
    // Yanlış dersin düğümünde kalmış (örneğin fiziki_ozellikler içinde kalmış Tarih) soruları
    // doğru dersin zihin ağı düğümüne otomatik olarak taşı ve hatalı düğümden temizle!
    if (profile.wrongQuestions) {
        let migrationOccurred = false;
        const targetCleanups: { fromNodeId: string; q: GeminiTestQuestion; toNodeId: string }[] = [];

        Object.entries(profile.wrongQuestions).forEach(([nodeId, qList]) => {
            if (!Array.isArray(qList)) return;
            const nodeSub = getNodeSubject(nodeId);

            qList.forEach(q => {
                const detectedSub = q.subject || getSubjectFromTopic(q.topic || '');
                if (detectedSub && detectedSub !== nodeSub) {
                    const toNodeId = getTargetNodeId(effectiveQuestionTopic(q), detectedSub);
                    if (toNodeId && toNodeId !== nodeId) {
                        targetCleanups.push({ fromNodeId: nodeId, q: { ...q, subject: detectedSub }, toNodeId });
                    }
                } else if (isTuikTopic(q.topic)) {
                    // TÜİK soruları eskiden hep Fiziki Özellikler'e düşüyordu: metnine göre doğru coğrafya düğümüne
                    const toNodeId = getTargetNodeId(effectiveQuestionTopic(q), 'geography');
                    if (toNodeId && toNodeId !== nodeId) {
                        targetCleanups.push({ fromNodeId: nodeId, q, toNodeId });
                    }
                }
            });
        });

        const movedOut: { nodeId: string; q: any }[] = [];
        if (targetCleanups.length > 0) {
            targetCleanups.forEach(({ fromNodeId, q, toNodeId }) => {
                profile.wrongQuestions![fromNodeId] = pullWrongs(profile.wrongQuestions![fromNodeId], fromNodeId,
                    item => !(item.id !== q.id && item.question !== q.question), movedOut);
                if (!profile.wrongQuestions![toNodeId]) {
                    profile.wrongQuestions![toNodeId] = [];
                }
                const alreadyThere = profile.wrongQuestions![toNodeId].some(
                    item => item.id === q.id || item.question === q.question
                );
                if (!alreadyThere) {
                    profile.wrongQuestions![toNodeId].push(q);
                }
                migrationOccurred = true;
                console.log(`%c[Auto-Heal] 🕊️ "${q.topic}" sorusu yanlış "${fromNodeId}" düğümünden "${toNodeId}" düğümüne taşındı!`, 'color:#10b981; font-weight:bold;');
            });

            if (migrationOccurred && currentUserProfile.wrongQuestions) {
                currentUserProfile.wrongQuestions = profile.wrongQuestions;
                if (isFirebaseActive && auth.currentUser) {
                    const uid = auth.currentUser.uid;
                    syncWrongNodes(uid, profile.wrongQuestions, new Set(targetCleanups.map(t => t.toNodeId)))
                        .then(() => removeWrongDocs(uid, movedOut))
                        .catch(console.warn);
                }
            }
        }
    }

};

/**
 * AI Onarım Sınavı için ÖSYM Blueprint Konu Dağılımına uygun tohum soruları seçer.
 * filterMode:
 * - 'all': Hem test hatalarını hem fotoğraflı analizleri harmanlar (Varsayılan)
 * - 'tests_only': Yalnızca uygulama içi test ve deneme yanlışlarını seçer
 * - 'analysis_only': Yalnızca kullanıcının galeriden/kameradan yüklediği soru analizlerini seçer
 * - 'stubborn_only': Yalnızca onarım sınavında tekrar yanlış yapılmış (inatçı/kritik) soruları seçer
 */
export const getWrongQuestionsForRepairExam = (
    subject: SubjectType,
    filterMode: 'all' | 'tests_only' | 'analysis_only' | 'stubborn_only' = 'all'
): { question: GeminiTestQuestion, nodeId: string }[] | null => {
    const profile = currentUserProfile;
    if (!profile.wrongQuestions && !profile.analysisHistory) return null;

    // 🧹 Yanlış düğüme düşmüş hatalı soruları taşı (bkz. healMisroutedWrongs)
    healMisroutedWrongs();

    // 🧹 OTO-ŞİFALANDIRMA (Auto-Healing Migration for Analysis History):
    // Yanlış dersin düğümünde kalmış fotoğraflı analizleri de doğru dersin zihin ağına taşı ve temizle!
    if (profile.analysisHistory) {
        let analysisMigrationOccurred = false;
        const analysisCleanups: { fromNodeId: string; log: AnalysisLog; toNodeId: string }[] = [];

        Object.entries(profile.analysisHistory).forEach(([nodeId, logs]) => {
            if (!Array.isArray(logs)) return;
            const nodeSub = getNodeSubject(nodeId);

            logs.forEach(log => {
                const detectedSub = resolveQuestionSubject({ topic: log.topic, question: log.analysisText }, nodeSub);
                if (detectedSub && detectedSub !== nodeSub) {
                    const toNodeId = getTargetNodeId(log.topic || 'Genel', detectedSub);
                    if (toNodeId && toNodeId !== nodeId) {
                        analysisCleanups.push({ fromNodeId: nodeId, log, toNodeId });
                    }
                }
            });
        });

        if (analysisCleanups.length > 0) {
            analysisCleanups.forEach(({ fromNodeId, log, toNodeId }) => {
                profile.analysisHistory![fromNodeId] = (profile.analysisHistory![fromNodeId] || []).filter(
                    item => item.id !== log.id
                );
                if (!profile.analysisHistory![toNodeId]) {
                    profile.analysisHistory![toNodeId] = [];
                }
                const alreadyThere = profile.analysisHistory![toNodeId].some(item => item.id === log.id);
                if (!alreadyThere) {
                    profile.analysisHistory![toNodeId].push(log);
                }
                analysisMigrationOccurred = true;
                console.log(`%c[Auto-Heal-Analysis] 🕊️ Fotoğrafik analiz "${log.topic}" yanlış "${fromNodeId}" düğümünden "${toNodeId}" düğümüne taşındı!`, 'color:#10b981; font-weight:bold;');
            });

            if (analysisMigrationOccurred && currentUserProfile.analysisHistory) {
                currentUserProfile.analysisHistory = profile.analysisHistory;
                if (isFirebaseActive && auth.currentUser) {
                    // Taşınan kayıtların yalnızca nodeId'si değişir (doküman kimliği aynı kalır)
                    writeAnalysisLogs(auth.currentUser.uid, analysisCleanups.map(({ log, toNodeId }) => ({ nodeId: toNodeId, log })))
                        .catch(console.warn);
                }
            }
        }
    }

    const subjectWrongs: { question: GeminiTestQuestion, nodeId: string }[] = [];

    const seenQuestionKeys = new Set<string>();
    const getDedupKey = (q: GeminiTestQuestion): string => {
        return (q.question || '')
            .replace(/\[Fotoğraf Analiz Sorusu\] Orijinal Soru\/Analiz Metni:\s*/gi, '')
            .trim()
            .toLowerCase()
            .slice(0, 120);
    };

    // 1. Uygulama içi test hatalarını topla (filterMode !== 'analysis_only' ise)
    if (filterMode !== 'analysis_only' && profile.wrongQuestions) {
        Object.entries(profile.wrongQuestions).forEach(([nodeId, questions]) => {
            if (getNodeSubject(nodeId) === subject && Array.isArray(questions)) {
                questions.forEach(q => {
                    if (!q.isRepaired && !q.isResolved) {
                        if (filterMode === 'stubborn_only' && !q.isStubborn) {
                            return;
                        }

                        // 🛡️ DERS İZOLASYON ZIRHI:
                        // 1. Sorunun açık subject'i varsa ve deneme dersiyle uyuşmuyorsa ASLA alma!
                        if (q.subject && q.subject !== subject) {
                            console.warn(`[getWrongQuestionsForRepairExam] 🚨 Sızan soru engellendi (q.subject: ${q.subject} !== ${subject})`, q.topic);
                            return;
                        }
                        // 2. Sorunun konusundan tespit edilen ders deneme dersinden farklıysa ASLA alma!
                        const detectedSub = getSubjectFromTopic(q.topic || '');
                        if (detectedSub && detectedSub !== subject) {
                            console.warn(`[getWrongQuestionsForRepairExam] 🚨 Sızan soru engellendi (detectedSub: ${detectedSub} !== ${subject})`, q.topic);
                            return;
                        }

                        const dedupKey = getDedupKey(q);
                        const idKey = q.id || '';
                        if ((!idKey || !seenQuestionKeys.has(idKey)) && (!dedupKey || !seenQuestionKeys.has(dedupKey))) {
                            if (idKey) seenQuestionKeys.add(idKey);
                            if (dedupKey) seenQuestionKeys.add(dedupKey);
                            subjectWrongs.push({ question: q, nodeId });
                        }
                    }
                });
            }
        });
    }

    // 1.5. Fotoğraflı analiz sorularını topla (filterMode !== 'tests_only' ise)
    if (filterMode !== 'tests_only' && profile.analysisHistory) {
        Object.entries(profile.analysisHistory).forEach(([nodeId, logs]) => {
            if (getNodeSubject(nodeId) === subject && Array.isArray(logs)) {
                logs.forEach(log => {
                    if (!(log as any).isRepaired && !(log as any).isResolved) {
                        if (filterMode === 'stubborn_only' && !(log as any).isStubborn) {
                            return;
                        }

                        // 🛡️ DERS İZOLASYONU: Fotoğraflı analiz konusu başka derse aitse alma!
                        const logTopic = (log as any).topic || '';
                        const detectedSub = getSubjectFromTopic(logTopic);
                        if (detectedSub && detectedSub !== subject) {
                            console.warn(`[getWrongQuestionsForRepairExam] 🚨 Analizden sızan soru engellendi (${detectedSub} !== ${subject})`, logTopic);
                            return;
                        }

                        const cleanText = (log.analysisText || '').trim().toLowerCase().slice(0, 120);
                        const idKey = `analysis_${log.id}`;
                        if (!seenQuestionKeys.has(idKey) && (!cleanText || !seenQuestionKeys.has(cleanText))) {
                            seenQuestionKeys.add(idKey);
                            if (cleanText) seenQuestionKeys.add(cleanText);

                            const normalizedTopic = normalizeTopicName(nodeId, subject, true);

                            const mockQ: GeminiTestQuestion = {
                                id: idKey,
                                question: `[Fotoğraf Analiz Sorusu] Orijinal Soru/Analiz Metni: ${log.analysisText}`,
                                options: ['A', 'B', 'C', 'D', 'E'],
                                correctAnswerIndex: 0,
                                topic: normalizedTopic,
                                explanation: log.analysisText,
                                image: log.imageUrl || undefined,
                                isRepaired: false,
                                isResolved: false,
                                isStubborn: (log as any).isStubborn
                            };
                            subjectWrongs.push({ question: mockQ, nodeId });
                        }
                    }
                });
            }
        });
    }

    // 2. Threshold (Sınır) kontrolü:
    // Analiz ve kritik hata modları için en az 3 soru yeterlidir; diğer modlar için standart eşikler geçerlidir
    const defaultThresholds: Record<SubjectType, number> = {
        history: 10,
        citizenship: 5,
        geography: 5,
        turkish: 10,
        math: 10
    };
    const minThreshold = (filterMode === 'analysis_only' || filterMode === 'stubborn_only')
        ? Math.min(3, defaultThresholds[subject] || 5)
        : (defaultThresholds[subject] || 5);

    if (subjectWrongs.length < minThreshold) {
        return null;
    }

    // 3. Sınav boyutu: KPSS dersinin gerçek soru sayısı kadar hedeflenir.
    // Eğer depodaki toplam kullanılmamış soru sayısı bundan az ise, var olan tüm soruları sınava alır.
    const targetMax = TARGET_QUESTION_COUNTS[subject] || 18;
    const targetExamSize = Math.min(targetMax, subjectWrongs.length);

    // 4. Blueprint konu dağılımına göre soru çekme
    const blueprint = EXAM_BLUEPRINTS[subject] || [];
    const totalBlueprintCount = blueprint.reduce((sum, item) => sum + item.count, 0);

    // Her topic için hedef soru adetleri hesapla (Toplam targetExamSize soru üzerinden orantıla)
    const targetSelection: { topic: string, target: number }[] = [];
    if (totalBlueprintCount > 0) {
        blueprint.forEach(bp => {
            const target = Math.round((bp.count / totalBlueprintCount) * targetExamSize);
            targetSelection.push({ topic: bp.topic, target });
        });
    }

    // Soruları topic bazında grupla
    const groupedByTopic: Record<string, { question: GeminiTestQuestion, nodeId: string }[]> = {};
    subjectWrongs.forEach(item => {
        const normalizedTopic = normalizeTopicName(item.question.topic || 'Genel', subject, true);
        if (!groupedByTopic[normalizedTopic]) groupedByTopic[normalizedTopic] = [];
        groupedByTopic[normalizedTopic].push(item);
    });

    // Her topic grubundaki soruları kendi içinde karıştır
    Object.keys(groupedByTopic).forEach(topic => {
        const arr = groupedByTopic[topic];
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
    });

    const selectedList: { question: GeminiTestQuestion, nodeId: string }[] = [];
    const selectedSet = new Set<string>();

    const pushToSelected = (item: { question: GeminiTestQuestion, nodeId: string }) => {
        const id = item.question.id || '';
        const key = getDedupKey(item.question);
        if (id && selectedSet.has(id)) return false;
        if (key && selectedSet.has(key)) return false;
        if (id) selectedSet.add(id);
        if (key) selectedSet.add(key);
        selectedList.push(item);
        return true;
    };

    // Önce blueprint dağılımına göre çek
    targetSelection.forEach(sel => {
        const normalizedTopic = normalizeTopicName(sel.topic, subject, true);
        const wrongsInTopic = groupedByTopic[normalizedTopic] || [];
        let takenForTopic = 0;
        while (wrongsInTopic.length > 0 && takenForTopic < sel.target && selectedList.length < targetExamSize) {
            const candidate = wrongsInTopic.pop()!;
            if (pushToSelected(candidate)) {
                takenForTopic++;
            }
        }
    });

    // Boş kalan slotları en çok hata yapılan konulardan (kalan havuzdan) round-robin ile doldur
    let remainingNeeded = targetExamSize - selectedList.length;
    if (remainingNeeded > 0) {
        const sortedTopicsByCount = Object.keys(groupedByTopic).sort((a, b) => {
            return (groupedByTopic[b]?.length || 0) - (groupedByTopic[a]?.length || 0);
        });

        let index = 0;
        let safetyLoops = 0;
        while (remainingNeeded > 0 && sortedTopicsByCount.length > 0 && safetyLoops < 300) {
            safetyLoops++;
            const currentTopic = sortedTopicsByCount[index % sortedTopicsByCount.length];
            const topicWrongs = groupedByTopic[currentTopic];
            
            if (topicWrongs && topicWrongs.length > 0) {
                const candidate = topicWrongs.pop()!;
                if (pushToSelected(candidate)) {
                    remainingNeeded--;
                }
            } else {
                sortedTopicsByCount.splice(index % sortedTopicsByCount.length, 1);
                continue;
            }
            index++;
        }
    }

    // Toplamda seçilen soruları karıştırıp sınırla (Max targetExamSize)
    for (let i = selectedList.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [selectedList[i], selectedList[j]] = [selectedList[j], selectedList[i]];
    }

    return selectedList.slice(0, targetExamSize);
};

/**
 * Onarım sınavında kullanılan hatalı soruları isRepaired = true ve isResolved = true olarak işaretler ve veritabanına kaydeder.
 */
/**
 * Onarım sınavında kullanılan hatalı soruları isRepaired = true ve isResolved = true olarak işaretler,
 * Firestore alt koleksiyonlarına kaydeder ve arayüzü anında günceller.
 */
export const markWrongQuestionsRepaired = async (
    subject: SubjectType, 
    questionIds: string[], 
    questionTexts?: string[],
    mode: 'mark' | 'unmark' = 'mark'
): Promise<boolean> => {
    try {
        const targetIdSet = new Set(questionIds.filter(Boolean));
        const targetTextList = (questionTexts || [])
            .map(t => (t || '').replace(/\[Fotoğraf Analiz Sorusu\] Orijinal Soru\/Analiz Metni:\s*/gi, '').trim().toLowerCase())
            .filter(t => t.length > 5);



        let updatedAny = false;
        const updatedNodeIds = new Set<string>();
        const updatedAnalysisNodeIds = new Set<string>();

        // 1. currentUserProfile.wrongQuestions üzerinde güncelleme
        if (currentUserProfile.wrongQuestions) {
            Object.entries(currentUserProfile.wrongQuestions).forEach(([nodeId, questions]) => {
                if (getNodeSubject(nodeId) === subject && Array.isArray(questions)) {
                    let nodeModified = false;
                    questions.forEach(q => {
                        const qId = q.id || '';
                        const qText = (q.question || '').trim().toLowerCase();
                        
                        const idMatch = !!(qId && targetIdSet.has(qId));
                        const textMatch = targetTextList.some(targetText => 
                            qText.includes(targetText.slice(0, 50)) || targetText.includes(qText.slice(0, 50))
                        );

                        if (idMatch || textMatch) {
                            if (mode === 'mark') {
                                q.isRepaired = true;
                                q.isResolved = true;
                            } else if (q.isRepaired) {
                                // Yalnızca onarım denemesinin koyduğu işareti geri al (kullanıcının kendi çözdükleri etkilenmez)
                                delete q.isRepaired;
                                delete q.isResolved;
                            } else {
                                return;
                            }
                            nodeModified = true;
                            updatedAny = true;
                        }
                    });
                    if (nodeModified) {
                        updatedNodeIds.add(nodeId);
                    }
                }
            });
        }

        // 2. currentUserProfile.analysisHistory üzerinde güncelleme
        if (currentUserProfile.analysisHistory) {
            Object.entries(currentUserProfile.analysisHistory).forEach(([nodeId, logs]) => {
                if (getNodeSubject(nodeId) === subject && Array.isArray(logs)) {
                    let nodeModified = false;
                    logs.forEach(log => {
                        const mockId = `analysis_${log.id}`;
                        const logText = (log.analysisText || '').trim().toLowerCase();
                        
                        const idMatch = targetIdSet.has(mockId) || targetIdSet.has(log.id);
                        const textMatch = targetTextList.some(targetText => 
                            logText.includes(targetText.slice(0, 50)) || targetText.includes(logText.slice(0, 50))
                        );

                        if (idMatch || textMatch) {
                            if (mode === 'mark') {
                                (log as any).isRepaired = true;
                                (log as any).isResolved = true;
                            } else if ((log as any).isRepaired) {
                                delete (log as any).isRepaired;
                                delete (log as any).isResolved;
                            } else {
                                return;
                            }
                            nodeModified = true;
                            updatedAny = true;
                        }
                    });
                    if (nodeModified) {
                        updatedAnalysisNodeIds.add(nodeId);
                    }
                }
            });
        }

        // 3. Firestore alt koleksiyonlarına ve dokümanlarına yaz
        if (isFirebaseActive && auth.currentUser) {
            const uid = auth.currentUser.uid;
            
            // Hatalı sorular: yalnızca işareti değişenler yazılır
            syncWrongNodes(uid, currentUserProfile.wrongQuestions, updatedNodeIds).catch(err => {
                console.error('[markWrongQuestionsRepaired] ❌ Firestore wrong_items write error:', err);
            });

            // Analysis history güncellemeleri
            if (updatedAnalysisNodeIds.size > 0) {
                persistAnalysisNodes(uid, updatedAnalysisNodeIds).catch(err => {
                    console.warn("[markWrongQuestionsRepaired] ❌ Firestore analysis_logs update error:", err);
                });
            }
        }

        // 4. Local storage güncelle ve tüm abonelere duyur
        if (auth.currentUser) {
            saveToLocal(`user_profile_${auth.currentUser.uid}`, currentUserProfile);
        }
        saveToLocal('kpss_current_user_profile', currentUserProfile);
        notifyProfileSubscribers();

        console.log(`%c[User Service] markWrongQuestionsRepaired tamamlandı. ${updatedNodeIds.size} soru düğümü ve ${updatedAnalysisNodeIds.size} analiz düğümü onarıldı.`, 'color:#22c55e;font-weight:bold;');
        return updatedAny;
    } catch (e) {
        console.error('[User Service] markWrongQuestionsRepaired failed:', e);
        return false;
    }
};

/**
 * Silinen bir onarım denemesinde kullanılan hatalı soruları/analizleri tekrar hata havuzuna döndürür.
 * Yalnızca o denemenin tohum soruları (referenceQuestion) etkilenir.
 */
export const unmarkWrongQuestionsRepaired = (subject: SubjectType, questionIds: string[], questionTexts?: string[]) =>
    markWrongQuestionsRepaired(subject, questionIds, questionTexts, 'unmark');

/**
 * Kullanıcının belirli bir dersteki tüm onarılmış (isRepaired / isResolved) hatalarını ve fotoğraflı analizlerini
 * tekrar aktif hata havuzuna döndürmek için işaretleri kaldırır.
 */
export const resetAllRepairedMistakes = async (subject: SubjectType): Promise<{ count: number; success: boolean }> => {
    try {
        let resetCount = 0;
        const updatedNodeIds = new Set<string>();
        const updatedAnalysisNodeIds = new Set<string>();



        if (currentUserProfile.wrongQuestions) {
            Object.entries(currentUserProfile.wrongQuestions).forEach(([nodeId, questions]) => {
                if (getNodeSubject(nodeId) === subject && Array.isArray(questions)) {
                    let nodeModified = false;
                    questions.forEach(q => {
                        if (q.isRepaired || q.isResolved) {
                            delete q.isRepaired;
                            delete q.isResolved;
                            resetCount++;
                            nodeModified = true;
                        }
                    });
                    if (nodeModified) {
                        updatedNodeIds.add(nodeId);
                    }
                }
            });
        }

        if (currentUserProfile.analysisHistory) {
            Object.entries(currentUserProfile.analysisHistory).forEach(([nodeId, logs]) => {
                if (getNodeSubject(nodeId) === subject && Array.isArray(logs)) {
                    let nodeModified = false;
                    logs.forEach(log => {
                        if ((log as any).isRepaired || (log as any).isResolved) {
                            delete (log as any).isRepaired;
                            delete (log as any).isResolved;
                            resetCount++;
                            nodeModified = true;
                        }
                    });
                    if (nodeModified) {
                        updatedAnalysisNodeIds.add(nodeId);
                    }
                }
            });
        }

        // Firestore persist
        if (isFirebaseActive && auth.currentUser) {
            const uid = auth.currentUser.uid;
            syncWrongNodes(uid, currentUserProfile.wrongQuestions, updatedNodeIds).catch(console.warn);

            if (updatedAnalysisNodeIds.size > 0) {
                persistAnalysisNodes(uid, updatedAnalysisNodeIds).catch(console.warn);
            }
        }

        if (auth.currentUser) {
            saveToLocal(`user_profile_${auth.currentUser.uid}`, currentUserProfile);
        }
        saveToLocal('kpss_current_user_profile', currentUserProfile);
        notifyProfileSubscribers();

        console.log(`%c[User Service] ${resetCount} soru/analiz üzerindeki onarım işareti kaldırıldı ve havuza iade edildi.`, 'color:#06b6d4;font-weight:bold');
        return { count: resetCount, success: resetCount > 0 };
    } catch (e) {
        console.error('[User Service] resetAllRepairedMistakes failed:', e);
        return { count: 0, success: false };
    }
};

/**
 * Onarım sınavında tekrar yanlış yapılan soruyu Zihin Ağı'na '🚨 İnatçı Hata' olarak geri kaydeder.
 */
export const recordStubbornMistake = async (
    subject: SubjectType,
    nodeId: string,
    question: GeminiTestQuestion,
    selectedOptionIndex?: number
): Promise<boolean> => {
    try {
        if (!currentUserProfile.wrongQuestions) currentUserProfile.wrongQuestions = {};

        // 🛡️ DERS KORUMASI: Sorunun gerçek dersini tespit et
        const trueSubject = resolveQuestionSubject(question, subject);
        let targetNodeId = nodeId;
        if (trueSubject !== subject || getNodeSubject(nodeId) !== trueSubject) {
            const correctNode = getTargetNodeId(effectiveQuestionTopic(question), trueSubject);
            if (correctNode) {
                targetNodeId = correctNode;
            }
        }

        if (!currentUserProfile.wrongQuestions[targetNodeId]) currentUserProfile.wrongQuestions[targetNodeId] = [];

        const stubbornQId = `stubborn_${question.id || Date.now()}`;

        // 🧹 ÇAPRAZ DÜĞÜM TEMİZLİĞİ: Bu soru başka bir düğümde (özellikle yanlış derste) kaldıysa oradan temizle!
        const crossRemoved: { nodeId: string; q: any }[] = [];
        Object.keys(currentUserProfile.wrongQuestions).forEach(otherNode => {
            if (otherNode !== targetNodeId && currentUserProfile.wrongQuestions![otherNode]) {
                currentUserProfile.wrongQuestions![otherNode] = pullWrongs(currentUserProfile.wrongQuestions![otherNode], otherNode,
                    q => !(q.id !== stubbornQId && q.id !== question.id && q.question !== question.question), crossRemoved);
            }
        });

        const exists = currentUserProfile.wrongQuestions[targetNodeId].some(q => 
            q.id === stubbornQId || q.question === question.question
        );

        if (!exists) {
            const stubbornQuestion: GeminiTestQuestion = {
                ...question,
                id: stubbornQId,
                subject: trueSubject,
                isRepaired: false,
                isResolved: false,
                isStubborn: true,
                seedIndex: undefined,
                userAnswerIndex: typeof selectedOptionIndex === 'number' ? selectedOptionIndex : undefined,
                explanation: `🚨 **[İNATÇI HATA - Onarım Sınavında Tekrar Yanlış Yapıldı]**\n${question.explanation || ''}`
            };

            currentUserProfile.wrongQuestions[targetNodeId].unshift(stubbornQuestion);

            // Update topic stats
            const mainTopic = normalizeTopicName(effectiveQuestionTopic(question), trueSubject);
            const statsKey = `${trueSubject}_${mainTopic.replace(/\s+/g, '_')}`;
            if (currentUserProfile.stats && currentUserProfile.stats[statsKey]) {
                currentUserProfile.stats[statsKey].wrongAnswers = (currentUserProfile.stats[statsKey].wrongAnswers || 0) + 1;
                currentUserProfile.stats[statsKey].lastStudied = Date.now();
            }

            // Save to Firestore wrong_questions subcollection
            if (isFirebaseActive && auth.currentUser) {
                try {
                    await syncWrongNodes(auth.currentUser.uid, currentUserProfile.wrongQuestions, [targetNodeId]);
                    // Başka düğümlerden temizlenen kopyalar
                    await removeWrongDocs(auth.currentUser.uid, crossRemoved);
                } catch (dbErr) {
                    console.warn("[User Service] Firestore wrong_questions subcollection update error:", dbErr);
                }
            }

            if (auth.currentUser) {
                saveToLocal(`user_profile_${auth.currentUser.uid}`, currentUserProfile);
            }
            saveToLocal('kpss_current_user_profile', currentUserProfile);
            notifyProfileSubscribers();

            console.log(`%c[İnatçı Hata] 🚨 Soru Zihin Ağı düğümüne (${targetNodeId}, ${trueSubject}) kaydedildi!`, 'color:#ef4444;font-weight:bold');
            return true;
        }
        return false;
    } catch (err) {
        console.error("[User Service] recordStubbornMistake error:", err);
        return false;
    }
};

/**
 * Checks if the user is within their daily AI quota and increments the counter if allowed.
 */
export const checkAndIncrementAiUsage = async (
    type: 'exam' | 'explain' | 'chat' | 'podcast' | 'game' | 'kapiChat' | 'historyChat' | 'kapiBank',
    isAdmin: boolean = false
): Promise<{ allowed: boolean; error?: string }> => {
    const userEmail = (auth.currentUser?.email || '').toLowerCase();
    const isAdminUser = isAdmin || ['cokunsuleyman@gmail.com', 'necmiyeortky@gmail.com', 'necmiyeort@gmail.com'].includes(userEmail);

    const profile = { ...currentUserProfile };
    if (!profile.uid) {
        return { allowed: false, error: 'Kullanıcı profili yüklenemedi. Lütfen tekrar giriş yapın.' };
    }

    const isPrem = !!profile.isPremium;
    const tier = isPrem ? 'premium' : 'free';
    const castedType = type as string;
    
    // --- TIME RESTRICTION FOR EXAMS ---
    if (castedType === 'exam' && !isAdminUser) {
        const now = new Date();
        const hour = now.getHours();
        if (hour < 16 || hour >= 20) {
            return { 
                allowed: false, 
                error: 'Yapay Zeka ile özel deneme sınavı üretme özelliği sadece 16:00 ile 20:00 saatleri arasında aktiftir.' 
            };
        }
    }

    // --- QUOTA LOGIC ---
    if (castedType === 'exam') {
        if (isPrem) {
            const billingPeriodId = getBillingPeriodId(profile);
            if (profile.examQuotaMonth !== billingPeriodId) {
                profile.examQuotaMonth = billingPeriodId;
                profile.examUsedCount = 0;
            }
            const used = profile.examUsedCount || 0;
            if (used >= 30 && !isAdminUser) {
                return { allowed: false, error: 'Bu ayki Yapay Zeka deneme sınavı limitinize (30 adet) ulaştınız. Gelecek fatura döneminizde kotanız yenilenecektir.' };
            }
            profile.examUsedCount = used + 1;
        } else {
            const used = profile.freeExamsUsed || 0;
            if (used >= 1 && !isAdminUser) {
                return { allowed: false, error: 'Ücretsiz Yapay Zeka deneme sınavı hakkınızı (1 adet) kullandınız. Sınırsız özellikler ve aylık 30 deneme hakkı için Premium\'a geçebilirsiniz!' };
            }
            profile.freeExamsUsed = used + 1;
        }
    } else if (castedType === 'podcast') {
        if (isPrem) {
            const billingPeriodId = getBillingPeriodId(profile);
            if (profile.podcastQuotaMonth !== billingPeriodId) {
                profile.podcastQuotaMonth = billingPeriodId;
                profile.podcastUsedCount = 0;
            }
            const used = profile.podcastUsedCount || 0;
            if (used >= 20 && !isAdminUser) {
                return { allowed: false, error: 'Bu ayki Kişisel Podcast üretim limitinize (20 adet) ulaştınız. Gelecek fatura döneminizde kotanız yenilenecektir.' };
            }
            profile.podcastUsedCount = used + 1;
        } else {
            if (!isAdminUser) {
                return { allowed: false, error: 'Kişiselleştirilmiş Podcast üretimi sadece PRO (Premium) abonelere özeldir. Ücretsiz hesaplar sadece sistemdeki hazır podcastleri dinleyebilir.' };
            }
            profile.podcastUsedCount = (profile.podcastUsedCount || 0) + 1;
        }
    } else if (castedType === 'kapiBank') {
        // 2026-10-18 (kullanıcı kararı): Kapı Bank denemesi ücretsizde ömür boyu 1, PRO sınırsız (YZ yok, maliyetsiz)
        if (!isPrem && !isAdminUser) {
            const used = profile.freeKapiBankUsed || 0;
            if (used >= 1) {
                return { allowed: false, error: 'Ücretsiz Kapı Bank deneme hakkını (1) kullandın. Sınırsız Kapı Bank denemesi için PRO\'ya geçebilirsin.' };
            }
            profile.freeKapiBankUsed = used + 1;
        }
    } else if (castedType === 'game') {
        // 2026-10-17: Oyun Alanı herkese sınırsız (oyunlar yapay zekâ kullanmıyor, maliyeti yok). Yalnızca sayılır.
        profile.freeGamesUsed = (profile.freeGamesUsed || 0) + 1;
    } else if (castedType === 'kapiChat') {
        if (isPrem) {
            const billingPeriodId = getBillingPeriodId(profile);
            if (profile.kapiChatQuotaMonth !== billingPeriodId) {
                profile.kapiChatQuotaMonth = billingPeriodId;
                profile.kapiChatUsedCount = 0;
            }
            const used = profile.kapiChatUsedCount || 0;
            if (used >= 100 && !isAdminUser) {
                return { allowed: false, error: 'Bu ayki Kapi Hoca sohbet limitinize (100 mesaj) ulaştınız. Gelecek fatura döneminizde kotanız yenilenecektir.' };
            }
            profile.kapiChatUsedCount = used + 1;
        } else {
            const used = profile.freeKapiChatUsed || 0;
            if (used >= 10 && !isAdminUser) {
                return { allowed: false, error: 'Ücretsiz Kapi Hoca sohbet limitinize (10 mesaj) ulaştınız. Aylık 100 sohbet hakkı için Premium\'a geçebilirsiniz!' };
            }
            profile.freeKapiChatUsed = used + 1;
        }
    } else if (castedType === 'historyChat') {
        if (isPrem) {
            const billingPeriodId = getBillingPeriodId(profile);
            if (profile.historyChatQuotaMonth !== billingPeriodId) {
                profile.historyChatQuotaMonth = billingPeriodId;
                profile.historyChatUsedCount = 0;
            }
            const used = profile.historyChatUsedCount || 0;
            if (used >= 100 && !isAdminUser) {
                return { allowed: false, error: 'Bu ayki Tarihi Şahıslar sohbet limitinize (100 mesaj) ulaştınız. Gelecek fatura döneminizde kotanız yenilenecektir.' };
            }
            profile.historyChatUsedCount = used + 1;
        } else {
            const used = profile.freeHistoryChatUsed || 0;
            if (used >= 20 && !isAdminUser) {
                return { allowed: false, error: 'Ücretsiz Tarihi Şahıslar sohbet limitinize (20 mesaj) ulaştınız. Aylık 100 sohbet hakkı için Premium\'a geçebilirsiniz!' };
            }
            profile.freeHistoryChatUsed = used + 1;
        }
    } else {
        // Daily limits for Explain and Chat
        const LIMITS = {
            free: { explain: 2, chat: 3 }, // 2026-10-18: 5 → 3 (sunucuda ayrıca günlük $ tavanı, aiQuota.ts)
            premium: { explain: 15, chat: 50 }
        };

        const limit = LIMITS[tier][castedType as 'explain' | 'chat'];
        const dateStr = getTurkeyDateString();

        if (!profile.aiUsage) {
            profile.aiUsage = {};
        }
        if (!profile.aiUsage[dateStr]) {
            profile.aiUsage[dateStr] = { examCount: 0, explainCount: 0, chatCount: 0 };
        }

        const usage = profile.aiUsage[dateStr];
        const typeKey = castedType === 'explain' ? 'explainCount' : 'chatCount';
        const currentCount = usage[typeKey] || 0;

        if (currentCount >= limit && !isAdminUser) {
            let msg = '';
            if (castedType === 'explain') {
                msg = isPrem
                    ? 'Günlük Yapay Zeka konu anlatımı limitinize (15 adet) ulaştınız. Yarın tekrar anlatım talep edebilirsiniz!'
                    : 'Günlük ücretsiz Yapay Zeka konu anlatımı limitinize (2 adet) ulaştınız. Premium üyeliğe geçerek limitinizi artırabilirsiniz!';
            } else if (castedType === 'chat') {
                msg = isPrem
                    ? 'Günlük Cezeri/Kapi Hoca mesaj limitinize (50 mesaj) ulaştınız. Yarın tekrar sohbet edebilirsiniz!'
                    : 'Günlük ücretsiz Cezeri mesaj limitinize (3 mesaj) ulaştınız. Premium üyeliğe geçerek limitinizi artırabilirsiniz!';
            }
            return { allowed: false, error: msg };
        }

        // Increment usage
        usage[typeKey] = currentCount + 1;
    }

    try {
        currentUserProfile = profile;
        saveUserProfile(profile).catch(err => console.error('[checkAndIncrementAiUsage] Background save failed:', err));
        return { allowed: true };
    } catch (err) {
        console.error('[checkAndIncrementAiUsage] Save failed:', err);
        return { allowed: false, error: 'Bakiye/Limit güncellenirken bir hata oluştu.' };
    }
};

// --- 🎯 HEDEFLERİM (Giriş anketi) ---

/** Profil Firestore'dan yüklendi ve anket henüz cevaplanmadı/yakında atlanmadıysa true. */
export const shouldShowGoalsSurvey = (profile: UserProfile | null | undefined): boolean => {
    if (!profile || !isProfileLoadedFromFirestore || !auth.currentUser || profile.uid !== auth.currentUser.uid) return false;
    if (profile.goals) return false;
    const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
    return !profile.goalsSkippedAt || Date.now() - profile.goalsSkippedAt > SEVEN_DAYS;
};

const updateOwnProfileFields = async (fields: Partial<UserProfile>) => {
    currentUserProfile = { ...currentUserProfile, ...fields };
    notifyProfileSubscribers();
    if (!isFirebaseActive || !auth.currentUser) return;
    await updateDoc(doc(db, "users", auth.currentUser.uid), sanitizeData(fields));
};

/** Ders check-up'ı bitti mi? (deneme modülündeki diğer denemelerin kilidi, TestModal) */
export const isCheckupDone = (subject: string): boolean => !!currentUserProfile?.checkupDone?.[subject];

export const markCheckupDone = async (subject: string): Promise<void> => {
    try {
        await updateOwnProfileFields({ checkupDone: { ...(currentUserProfile?.checkupDone || {}), [subject]: Date.now() } });
    } catch (e) {
        console.warn('[checkup] tamamlanma kaydedilemedi:', e);
    }
};

export const saveUserGoals = async (goals: Omit<UserGoals, 'completedAt'>): Promise<void> => {
    await updateOwnProfileFields({ goals: { ...goals, completedAt: Date.now() } });
};

export const skipGoalsSurvey = async (): Promise<void> => {
    await updateOwnProfileFields({ goalsSkippedAt: Date.now() });
};

/**
 * Returns if the current user profile is premium.
 */
export const isPremiumUser = (): boolean => {
    if (!currentUserProfile?.isPremium) return false;

    if (currentUserProfile.premiumUntil) {
        const untilMs = typeof currentUserProfile.premiumUntil === 'number'
            ? currentUserProfile.premiumUntil
            : (currentUserProfile.premiumUntil.seconds ? currentUserProfile.premiumUntil.seconds * 1000 : null);

        if (untilMs && Date.now() > untilMs) {
            return false; // Subscription expired!
        }
    }
    return true;
};

/**
 * Returns the current user profile.
 */
export const getCurrentUserProfile = (): UserProfile => {
    return { ...currentUserProfile };
};

/**
 * Calculates a unique billing period ID based on when the user subscribed.
 */
export const getBillingPeriodId = (profile: UserProfile): string => {
    if (!profile.premiumStartedAt) {
        // Fallback: If not set, initialize to current time (or a past epoch if they are already premium)
        profile.premiumStartedAt = Date.now();
    }
    
    const start = new Date(profile.premiumStartedAt);
    const now = new Date();
    
    let monthsDiff = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
    if (now.getDate() < start.getDate()) {
        monthsDiff--;
    }
    
    const year = start.getFullYear();
    const month = (start.getMonth() + 1).toString().padStart(2, '0');
    const day = start.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}-p${monthsDiff}`;
};

/**
 * Checks if the user has enough voice quota (seconds) left.
 */
export const checkVoiceQuota = async (): Promise<{ allowed: boolean; remainingSeconds: number; error?: string }> => {
    const profile = { ...currentUserProfile };
    if (!profile.uid) {
        return { allowed: false, remainingSeconds: 0, error: 'Kullanıcı profili yüklenemedi. Lütfen tekrar giriş yapın.' };
    }

    const isPrem = !!profile.isPremium;

    if (isPrem) {
        // Premium Limit: 300 minutes = 18,000 seconds
        const LIMIT_SECONDS = 300 * 60; // 18,000
        const billingPeriodId = getBillingPeriodId(profile);
        
        let needsSave = false;
        if (profile.voiceQuotaMonth !== billingPeriodId) {
            profile.voiceQuotaMonth = billingPeriodId;
            profile.voiceSecondsUsed = 0;
            needsSave = true;
        }

        const used = profile.voiceSecondsUsed || 0;
        const remaining = Math.max(0, LIMIT_SECONDS - used);

        if (needsSave) {
            currentUserProfile = profile;
            await saveUserProfile(profile);
        }

        if (remaining <= 0) {
            return {
                allowed: false,
                remainingSeconds: 0,
                error: 'Bu ay için tanımlanan 300 dakikalık sesli konuşma limitinizi doldurdunuz. Gelecek fatura döneminizde kotanız otomatik yenilenecektir.'
            };
        }

        return { allowed: true, remainingSeconds: remaining };
    } else {
        // Free Limit: One-time 5 minutes = 300 seconds (Does NOT reset monthly/billing period)
        const LIMIT_SECONDS = 5 * 60; // 300
        const used = profile.voiceSecondsUsed || 0;
        const remaining = Math.max(0, LIMIT_SECONDS - used);

        if (remaining <= 0) {
            return {
                allowed: false,
                remainingSeconds: 0,
                error: 'Ücretsiz 5 dakikalık deneme konuşma süreniz dolmuştur. Cezeri ile 300 dakika sesli sohbet etmek için Premium\'a geçebilirsiniz!'
            };
        }

        return { allowed: true, remainingSeconds: remaining };
    }
};

/**
 * Deducts voice call duration (seconds) from the user's quota and logs daily usage.
 */
export const deductVoiceSeconds = async (seconds: number): Promise<{ remainingSeconds: number }> => {
    const profile = { ...currentUserProfile };
    if (!profile.uid) {
        throw new Error('Kullanıcı profili yüklenemedi.');
    }

    const isPrem = !!profile.isPremium;
    const LIMIT_SECONDS = isPrem ? 300 * 60 : 5 * 60;
    
    // 1. Update overall quota usage
    const currentUsed = profile.voiceSecondsUsed || 0;
    const nextUsed = currentUsed + seconds;
    profile.voiceSecondsUsed = nextUsed;

    // Reset month indicator for premium if it is out of sync
    if (isPrem) {
        const billingPeriodId = getBillingPeriodId(profile);
        if (profile.voiceQuotaMonth !== billingPeriodId) {
            profile.voiceQuotaMonth = billingPeriodId;
        }
    }

    // 2. Log daily statistics in aiUsage for tracking daily records
    const dateStr = getTurkeyDateString();
    if (!profile.aiUsage) {
        profile.aiUsage = {};
    }
    if (!profile.aiUsage[dateStr]) {
        profile.aiUsage[dateStr] = { examCount: 0, explainCount: 0, chatCount: 0 };
    }
    profile.aiUsage[dateStr].voiceSeconds = (profile.aiUsage[dateStr].voiceSeconds || 0) + seconds;

    currentUserProfile = profile;
    await saveUserProfile(profile);

    const remaining = Math.max(0, LIMIT_SECONDS - nextUsed);
    return { remainingSeconds: remaining };
};

